// Email content-builder domain model (EMAIL-005 / EMAIL-007 / EMAIL-008 / EMAIL-009).
//
// The current Claude Design builder has 27 blocks. Eight earlier application
// blocks remain supported in saved campaigns, but are not new-block choices.
//
// Stored state is `builderStateJson` version 2. Version 1 payloads are still
// parsed and migrated best-effort (see parseEmailBuilderStateJson).

import { normalizeEmailLinkUrl } from "./emailSafeLink";
import { gmailKeepBackground, gmailKeepDarkText, gmailKeepLightText, isGmailLightColor } from "./emailGmailDarkMode";
import { buildEmailRichTextValue, emailRichTextHtml, emailRichTextToPlainText, isEmailRichText } from "./emailRichText";
import { getPerkOfTheWeekTextLines, renderPerkOfTheWeekContent } from "./emailPerkOfTheWeek";

// ---------------------------------------------------------------------------
// Shared colors / icons
// ---------------------------------------------------------------------------

export const emailBuilderColors = {
  darkNavy: "#0A0A0A",
  blue: "#1878F2",
  lightBlue: "#EFF5FD",
  veryLightGray: "#F8F9FB",
  white: "#FFFFFF",
  black: "#0A0A0A",
} as const;

export type EmailBuilderIconKey =
  | "calendar"
  | "megaphone"
  | "memberSearch"
  | "personAdd"
  | "people"
  | "briefcase"
  | "bank"
  | "phone"
  | "tag"
  | "pencil"
  | "warning"
  | "sparkle"
  | "bolt"
  | "dollar"
  | "gavel"
  | "email"
  | "clock"
  | "mapPin"
  | "link"
  | "news"
  | "chart"
  | "globe"
  | "star"
  | "shield"
  | "building"
  | "trophy"
  | "lightbulb"
  | "handshake";

// Kept for callers that still store or inspect the earlier icon glyph map.
export const emailBuilderIconGlyphs: Record<EmailBuilderIconKey, string> = {
  calendar: "&#128197;",
  megaphone: "&#128227;",
  memberSearch: "&#128269;",
  personAdd: "&#128100;",
  people: "&#128101;",
  briefcase: "&#128188;",
  bank: "&#127963;",
  phone: "&#128241;",
  tag: "&#127991;",
  pencil: "&#9997;",
  warning: "&#9888;",
  sparkle: "&#10024;",
  bolt: "&#9889;",
  dollar: "&#128176;",
  gavel: "&#128296;",
  email: "&#9993;",
  clock: "&#128336;",
  mapPin: "&#128205;",
  link: "&#128279;",
  news: "&#128240;",
  chart: "&#128202;",
  globe: "&#127760;",
  star: "&#9733;",
  shield: "&#128737;",
  building: "&#127970;",
  trophy: "&#127942;",
  lightbulb: "&#128161;",
  handshake: "&#129309;",
};

// Rasterized from the approved outline icon paths. Indexed PNG palettes let
// email clients show the chosen color without SVG or icon-font support.
type EmailBuilderRasterIconKey = EmailBuilderIconKey | "image" | "arrowRight" | "play";

const emailBuilderIconPngs: Record<EmailBuilderRasterIconKey, string> = {
  arrowRight: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAMAAABg3Am1AAAAP1BMVEX///////////////////////////////////////////////////////////////////////////////////81m6ZbAAAAFXRSTlMAAQIcHTM0NYmRkpWanJ2nqKrN/v9jr5A5AAAAcUlEQVR4nO2VyQqAMAxEWxu17kvy/98qBE/uQ0EUMuf3oA1h4pzlxRBhfGSuPcAHFpEWMFSAjEpAwzdq9JkZnzcoP0oxqTHujajrc5p5y4drXqRMFeAn3Xx6eDrYdayd8f/g4SKDqxIuY7ju8YNiScsC74wX+XKo4kgAAAAASUVORK5CYII=",
  play: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAMAAABg3Am1AAAAP1BMVEX///////////////////////////////////////////////////////////////////////////////////81m6ZbAAAAFXRSTlMAAgcSGh0qQVVviKKus8Xb5/D6/v+eqsOlAAAAeUlEQVR4nO2UORKAIBAE8b7FY/7/VlORgO0qI2Viugqa2XUu56dZGghoHwoGSGsLAR1jyQDJdxDQOVUMkLYeApLN8A2wGVYQg+EQMBjWMynDEZAyHAMJwy8A9Erw0VQr/DhaDVg+WG86QHBE6RKAa4YusrlGx3M+mwvuKxgfM1eymgAAAABJRU5ErkJggg==",
  image: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAmQi6W+StDvqNOJ3IRUls9CBTAAAACXBIWXMAAAsTAAALEwEAmpwYAAABEUlEQVR4nGNgoA9g9xREAVMKoBJP/6OBEIg4H7r4//8XwBKl/9tQjcr4Hw6W8P+xANVOrv4vYHr+N3TX5P8EU/If0SWgIvgl2D3hrkeVeAp3PaoEH8L1qBJAv8BcjyoB9AvM9agSIL9AXU+kBE6jcFrOAnOuW2gKqgev/v8fB6TUgPJJWIKEyR4o8VkBMxCV//9xOf/fCEMCqMEIKPl5P7oEUEwBJBuPJgHWAJL+jyYB1gCW/40iAdUAdgKKBFQDUEU8igRcAwPDfhSj4BrQUgmSBlQJJA0oEsgaUCSQNSBLoGiASYDiG0UDLPaB8Y2qARb7wPje+v/rRsyMw4Irq4HiGxXEQc1k99xzGmt2pjUAAGmE+zWAJX2fAAAAAElFTkSuQmCC",
  calendar: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAzK/1kzEh4YL7wQR6dr968HzDAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAp0lEQVR4nGNgIBckqaHScKD/CZWGA/mPqDR+CVYvQUH7z4JgAKKXBEDEuTv/o4EvG8ASHOji//83gCUi/39HNar+/1SwhP//A6iW8/7/Amav/47uqvp/CDaKB6GqwBRKkCBLYPMtNSWgfkMAe6gEZpAQksBpFD38MSpBQwlwokYB0ETt//8CqjgPNBvEgzIOMoBlHBZcWQ0zc86AZE5wdkYGsOxMIgAAmRXZWhi+niUAAAAASUVORK5CYII=",
  megaphone: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAzF+u3h7uDjOJ+kK7dpwp78ceAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA/ElEQVR4nGNgGAUIwHHHFJswu57//89YxHtP/f///yOGcJDZ//9YJKqngIU9/VElWJXXg4RzrjLII0uA7AQZMhvIRpYA2/n/m1EAA4oExM5fsg0QLkyieiLYTj91mDqoRDDYzmRVhLlQCXug8MIZyO6DSsjD7MSQ+NKA5lGCEriMwmk5TufCA3U7ugQDu/8vcJCcbUCT4P7/qVMKJPXlUQGKBNP/37BgB7sCRQJE/oe6Asmov0CS+f9q9Khlz/8G9OH+/wcwEsP8/94MTPn/FYBMsCvgEiX//yfm//8D0Y6c4DjyQQaIw3hISTQGKP6vgAEL6DxoG4BNfMgDAEhhvvGGUc1WAAAAAElFTkSuQmCC",
  memberSearch: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAqAvA2lgk5R35RW/wfo3uKf6kAAAACXBIWXMAAAsTAAALEwEAmpwYAAABbUlEQVR4nGNgoCPIFjbchk088T8QiGGKc50HSfwJwJBY////FM///3+hi7Od/++qwBTy/08CmgTj/88KDAxM9v8F0CTy/zuBKJX/P9Ak9v8HW8v6/zeahPxPBRDFNP8jmoT9Fwjt/5lYCZxG4bQcp3NxehAeJA1oEjgDERbsCzAkIBElgSnOwNAtbLgDm7jqY/cSuyBM8YXzQUb9lEIXT/4PBWao4rxA9d8Nhev////IFGgsqgATZ5L//9+6gYGBY/P/j+FAbaUwCeX//62gVgmCDbwAlZD//xemJv7/f5f//79CONz/f8ISGlM90JON/79DbMmEqQBFyEewCQFQkx7AJBjBEdIPCX22/1/g7mP+7wAkWf4bQFTBTUKV4AFzsBi1Hu5sNMv3/18Al0BxLjRNIXvwewKIbf8dIc7ACwnkMrDEFyQJBlAglkO0yKMkS1Cwq9WD018iZl5N//8NQwwM2PwNsEswqOEQxwsASH7MysuLoSYAAAAASUVORK5CYII=",
  personAdd: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAzSHgr/WjDFy/cEmMNhl/85YbAAAACXBIWXMAAAsTAAALEwEAmpwYAAABNklEQVR4nGNgoCPQypy2CJu4yn8gSMIU544HSXzdgCGx////YNP//3+jizPF/7coYG/+/1UBTYLl/88CBgb2+f8T0CT0/5uDqOL/n9Ak1v9vAFEc/3+hSeR/LQBR7PHf0CTmf4bQ9j+JlcBpFE7LcTqXDZcHcQYJzkDEHewMajgiCnfU8i6bKJJ1AVNcxx5k1OdD6OLP/kNBHqo4B8hNhsIgdzWgSJz//9/mOgND9eH///8gi7P9/28BYTX//78BRcO3AgiLPR8ooeIEs+H//wVw2+YwMPh/gXKeosWC/EcoI/+/A1YJpv+fC7BK8Pz/wYBVgvm/AZKoiqBg/FdBQZDD7v8/gCThDwkakMPWo/gJSSL/fwF2o/w/o9oNt3z+RxwS8BBAl4CHGboEBsAwAu4wdCOIBACME7dB675/1AAAAABJRU5ErkJggg==",
  sparkle: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMA4VY1vyUY07CUQXAJ7vZZskCiAAAACXBIWXMAAAsTAAALEwEAmpwYAAABWklEQVR4nOVSK0wDQRAdetCW5kgQgDlxRaAQRSAwpBBUgyhBIQgpqkFRhSNHQqpBVIGgFgGkDQbFGTQkCGQVBtNcIRy/9rF7e9t2j8ViGLHzMm9eXmZniP421sq/EPkXfT0FVLXEILCgJbLAh94CeNPVDfgOZjREEq0M9rQWpQSeNcQB0rGfJjfTFfhEDnL19V516GoRLFpEGZ7HjldCYgNBlIgSAj2GRNDn5QpE8YodaoPYBJanXIHN1UtgQnrYuO85zsOTHkzS7o5sNDDebYrbeJL4rE9AVESnKlCqiZ2++eKOlJzDL/RPXoQngK0IuEtbgIanCFhneAh5T62beBfgBK5CGPgU4DqyxBi7A7NmuXSBtEIM49WsAZa7jUOFSOJukv+rNRJZ+wAegE4T2MWoQtzy9vIWf78UIsvr7N5ZUg+FLXGf57ne+qRiSYDZiCJ2JNGpOsc/jG+hArOCBUr5oAAAAABJRU5ErkJggg==",
  news: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAzK8hk+HAMQH7eQSC9YTSMK3TAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAz0lEQVR4nGNgIBOwuwkigZQCmDj3y/8o4MsGqATffzRwACpR+f8rslHx/6dDJfz/r0C2kev/Fygr/2sDsgRH/DcoS/4jqiPhfLwSRjCrldEk9GFO/YQmIQ+T+Igu8Rdi0n0MiY+YbOwSRso4JPQ/4ZCAMTAsh0ugOxcmgeFBmARGkKC4GzkQaSCB03Kczh3M/lDGlNCHuZVoCZxGEe+q/K9IGQUCIHxgNuuCZy0IgPDr/3+NR82b0MzJgp6VYdmZ+ySm+BxwAYBaXKAWGaQAADllIKqDVtEqAAAAAElFTkSuQmCC",
  briefcase: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAzDGT4cD7AQSCg/WXendLH2dSAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAoUlEQVR4nGNgoCNgMhUEgmAFdHGOZf/B4E8DmgTXfyiYgCah9v8LyCj//0loEvb/r4Movv+f0STivxSAKHb/r2gS8h9RafwSIC/s/y0IBiAa5hmYFxAA6hkudHGYZ6BeQACYZ6BeQACYZ6BeQACYZzCcDhMZlRgiEvFf0MRhUWv//zmqRC00MejjSj48uBIcxzR08cwGiJmQfIkAmDl00AAAgc+uJH9qkggAAAAASUVORK5CYII=",
  bank: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAzK70Ij8P6NmRU8EEZncKidx/AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAtElEQVR4nGNgoC9wUsEuzmX/eQE2cZ75////PIBFwvs/EGzBFGet//9J///3AAyJ+///P+D9//8funjS//+lDAzh//+roYqz9P//7cDAwLb//w8HFInF///PAdEn//+3QhZH8GEqIADJBKiZUIBsJ8QVEIDqSpC7oQah+gvk0wQwy+P//x+CSKD///8WsIT+fwzwCb+Ek6Cg/WdUDI8x+Y+omGFUYihJ4IxaslIJOsCRr6kNAHZi8zMk7ZpbAAAAAElFTkSuQmCC",
  phone: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAIVBMVEVHcEz///////////////////////////////////////+PBM77AAAACnRSTlMAzARvr8Ky97MhKwnMIwAAAAlwSFlzAAALEwAACxMBAJqcGAAAAFdJREFUeJxjYCAPMFkIwkGzApKE6iokEIQkYYUssRhJoms5wqiqZUgSUguxs0clRiWGhMQUQUFPrBJeq1YtIU0Cp1E09gfOzIkzO+MsAJjMEEYlKzCQBwDgVWaF6SaJswAAAABJRU5ErkJggg==",
  tag: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMA5cwlKugmsBIYWEuh8CEXo0t2AAAACXBIWXMAAAsTAAALEwEAmpwYAAAA8UlEQVR4nJWTwQ3CMAxFLSEhYIBeK3FHFRswBAOUGRigHYE5WIELGzBGz0VCFUKVoSFx/J0cIKfaL8913JTo/9WtZR0BNCyrrzWoIuA9gDFU2qBS3cPTrAQlAtryY5cFqCiAigagaACKA50/9LzkAkAz+OgQz4JAKVBKK/By3ZgBsTELRLFAlAQEJQFTY20WfM5yncBYp8qTpstQWLDil2uit8oX6IFCKT1QeHmq+Hbt5VBjNPdJ7QNFbwMFdqkA66rIdCKhaSTGRpCEFSSTCD6VCl7JCG505/QLkBuy/aFF4ZxAtLjx0OYALS+nbP7H9Qb4U6/dsJqQeAAAAABJRU5ErkJggg==",
  pencil: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAJegq5eRtrAvz1TCLRxgxUn5SAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA4klEQVR4nGNgoBxwXMttwCp+8v//OdhkpP4DwUJMcUZ/kMQvTC0i/39mb5v//wAWDc4MDJb/Z2Bq+CLAwMD2/y+mBkcgxf3/G1YNmBJQDZhGQTVgWM4Y/18ZSh/AqkEEzYMwG2A0HIj+/2SATGOxQXlUA/U04IoHdnjEQWg46MShgeH+/wSsGhjO/9+AzUkMDOs/YXMSAwPf/+/YNbCBEyqmBqBr/zEwbMNwEgMD0/8Fz4r+YziJgWH//yhQtsPQwJAPEv4/HUMDQz0ol55twBAHSnwqe4ApzMAgXrYBmzDpAACaObAo4y1FrgAAAABJRU5ErkJggg==",
  warning: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAzK3sH4YPXS6d+EJy2b5NxfpnAAAACXBIWXMAAAsTAAALEwEAmpwYAAABWklEQVR4nL2SPU7DQBBGh2CHWAQEoscUtCggobSxoKOiTOdIqZG4gWloaOAGNBR05gbkBiAuAJwAORECAuFjdtc/u+ulhK/ISPuUGb/dIfrXLG493LjOmx1gPHCAAJwLB0iQhXhzdHrE8QJm9V4+xkQdHNbAAa7Ez3UNvGBE1Ma3fc7tReFBFjjCpyi36FogxoYoDbya5610GonqpdMTAyzji2hvk+gOTwZIcM8WbLJkyjd74IsNM75iTAYakNoSWPJn2C3AviEvtRUw5HNtCSjU5IdKWwFdPlYvp0BQybfSSVQBr4dCXmqTEiRdPsGKrOJKOHOFvNKuUsoH+CAjl/kWnUttLUO8kzIamaCNTGln+UE+vJAv/lh+rmje1UZpQH6OV71M2YoFIn4jxyIn/FoNbNfBPC/Tr8C3V0wk5lYesLNqZQ08nKkjoovvAnKFTp/tVuv9+ti/yA+cdc0MJb85dQAAAABJRU5ErkJggg==",
  bolt: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAzCT8v6+b3G1H8BKAYEDIhkXaAAAACXBIWXMAAAsTAAALEwEAmpwYAAABPklEQVR4nKWUPW7CQBCFV8ECIkIkbkARGjfQpjLiAkhpUpIbmCZVCnKDIHEDLkA6t7kB3ABugBSTBKEkj9m1vT/eQULiK+dJfuP3xhbiIjoP/Lwx+eKFADteiPHNzuvALyvMgD9ufrUA/jkhQYpPThjiGT1mXsXhDmNGeMN9DX1/fh3htYInXwjp5br48OaNCT1mi5UnBPh5F2tsPCHGXIgl2uV5HelUqq0c3csMeyFfsaDohWKSi448Icka6uQPiqShYogXz1BCMdnrZIYSisma54Yii8kSEn0SIVJ7fWPobmkZGmHnGhbrt+RZlQyzdeRZhczR3dJZqV7KNOmsVC9lbuisYh2TRRU9E5NNgLGJyaaGwYI7IFHBI//ldBE5vWi2cHvRrOH0YljCj0lx6tukhvq8MGJiUpz6YZzLEY60vJlcZR1SAAAAAElFTkSuQmCC",
  dollar: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAzBoM9a/3ZYZ3o/vA2ToSyfr8AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAyElEQVR4nGNgoASoBuGQiP+KQ0L+I20lmKoOCvp/EQQCVKcxt/2HAVSnKf7HLsEs///3RWxGcfz/Nhmr5Wr/87C7qv7/BOwS638bYJc4/xeHB3FK4DQKp+U4nYvTg/AgkVmugCqDCMQMA1QtLXAZIVQt0IiS3f//I6oWBqirLPP/N2CVYHj23wm7BOf/79glmPf/wi7BcP8PiRI4jcJpOS7nonlQFZTSsAVJPCKFogYikoQHShjCjcKIKCQP0lYCZwGAs8ggDgAAgV6iDdbELWkAAAAASUVORK5CYII=",
  gavel: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMA5SYkrynV6PUhC6MCeXeazlPSAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA8klEQVR4nK2TPRKCMBCFGRwLZ2yIBwA8gYU3oLKCm1B5ClvPQGlt6U3o7ZhxwBmLdZMMIT8vlW7D8B5fsstLkuS3Op6wnuYvbJSEjTSnXQQgEQFoKCAw5Ahh4FzS1ABgaqSJAO2GOxTKFgEgFh/MECD1PIOP1PMMPnKtyghiPpVPJxcWDOLkwn0ZxM6FXzOD2LnwAns0pNoa/RalaSkEdGMA0I1hIPPinQHZGASEd4QMUERaEjIXDHAuC7CyAM5lAToLcKq3AKfadwOBLX0woAwAqKXW8Pz3dLnDG9MR4Tu2aYnGAzCSWzs+kc6NPbD+p/oC+tSuaM7L6qIAAAAASUVORK5CYII=",
  email: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAzJMWvuJ9Lvk8Be9Zq2tmvJ+1AAAACXBIWXMAAAsTAAALEwEAmpwYAAAA90lEQVR4nNWTLQ7CQBCFN5CUtKIJqrIGX4LHcIEeYZE4wGGwIOkN6EG4ARKB5QptuglumP1r96f15YlNdr7N2zebWUJGqtli7mhT8Hq0A091iSDw6wBbBCdgrhWFK4JMYEsB1Ljm8HFBDA2uKTRLuz7NoRIAHjZ4ggbwMusrUIBRYEVXD3FLBahWqiOhaI8GqQTcNNHgyK/UAGN8VTPxjYfUQG1lUn6kBdJAJk2ICcSVIqmI0QERsgh1cAOQNR7OdKsm4Pbt41gAA7XPaQHMrJtxADm/ST/o9I8gZz6gfOAGR/Q+NNSToW/Q93EuJXfs+WoHP8849ANIE+9ajVfbjgAAAABJRU5ErkJggg==",
  clock: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMA98gmEgdr06Gz4H1EUoFw3osMAAAACXBIWXMAAAsTAAALEwEAmpwYAAABjklEQVR4nJ1UzysFURS+eW8eordhoaRRohS9ofRixcZOXs9SSVJYWdjZDBsbC8Uf8JaWw0LZkZ2lspeFtWaSMU/6nLm/Z5iSb3HnzPnuOXN+DmP/x9L16fjx4w91eQscY/NZfekQEgOerXeeSRWe77v0GGxYRJ3UR3R1ZZuoPaPvddHeFOJigPBWEw8Ir5S86mLIMrDM68ZkChEPcvaMB15DnwzJxygXWgl/NBGLwKqIRVa1SOTq40J6kqaS0IoTeUETVXxwS7fdyBJOEKa+K3hnWYLtYp3OTvTniQUM07mMgzzRhTc6n7hdhqjgi85XeHmihM802pDlCcdN4221NRErKUgyrzVMSsmPMkQLmLAI42oaiuGuzMfZHTEj+uM38Gwm0eGaBDmT6ARNSVLcp+3t5iXp0EXUEEU0ZdcQZTeNUpCNMq1VkK01w6CgFD1qfCTKgXShB06iiaihTCPLxIxobqjnrD2gNXhR8pq1BsWLI1ZthlZtxxWF17leFixn4TpTkBu//wBY4S/j7/gG9EHqcg3y6YMAAAAASUVORK5CYII=",
  mapPin: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAWJ4LHP3l2KuOwO8xfXChyXftAAAACXBIWXMAAAsTAAALEwEAmpwYAAABuUlEQVR4nJ1Tv0vDQBS+tEFbsKUguKaDk4PVxUWH4CYdHF3UKA7iVBdd6x8g1EFcC45dOugopCCC4KCLs0VwLgaT/lI/711zl0tbRPyG8PK+d+/Hd+8Y+z+mXmu1+caof60EjuB82L/qQMCfjfsNl/v2jznbz8WIKnBwy9jKBbCl+1PA7sCaAfKxA97CwEpW8KVVgN+UdsbxoypFfEdRL7hSdkHPm0JXmqYTUIXFuctpquL6dkik8U6F+OgBpW/hLCQmsCNyc1BHS+KX8EghBrBxBOoojc+QaKFBcR3brGCPy6x6LPi8toUT6ohXSzpeSNQDwdrUH/lKPUm05YeVe/LzK1Hpx1O5nZCoki8qbjpSEws5vV1DCEFYJw0NRw6YwEdITAqhn0kSutdlHIZERoyqRLTwpGQXrYays7KSnV9Ukylk4Sn7TrtNXmJT2QktKHbPpgu1GQb6dhRl4VqaRTUeIYW2XLgySaMQ/fIQLRNjD3KZqrjR/aSUGDcDP/4M+PKIDWjpKy2QFnudddSyaXN16Zw37Ke3k08g1qs60quPOUBVOEYqEN6A7XF+/qSHnrLC/el4/x/xA2CV76cPLrVQAAAAAElFTkSuQmCC",
  link: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAJOSswToDbdWWL+1REfayH/jtAAAACXBIWXMAAAsTAAALEwEAmpwYAAABfUlEQVR4nGNgoBVgLXaxDMAint7/////T2UY4oz+/0HghwC6hND///863v//vxVdg/7/n2EMrPP//xZA1/BpAZDi0v/fhiLOpv9fEcy49P87qkn/P0GM4Pr/CVWHvyKUNf8/ql8SE6CM+P8FGF4Bg7z/BtglOP47ECEhPtmyCptRIaBA8sG0/DI49P47ojuXERjcv/cD8QIQj+v/D5gGYLD6XOA9/B8SJkLwIAEG6x4QHfT/M4TnBtcACSVG/c9IPLASaCgJGyLzoEpAggzYNNjDjINrSIEosf+vCuKdhmtgqIc58r/NBd4j/+EaGPr/Q7y1H+JJeOJh/f8TwhCBBIstTAP7/69Q1hGQuCs8XJn/P4Mxl0+2LISLM7D8b2DACnBKMP9/gF2CE245mxiKBNy5DEJfElBk9CEeBIUSaiqv/68L0fD/E6qODHjqV0QRZ2DcD8ovqfMRwQoD0cAc1vT+P7oGSOIBgi8YeZIhXR+UVzFzMTjfm2PL99QBAIqVxBihyo8kAAAAAElFTkSuQmCC",
  chart: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAKlBMVEVHcEz///////////////////////////////////////////////////+LBpLMAAAADXRSTlMAzK8hM5PhwAWXgvt3S59ywQAAAAlwSFlzAAALEwAACxMBAJqcGAAAAHJJREFUeJxjYKALMFLGIaF7CYeE7EXyJdBsQ0ig2YaQQDN0MEkgPIMmgfAMmgQ2k4eOBNi/2CTA/sUmgUYMEgmwcxEEmgcRBFqQoAGcErXXcEjE3j2AXcL37jVBNACxn/MuBoC4mGM1DgkGlnDsRtEcAACVR3p2Hsp91QAAAABJRU5ErkJggg==",
  globe: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMA9cwTCKIjvXqEblXdRjDFYRTXAAAACXBIWXMAAAsTAAALEwEAmpwYAAABiElEQVR4nJ1UPUsDQRAdcjEfQio/EARXUijYJGm10FQqWGgRsDP6C4LYR0HrRLCPqL0Gf4CNtfEv2AtykZDERJ87e5vLJWZEnOIxN3PzZmZ3Zon+Lytnh4u79z/MzhWMLKUH7ZEdWJnKB+3hc/gysxZwHABujq05BSz07QmF7s0WWiU8LtfgPvuOKtxTKqEQRYs2FGYDAU/kKDcfUW6aXvsh+2imKY42UQVlcoqYtiWlcESUwaSFddS9wuLo6q5ucczqh+61xqphmtOY0vQ6UV2rL5Zrj3+IoOHpeY4zuqO6mjKGT/544J/CNY6mcealKKclyqJAnO9SY8iYPIv1ZzCvcRXbGt+8SmL40jiGd0N7Z9C0m0DHsDNWuBCLuri2wRZX6JKPRKrhYxFJLcpgMgnXYJNbxgip/+YQqcTkJalcsUHxSEKYGH2I4rH3LqozfFHi1crDELPjUx4eH2/gslzc4MCJIyoPNa/BRW8NNgNrYBbnetTiiKtG4RNhOcV1lh8AEp+Mv8s3ANNvHd4EYQ8AAAAASUVORK5CYII=",
  star: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAlL0Zg3WkUPIJ4jst0GILiFyRAAAACXBIWXMAAAsTAAALEwEAmpwYAAABh0lEQVR4nL1SvU4CQRAeOBSRnxgNiR3aWFjImRgbC01stDqtCBVGHwAaOpPTJ4BCO2MML6AJDyCdsdI3gNhZGfwBguLnzt2R3YW1MnGKu2/m29lvdmaI/mLrR78Q2VdzPA7cGIkwsGAkboFPswRgFIlj0DSKhNF1jSIPeEwaRbIoRdEZjycwIGqiMUZMokvk4sAkQZTElxaM3e0vAyWiKFBLH1f9qFWevYawATtNRv3FnC3wC3zrMuEGzrdXJlst7T06znfC740DLOXupeBF+RB49+rEll5gBH7VGY9XzEHb+4eAmZGEwM8EJ2jUjWgp6gWO2lVVclgGjRVZkKccfCh6E3INsthTiJgUKaKqEAmZX9CGmPJ7y+aipRAhvA3hptaGME7E1+LxTGm7Nc1eothp8JkrheB8qwh0bPVW4kVvWTs8ubad0raxgPq8WAKxFKcb2ssz4PjzJTPaDHhr+nkiZvw9CqwC9PIMzhnJuCW8XR+eCcZWiN72EK9VFIKe5iReXaH/sh/SIuglZV5zdAAAAABJRU5ErkJggg==",
  shield: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAzS7dwFf4sm0gC6SISuyOmA0WAAAACXBIWXMAAAsTAAALEwEAmpwYAAABQElEQVR4nO2TsUrEQBRFh7DBNbAfIAgZECz1DxwrxSp+gCDYCDaxsLDLdiIWamclabS2sV4srHf/QNDKMk6YCKtcn8lkMrNJI2Lnq+67h5l5A+8y9osKzu67wRKw0OX3Y0Btt30vgopR8JY/ArbosumF619FwAcLiOanln2yBiCbMDYISbwc136POsjyXV9863UNzknv63luNqh51yBRm4+p+ebOUVxoHWbuJKb/B38Bci0i6QJRg5FyQTzVYgxu+x7tS1VPGNqgj0+tbs1alNXDgVFvNljFoVY+ChskMKstYG25j2b6Xew1YNm8zdgcpFnyeYE7A4IQz7VeQZY2x6+h9FcoV6/WIJ5AzksRQXJ7xEvKEq9y5QY0GAPFZJBQrlIHMI+yJCk1GWczVWVJDmd9InQm78g53faw2LrnJ/UFxyOwOq63RXIAAAAASUVORK5CYII=",
  building: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAzK8hlDHhgvsBwfUEeL/ZHXVSAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA3ElEQVR4nGNgIBOweglCwZIAZHGeiv9w8OUAkgT7fyRQgCQR8f8bzKj8/61IEv7/b8KYvP+/IEms/zYBxuTM/4ckIf8ROxu/hBHIamUsEvogp34iRQKnUaS7Cs7+jRTCKBLIIYxiOXIIozj3N1IIo/kDEcJo/oCGMDC+96M5F+wYcHxjkwDHNzYJUHxjNQoU3+iBCJYAxTd6sIMlYAycEhhGwRnoltNAAqflA+KP9d8wJCBRCwt2JIm54MQQD4qo34JwsB+WfFj+YwEFDKiZHwbaD0CTDxpALTaIBQBt6RUR/k+HZwAAAABJRU5ErkJggg==",
  trophy: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAzN32IjbBBuquTJ1yixXsGrUgAAAACXBIWXMAAAsTAAALEwEAmpwYAAABdElEQVR4nJ2UPUvEQBCGR0gQ9AwqomUQFcsDQawk0U6wOLSxtbIQPD9ALGzFMo392VuclYWNFoLlCf4AreyTwJHzg9dJwrKze0TUKZI382xmd2Z3luh/tjYm7EKAFoT1BPAliCVIdaRAg6tt8cGDJm9LtWL+noedyIUT9YMsZDEATJuhxoFRFm3MkS9BTBt4Z3GHhg1cfLBoJmQDqnd/ApWhKievXG5lgkVJEg3qqiR5EaNUgyBTRWR7Qaikgy89iFd2r2StWJGyI8wreYglAYbxqWQH1wI4QdoolRukoQA8+1kptoy5iY6RPubv0wA7BvDqSB6I1vn1agB643wXZvixZ/pp8Lk8bcv7FiAnrxGS0PZzVbOb3Sju9xeb5f8FDAV8/Fvppe33mnm5OuhaadAqMEV0AMxaafjo8VivhdhMpIasKK8b6S0r7ASLpdjEuQHaeCrFiLGz4jS4cj+4yyOo3kSmO93octnplcC4MOxL4/f2DeLVBxo3eNLvAAAAAElFTkSuQmCC",
  lightbulb: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAzbAh8QhtNdu9kX9KpBLZ/t4sAAAACXBIWXMAAAsTAAALEwEAmpwYAAABN0lEQVR4nL2SPU7DQBCFFwKJbIcITpCCAyxyRQcFNWuJho60VE5DQRUkBBVCNNRwg3AEbhBukIYLJAo/CaCH5fUyz5gFRMGTLL2db3Z2dtZK5aobAFvZh2tVUh84PVA7+8A04XgInOXmELhkkGKWOPdG8ZrBTWGbwK2AADPafCKgR4t5PAoY0vaaGQlgn2V1nW3ghcAAHWfreCKwhztnQzwQmMOys0vYJBDg6EfgLRWVDl+Qw5t82WwMbm5q0YwJ9OWC6upZ4i1DiyHk2UIeQ0pDHGBbwAYunM3erCMgwKuz65hSIw1M1ooG2zgnoDSOrYk+Uqx2MbJ93ZeGQL9JKte26hVN6klSBlExLj0ux1ULNhKvfgLVVKdq6t8Vr+SqVtTIVe3BC7yl/kWx72z9dbffAG+p3+odgwiX9hr/9P4AAAAASUVORK5CYII=",
  handshake: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAJOWv9J/QDwLYdz8tvFgZO3HZAAAACXBIWXMAAAsTAAALEwEAmpwYAAABY0lEQVR4nL2TP0vDQBjGr0qN0AwhitCxOFoIjroEByeHLOoat5YudnJ2EZwEceiokzqK+AEc+gEc3DsJnZMSqFAe770/iXeXrn2Hu+Oe+9099+TC2Epqa3/JTJwF1N0dPEzlfCPKpYAOb70JsBiKiRC56gm5Aq+eBMRKNfAusZhgHpQLNdLG7K2V0NISEMO9R5ww9kGbVwCNwQHGfLITl4BA0NUXiCuAsR1kn7X3XcdRfSpr+KoXNnBBnfdzODWFJn6peweKwBBawBNvE267YyIpIWMgQmYiNxzxI/T8yEI2UxTfmA+NQDQCFWHXRuzQtbHz3VfqG/YpZTkIFQXIkZEjiMhDGYMpCGNJsWT/FNe2IC3VCALx7a2UpeNT+3BhaZuiGRmLpaUmVDTqzFxb4kLx8u/IXFsao19Z0rmFmJ0l9I3Na8kHSR/LBhhrc+HeBfi7esagAoznfetcrK7c33lV9QdhKrtC/GfrpwAAAABJRU5ErkJggg==",
  people: "iVBORw0KGgoAAAANSUhEUgAAADAAAAAwBAMAAAClLOS0AAAAMFBMVEVHcEz///////////////////////////////////////////////////////////9EPuwCAAAAD3RSTlMAqcwMIb76S/FvWt0f2ticID7YAAAACXBIWXMAAAsTAAALEwEAmpwYAAABKElEQVR4nL2TsWoCQRCGJygEUqXQTjjwBbw22HilleQJrgmkU6uQLo9gYxmIvWW6VL6DnY2dbTDkggFxsjP/7DFCzirxK+a/vbndu/33P6Lz0W/eTkUzSEnGgUa4SCCRy4k0dmNtiEQemIcb5vswFWLUJ7xKa3Pe9YhMwAUXabiV8zWVogx4KdLlTyfCC89ErvjbiZB8pSK10daJkB+g74UX11gXXk4tVfnyAb/iO/dOTm4wWPL2qyWVJlbbjoNqkTZa5AlH+6xPQM5Iv9lWkKmb8vX6rQDRsmRhd0A2l8ctPgU/sBTseDRTNFSYCwOlLih6bh213Iy3UAFkqst3VJ4rwLGiWhJsLQ0CaswOQHQ0WZ0P38BI6x82kq1vYKQ183+pjY7v/Rs/k1PawPXwVRsAAAAASUVORK5CYII=",
};

const encodeEmailIconPngChunk = (type: string, data: Uint8Array): Uint8Array => {
  const chunk = new Uint8Array(data.length + 12);
  const view = new DataView(chunk.buffer);
  view.setUint32(0, data.length);
  chunk.set(Array.from(type, (character) => character.charCodeAt(0)), 4);
  chunk.set(data, 8);
  let crc = 0xffffffff;
  for (let index = 4; index < data.length + 8; index += 1) {
    crc ^= chunk[index];
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0);
    }
  }
  view.setUint32(data.length + 8, (crc ^ 0xffffffff) >>> 0);
  return chunk;
};

const recolorEmailIconDataUrl = (base64: string, color: string): string => {
  const hex = color.replace(/^#/, "");
  const expanded = hex.length === 3
    ? hex.split("").map((value) => value + value).join("")
    : hex;
  const validColor = /^[0-9a-f]{6}(?:[0-9a-f]{2})?$/i.test(expanded);
  const rgb = validColor
    ? [0, 2, 4].map((offset) => parseInt(expanded.slice(offset, offset + 2), 16))
    : [10, 10, 10];
  const alpha = validColor && expanded.length === 8
    ? parseInt(expanded.slice(6, 8), 16)
    : 255;
  const bytes = Uint8Array.from(atob(base64), (value) => value.charCodeAt(0));
  const view = new DataView(bytes.buffer);
  const chunks = [bytes.slice(0, 8)];
  let paletteSize = 0;
  let hasTransparency = false;
  let offset = 8;
  while (offset + 12 <= bytes.length) {
    const length = view.getUint32(offset);
    const type = String.fromCharCode(...Array.from(bytes.slice(offset + 4, offset + 8)));
    const data = bytes.slice(offset + 8, offset + 8 + length);
    let chunk = bytes.slice(offset, offset + length + 12);
    if (type === "PLTE") {
      paletteSize = length / 3;
      for (let index = 0; index < length; index += 3) data.set(rgb, index);
      chunk = encodeEmailIconPngChunk(type, data);
    }
    if (type === "tRNS") {
      hasTransparency = true;
      if (alpha < 255) {
        // Omitted palette entries are opaque in PNG. Include them when the
        // selected color is transparent, while retaining edge antialiasing.
        const transparency = Uint8Array.from({length: paletteSize}, (_, index) =>
          Math.round((data[index] ?? 255) * alpha / 255)
        );
        chunk = encodeEmailIconPngChunk(type, transparency);
      }
    }
    if (type === "IDAT" && alpha < 255 && !hasTransparency && paletteSize > 0) {
      chunks.push(encodeEmailIconPngChunk("tRNS", new Uint8Array(paletteSize).fill(alpha)));
      hasTransparency = true;
    }
    chunks.push(chunk);
    offset += length + 12;
  }
  const recolored = new Uint8Array(chunks.reduce((length, chunk) => length + chunk.length, 0));
  let outputOffset = 0;
  chunks.forEach((chunk) => {
    recolored.set(chunk, outputOffset);
    outputOffset += chunk.length;
  });
  return `data:image/png;base64,${btoa(String.fromCharCode(...Array.from(recolored)))}`;
};

export const getEmailBuilderIconDataUrl = (
  icon: EmailBuilderRasterIconKey,
  color = "#0A0A0A"
): string => recolorEmailIconDataUrl(emailBuilderIconPngs[icon] || emailBuilderIconPngs.calendar, color);

// max-width:none: a page or mail client rule img{max-width:100%} lets a
// shrink-to-fit cell squeeze the icon out of its width, which took the right
// padding of the Logo and Date pill (Monday 13390254380, S13).
export const renderEmailBuilderIcon = (
  icon: EmailBuilderRasterIconKey,
  color: string,
  size = 20
): string => `<img data-cmp-newsletter-builder-icon="${icon}" src="${getEmailBuilderIconDataUrl(icon, color)}" alt="" width="${size}" height="${size}" style="display:inline-block;width:${size}px;height:${size}px;max-width:none;vertical-align:middle;" />`;

// Options for the gold "Change Icon" pickers in block settings panels.
export const emailBuilderIconOptions: Array<{
  key: EmailBuilderIconKey;
  label: string;
}> = [
  { key: "calendar", label: "Calendar" },
  { key: "megaphone", label: "Megaphone" },
  { key: "memberSearch", label: "Member Search" },
  { key: "personAdd", label: "Person Add" },
  { key: "sparkle", label: "Sparkle" },
  { key: "news", label: "News" },
  { key: "briefcase", label: "Briefcase" },
  { key: "bank", label: "Bank" },
  { key: "phone", label: "Phone" },
  { key: "tag", label: "Tag" },
  { key: "pencil", label: "Pencil" },
  { key: "warning", label: "Warning" },
  { key: "bolt", label: "Lightning Bolt" },
  { key: "dollar", label: "Dollar" },
  { key: "gavel", label: "Gavel" },
  { key: "email", label: "Email" },
  { key: "clock", label: "Clock" },
  { key: "mapPin", label: "Map Pin" },
  { key: "link", label: "Link" },
  { key: "chart", label: "Chart" },
  { key: "globe", label: "Globe" },
  { key: "star", label: "Star" },
  { key: "shield", label: "Shield" },
  { key: "building", label: "Building" },
  { key: "trophy", label: "Trophy" },
  { key: "lightbulb", label: "Lightbulb" },
  { key: "handshake", label: "Handshake" },
];

export const getEmailBuilderIconOptions = (
  selectedIcon: EmailBuilderIconKey,
): typeof emailBuilderIconOptions =>
  selectedIcon === "people"
    ? [...emailBuilderIconOptions, { key: "people", label: "People" }]
    : emailBuilderIconOptions;

// ---------------------------------------------------------------------------
// Block type keys (27 design types and eight retained application types)
// ---------------------------------------------------------------------------

export type EmailBuilderBlockType =
  | "textAndButtonHeroBanner"
  | "textHeroBanner"
  | "emailBanner"
  | "updateBanner"
  | "alertBanner"
  | "advocacyUpdateBanner"
  | "memberSpotlight"
  | "articleList"
  | "splitArticleWithBorder"
  | "textAndEmailContent"
  | "fourGridIconAndTitle"
  | "sixGridIconAndTitle"
  | "upcomingEventsList"
  | "eventAnnouncement"
  | "specialGuestSponsor"
  | "sponsorsList"
  | "advertisingPdf"
  | "keyLinks"
  | "inquiries"
  | "signature"
  | "heading"
  | "text"
  | "image"
  | "button"
  | "divider"
  | "spacer"
  | "socialLinks"
  | "newsroomHighlights"
  | "newMembers"
  | "advertisementBanner"
  | "gallery"
  | "footer"
  | "columns"
  | "imageText"
  | "video"
  | "subscribe"
  | "logoDate"
  | "perks"
  | "perkWeek";

// Canonical palette labels (also used by the selected-block "Block | <name>"
// chip — the real block name, never the mock's copy-pasted chip text).
export const emailBuilderBlockLabels: Record<EmailBuilderBlockType, string> = {
  textAndButtonHeroBanner: "Text and Button Hero Banner",
  textHeroBanner: "Text Hero Banner",
  emailBanner: "Email Banner",
  updateBanner: "Update Banner",
  alertBanner: "Alert Banner",
  advocacyUpdateBanner: "Advocacy Update Banner",
  memberSpotlight: "Member Spotlight",
  articleList: "Information Block",
  splitArticleWithBorder: "Split Article with Border",
  textAndEmailContent: "Text and Email Content",
  fourGridIconAndTitle: "Four-Grid Icon and Title",
  sixGridIconAndTitle: "Six-Grid Icon and Title",
  upcomingEventsList: "Upcoming Events List",
  eventAnnouncement: "Event Announcement",
  specialGuestSponsor: "Special Guest Sponsor",
  sponsorsList: "Sponsors List (1 to 3 sponsors per block)",
  advertisingPdf: "Advertising PDF",
  keyLinks: "Key Links",
  inquiries: "Inquiries",
  signature: "Signature",
  heading: "Heading",
  text: "Text",
  image: "Image",
  button: "Button",
  divider: "Divider",
  spacer: "Spacer",
  socialLinks: "Social Links",
  newsroomHighlights: "Newsroom Highlights",
  newMembers: "New Members",
  advertisementBanner: "Advertisement Banner",
  gallery: "Gallery",
  footer: "Footer",
  columns: "Columns",
  imageText: "Image + Text",
  video: "Video",
  subscribe: "Subscribe to Newsletter",
  logoDate: "Logo and Date",
  perks: "Member Perks",
  perkWeek: "Perk of the Week",
};

// ---------------------------------------------------------------------------
// Repeating-item shapes
// ---------------------------------------------------------------------------

export type EmailBuilderIconGridItem = {
  id: string;
  icon: EmailBuilderIconKey; // icon tile + "Change Icon"
  title: string; // "TITLE"
  linkTo: string; // "LINK TO (OPTIONAL)" — placeholder "Add a link"
  linkMode?: "url" | "pdf";
  pdfUrl?: string;
  pdfFileName?: string;
  // Renders the icon on a light highlight tile (seen on "Promote Your
  // Business" in the Member Welcome template / Six-Grid screenshot).
  highlighted?: boolean;
};

export type MemberSpotlightItem = {
  id: string;
  imageUrl: string; // image thumbnail + "Change Image"
  title: string; // "TITLE"
  description: string; // "DESCRIPTION"
  buttonName: string; // "BUTTON NAME" — renders as "<name> ›" link
  addLink: string; // "ADD LINK" — placeholder "Add link to redirect the button"
};

export type ArticleListItem = {
  id: string;
  imageUrl?: string;
  linkText?: string;
  linkUrl?: string;
  title: string; // "TITLE"
  description: string; // "DESCRIPTION"
};

export type SplitArticleItem = {
  id: string;
  icon: EmailBuilderIconKey; // icon tile + "Change Icon"
  title: string; // "TITLE"
  desc: string; // "DESC"
  buttonName: string; // "BUTTON NAME" — renders as blue caps "<NAME> ›" link
  addLink: string; // "ADD LINK"
};

export type KeyLinkItem = {
  id: string;
  buttonText: string; // "BUTTON TEXT"
  addLink: string; // "ADD LINK"
};

export type EmailBuilderSponsorItem = {
  id: string;
  logoUrl: string; // logo thumbnail + "Change Sponsor Logo"
  linkTo: string; // "LINK TO (OPTIONAL)" — placeholder "Add a link"
};

// Event card used by Upcoming Events List ("EVENT n DETAILS") and Event
// Announcement ("EVENT DETAILS"). Events are picked from the real Events
// module (EMAIL-009): `eventId` stores the picked CmpEvent id and `linkUrl`
// stores the website destination. Upcoming Events "Learn More ›" opens the
// event details page; Event Announcement "Register Now" opens registration.
export type EmailBuilderEventItem = {
  id: string;
  eventId: string;
  eventTitle: string; // "EVENT TITLE"
  date: string; // "DATE"
  time: string; // "TIME"
  location: string; // "LOCATION" (extra lines render as the gray address line)
  linkUrl: string;
  tag?: string;
  tagColor?: string;
  description?: string;
  /** The event's featured (hero) image; D22 EC-26 card image. */
  imageUrl?: string;
};

export type NewsroomHighlightItem = {
  id: string;
  title: string;
  linkUrl: string;
  imageUrl: string;
  category: string;
  publishedAtUtcMilli: number | null;
  author: string;
  readTime: string;
  sourceAttribution: string;
};

export type NewMemberItem = {
  id: string;
  name: string;
  logoUrl: string;
  category: string;
  joinedAtUtcMilli: number | null;
  linkUrl: string;
};

export type GalleryItem = {
  id: string;
  imageUrl: string;
  title: string;
  linkUrl: string;
  /** D22 EC-30: optional button under the image title. */
  buttonLabel?: string;
  buttonUrl?: string;
};

export type NewsroomHighlightsSettings = {
  title: string;
  highlightColor: string;
  items: NewsroomHighlightItem[];
  /** COLOR_DEFS newsroom Button Colour; missing = highlightColor. */
  buttonColor?: string;
};

/** D22 EC-27 (F10): a newsroom block shows at most 6 articles. */
export const NEWSROOM_BLOCK_MAX_ARTICLES = 6;

export type NewMembersSettings = {
  title: string;
  highlightColor: string;
  items: NewMemberItem[];
  /** Joined-date window in days. Missing on blocks saved before Oct 2026 = 30. */
  days?: number;
  /**
   * Members the user removed from this block. A list refresh adds newly
   * qualifying members but never re-adds a member the user removed.
   */
  excludedMemberIds?: string[];
  /**
   * Members the user added from "+ Add Members" who are outside the 30-day
   * window. A list refresh keeps them while they stay Active.
   */
  includedMemberIds?: string[];
};

export type AdvertisementBannerSettings = {
  imageUrl: string;
  linkUrl: string;
  /** Design (16) AD_SIZES: large = 1 image, medium = 2, small = 3. Missing = large. */
  size?: "large" | "medium" | "small";
  /** Images 2 and 3 for medium and small (image 1 is imageUrl/linkUrl). */
  extraSlots?: Array<{ imageUrl: string; linkUrl: string }>;
  /** IMAGE DISPLAY. Missing = the saved behaviour (whole image). */
  imageFit?: "cover" | "contain";
};

export const emailAdBannerSizes = {
  large: { label: "Large", tier: "Premier Sponsor", count: 1, width: 600, height: 200 },
  medium: { label: "Medium", tier: "Feature Sponsor", count: 2, width: 290, height: 240 },
  small: { label: "Small", tier: "Supporting Sponsor", count: 3, width: 190, height: 120 },
} as const;

export const getEmailAdBannerSlots = (
  settings: AdvertisementBannerSettings,
): Array<{ imageUrl: string; linkUrl: string }> => {
  const count = emailAdBannerSizes[settings.size || "large"].count;
  const base = [{ imageUrl: settings.imageUrl, linkUrl: settings.linkUrl }, ...(settings.extraSlots || [])];
  return Array.from({ length: count }, (_, index) => base[index] || { imageUrl: "", linkUrl: "" });
};
export type GallerySettings = { title: string; items: GalleryItem[]; buttonColor?: string };
export type FooterSettings = { organization: string; communityName: string };
export type ColumnsSettings = { left: string; right: string };
export type ImageTextSettings = {
  imageUrl: string;
  imageSize: number;
  text: string;
  linkText: string;
  linkUrl: string;
  highlightColor: string;
};
export type VideoSettings = { caption: string; linkUrl: string; imageUrl: string };

/** Design (16) "Subscribe to Newsletter" (EC-24). */
export type SubscribeSettings = {
  title: string; // QUESTION
  text: string; // TEXT
  buttonName: string;
  linkUrl: string; // BUTTON LINK (URL)
  highlightColor: string; // Button Colour
};

/** A member perk copied into an email (D22 EC-24). */
export type EmailBuilderPerkItem = {
  id: string;
  title: string;
  company: string;
  logoUrl: string;
  description: string;
  /** "In Person", "Online" or "Contact Company". */
  redeem: string;
  /** "Valid until Dec 31, 2026" or "". */
  validUntil: string;
  createdAtUtcMilli: number | null;
  // Perk of the Week phone (design (19)); absent on perks saved before Oct 2026.
  /** Redemption Availability (the app's once / multiple times text). */
  availability?: string;
  /** To Redeem Perk (the app's instruction for the redeem type). */
  howToRedeem?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  /** The member's links, shown as icons under "Connect with ...". */
  socials?: EmailBuilderPerkSocialKey[];
};

export type EmailBuilderPerkSocialKey =
  | "website" | "email" | "phone" | "google" | "instagram" | "facebook" | "linkedin" | "x" | "tiktok";

/** Design (16) "Auto Add 5 Most Recent Perks". */
export type PerksSettings = {
  title: string;
  highlightColor: string;
  items: EmailBuilderPerkItem[];
  /** True once the 5 most recent perks were added. */
  seeded?: boolean;
};

/** Design (16) "Auto Select a Perk of the Week". */
export type PerkWeekSettings = {
  label: string; // TITLE
  subtitle: string;
  headline: string;
  linkUrl: string; // PERK LINK (URL)
  /** Always #E1A731: the banner is fixed gold (design (19), no picker). */
  highlightColor: string;
  perk: EmailBuilderPerkItem | null;
};

/** Design (16) "Logo and Date" (EC-20). */
export type LogoDateSettings = {
  /**
   * New blocks start with the Step 2 Chamber Logo. Empty without logoRemoved
   * (blocks saved before Oct 2026) = the Step 2 Chamber Logo.
   */
  logoUrl: string;
  /** "Delete Logo": the block shows no logo, not the Step 2 Chamber Logo. */
  logoRemoved?: boolean;
  logoWidth: number; // px, 90-300
  layout: "split" | "centered";
  date: string;
  dateBackgroundColor: string;
};

export type EmailBuilderEventLinkDestination = "details" | "registration";

export const buildEmailBuilderEventLinkUrl = ({
  chamberWebsiteUrl,
  slug,
  destination,
}: {
  chamberWebsiteUrl: string | null | undefined;
  slug: string;
  destination: EmailBuilderEventLinkDestination;
}): string => {
  const base = (chamberWebsiteUrl || "").trim().replace(/\/+$/, "");
  const eventDetailsUrl = `${base}/events/${slug}`;

  return destination === "registration"
    ? `${eventDetailsUrl}/purchase`
    : eventDetailsUrl;
};

const eventRegistrationPathPattern =
  /^([^?#]*\/events\/[^/?#]+)\/purchase\/?(?=[?#]|$)/;

/**
 * Keeps older saved Upcoming Events cards compatible with the current
 * Learn More destination without changing Event Announcement registration.
 */
export const normalizeUpcomingEventLinkUrl = (linkUrl: string): string =>
  normalizeEmailLinkUrl(linkUrl).replace(eventRegistrationPathPattern, "$1");

// ---------------------------------------------------------------------------
// Per-block settings (field order/labels per blocks-a.md / blocks-b.md)
// ---------------------------------------------------------------------------

export type TextAndButtonHeroBannerSettings = {
  backgroundImageUrl: string; // "BACKGROUND IMAGE" + "Change Image"
  title: string; // "TITLE"
  helperText: string; // "HELPER TEXT"
  textColor: string; // "Select Text Color"
  highlightColor: string; // "Select Highlight Color" (button fill)
  backgroundColor: string; // "Select Background Color"
  buttonName: string; // "BUTTON NAME"
  buttonTextColor: string; // "Text Color" (button)
  addLink: string; // "ADD LINK"
  /** Design (16) hero "ADD IMAGE" (EC-25): optional image beside the text. */
  sideImageUrl?: string;
  sideImagePosition?: "left" | "right";
  /** IMAGE WIDTH in px, 90-300 (design default 200). */
  sideImageWidth?: number;
  sideImageFit?: "cover" | "contain";
};

export type TextHeroBannerSettings = {
  textColor: string; // "Select Text Color"
  backgroundColor: string; // "Select Background Color"
  title: string; // "TITLE"
  textContent: string; // "TEXT CONTENT"
};

export type EmailBannerSettings = {
  textColor: string;
  highlightColor: string; // chip fill
  backgroundColor: string;
  ceoImageUrl: string; // "CEO IMAGE" + "Change Image"
  title: string; // "TITLE" (multi-line; last line renders in highlight italic)
  buttonName: string; // "BUTTON NAME" (renders as the uppercase chip)
  buttonTextColor: string; // "Button Text Color"
  addLink: string; // "ADD LINK"
};

export type UpdateBannerSettings = {
  title: string; // "TITLE"
  description: string; // "DESCRIPTION"
  textColor: string;
  highlightColor: string; // button fill
  backgroundColor: string;
  buttonName: string; // "BUTTON NAME" (drives the rendered button label)
  buttonTextColor: string; // "Button Text Color"
  addLink: string; // "ADD LINK"
};

export type AlertBannerSettings = {
  alertIcon: EmailBuilderIconKey; // "ALERT ICON" + "Change Icon"
  alertTitle: string; // "ALERT TITLE"
  textColor: string; // "Select Text Color" (live banner text color)
  backgroundColor: string; // "Select Background Color" (no highlight row)
};

export type AdvocacyUpdateBannerSettings = {
  icon: EmailBuilderIconKey; // icon tile + "Change Icon"
  title: string; // "TITLE" (the eyebrow, e.g. ADVOCACY UPDATE)
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  label: string; // "LABEL" (grouped sub-card)
  description: string; // "DESCRIPTION" (grouped sub-card, rich w/ link)
  buttonName: string; // "BUTTON NAME"
  buttonColor: string; // "Button Color"
  buttonTextColor: string; // "Button Text Color"
  addLink: string; // "ADD LINK"
};

export type MemberSpotlightSettings = {
  title: string; // "TITLE"
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  items: MemberSpotlightItem[]; // "Grid Items" / "+ Add Grid Item"
};

export type ArticleListSettings = {
  title?: string;
  imageSize?: number;
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  items: ArticleListItem[]; // "Grid Items" / "+ Add Grid Item"
};

export type SplitArticleWithBorderSettings = {
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  items: SplitArticleItem[]; // "+ Add Grid Item"
};

export type TextAndEmailContentSettings = {
  textContent: string; // "TEXT CONTENT" (toolbar includes alignment controls)
  textAlign: "left" | "center" | "right" | "justify";
  textColor: string;
  backgroundColor: string;
};

export type FourGridIconAndTitleSettings = {
  title: string; // "TITLE"
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  items: EmailBuilderIconGridItem[]; // "Grid Items" / "+ Add Grid Item"
};

export type SixGridIconAndTitleSettings = {
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  title: string; // "TITLE"
  items: EmailBuilderIconGridItem[]; // "Grid Items" / "+ Add Grid Item"
};

export type UpcomingEventsListSettings = {
  title: string; // "TITLE"
  textColor: string;
  imageUrl: string; // image thumbnail + "Change Image"
  highlightColor: string; // date tile / links
  backgroundColor: string;
  events: EmailBuilderEventItem[]; // "EVENT n DETAILS" / "+ Add Another Event Card"
  /** COLOR_DEFS events Button Colour (Register Now); missing = highlight. */
  buttonColor?: string;
};

export type EventAnnouncementSettings = {
  buttonName?: string;
  title: string; // "TITLE" (optional heading; empty by default per template)
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  events: EmailBuilderEventItem[]; // "EVENT DETAILS" / "+ Add Another Event"
  /** COLOR_DEFS eventAnn Button Colour (EC-38); missing = highlightColor. */
  buttonColor?: string;
};

export type SpecialGuestSponsorSettings = {
  textColor: string;
  backgroundColor: string;
  title: string; // "TITLE"
  sponsorLogoUrl: string; // "SPONSOR LOGO" + "Change Image"
  textContent: string; // "TEXT CONTENT" (italic default)
};

export type SponsorsListSettings = {
  title: string; // "TITLE" (before the color rows on this block)
  textColor: string;
  backgroundColor: string;
  sponsors: EmailBuilderSponsorItem[]; // "SPONSOR LOGO" group / "+ Add Sponsor" (1 to 3)
};

export type AdvertisingPdfSettings = {
  textColor: string;
  highlightColor: string; // button fill
  backgroundColor: string;
  title: string; // "TITLE"
  imageUrl: string; // "IMAGE" + "Change Image"
  buttonName: string; // "BUTTON NAME"
  buttonTextColor: string; // "Button Text Color"
  addLink: string; // "ADD LINK" — mutually exclusive with ATTACH PDF ("OR")
  attachPdfUrl: string; // "ATTACH PDF" upload tile
  attachPdfName: string;
};

export type KeyLinksSettings = {
  title: string; // "TITLE"
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  items: KeyLinkItem[]; // "Grid Items" / "+ Add Grid Item"
};

export type InquiriesSettings = {
  textColor: string;
  backgroundColor: string; // no highlight row on this block
  title: string; // "TITLE"
  textContent: string; // "TEXT CONTENT"
  email: string; // "EMAIL"
  phoneNumber: string; // "PHONE NUMBER"
};

export type SignatureSettings = {
  /**
   * Design (19) DEFAULTS.signature: the one SIGNATURE rich-text field. The
   * block shows the chamber name and the Step 2 Chamber Logo below it.
   * Missing on blocks saved before Oct 2026: those blocks keep the legacy
   * fields below until the builder loads them (migrateLegacySignatureSettings).
   */
  text?: string;
  textColor: string;
  backgroundColor: string;
  /** Legacy (before Oct 2026): opening line, "Sincerely," when missing. */
  opening?: string;
  /** Legacy: script-styled signature line. */
  addSignature?: string;
  /** Legacy: "NAME". */
  name?: string;
  /** Legacy: "TITLE LINE". */
  designation?: string;
  /** Legacy: "ORGANIZATION". */
  chamberName?: string;
};

// Optional presentation fields preserve version-2 drafts without reseeding content.
export type EmailBuilderPresentationSettings = {
  hiddenFields?: string[];
  topImageUrl?: string;
  imageWidth?: number;
  extraText?: string;
  pdfUrl?: string;
  pdfFileName?: string;
};
export type SimpleEmailBlockSettings = {
  text: string;
  imageUrl: string;
  imageWidth: number;
  addLink: string;
  textAlign: "left" | "center" | "right";
  textColor: string;
  backgroundColor: string;
  highlightColor: string;
  height: number;
  /** Divider only (D22 EC-35): line thickness in px, 1-12. Missing = 1. */
  lineHeight?: number;
  /** Image only (What's New): Cover Image (3:2 frame) or Fit Image. Missing = Fit. */
  imageFit?: "cover" | "contain";
};

export type EmailBuilderBlockSettingsMap = {
  textAndButtonHeroBanner: TextAndButtonHeroBannerSettings;
  textHeroBanner: TextHeroBannerSettings;
  emailBanner: EmailBannerSettings;
  updateBanner: UpdateBannerSettings;
  alertBanner: AlertBannerSettings;
  advocacyUpdateBanner: AdvocacyUpdateBannerSettings;
  memberSpotlight: MemberSpotlightSettings;
  articleList: ArticleListSettings;
  splitArticleWithBorder: SplitArticleWithBorderSettings;
  textAndEmailContent: TextAndEmailContentSettings;
  fourGridIconAndTitle: FourGridIconAndTitleSettings;
  sixGridIconAndTitle: SixGridIconAndTitleSettings;
  upcomingEventsList: UpcomingEventsListSettings;
  eventAnnouncement: EventAnnouncementSettings;
  specialGuestSponsor: SpecialGuestSponsorSettings;
  sponsorsList: SponsorsListSettings;
  advertisingPdf: AdvertisingPdfSettings;
  keyLinks: KeyLinksSettings;
  inquiries: InquiriesSettings;
  signature: SignatureSettings;
  heading: SimpleEmailBlockSettings;
  text: SimpleEmailBlockSettings;
  image: SimpleEmailBlockSettings;
  button: SimpleEmailBlockSettings;
  divider: SimpleEmailBlockSettings;
  spacer: SimpleEmailBlockSettings;
  socialLinks: SimpleEmailBlockSettings;
  newsroomHighlights: NewsroomHighlightsSettings;
  newMembers: NewMembersSettings;
  advertisementBanner: AdvertisementBannerSettings;
  gallery: GallerySettings;
  footer: FooterSettings;
  columns: ColumnsSettings;
  imageText: ImageTextSettings;
  video: VideoSettings;
  subscribe: SubscribeSettings;
  logoDate: LogoDateSettings;
  perks: PerksSettings;
  perkWeek: PerkWeekSettings;
};

export type EmailBuilderBlockOf<T extends EmailBuilderBlockType> = {
  id: string;
  type: T;
  settings: EmailBuilderBlockSettingsMap[T] & EmailBuilderPresentationSettings;
};

export type EmailBuilderBlock = {
  [K in EmailBuilderBlockType]: EmailBuilderBlockOf<K>;
}[EmailBuilderBlockType];

// ---------------------------------------------------------------------------
// Builder state (version 2)
// ---------------------------------------------------------------------------

export type EmailBuilderState = {
  version: 2;
  preservedDocument?: {
    builderStateJson: string;
    contentHtml: string;
    contentText: string;
    footerHtml: string;
  };
  newsletterLabel?: string;
  logoUrl: string; // Step 2 "Chamber Logo"
  greetingLine: "" | "firstName" | "fullName" | "companyName" | "none";
  // Version-2 storage keeps this field name for backwards compatibility. It
  // now owns only the chamber-customizable message; copyright, social links,
  // and unsubscribe markup are generated from authoritative profile data.
  footerText: string;
  fontFamily: string; // Template Settings tab "CHOOSE FONT" (default DM Sans)
  theme: {
    accentColor?: string;
    textColor: string;
    highlightColor: string;
    backgroundColor: string; // Template Settings tab "BACKGROUND COLOR"
    footerBackgroundColor: string;
  };
  selectedBlockId: string;
  blocks: EmailBuilderBlock[];
};

export type EmailFooterProfile = {
  name?: string | null;
  /** Chamber website root, used to replace expiring website file links. */
  chamberMemberProWebsiteUrl?: string | null;
  facebook?: string | null;
  instagram?: string | null;
  threads?: string | null;
  linkedin?: string | null;
  twitter?: string | null;
  tiktok?: string | null;
  youtube?: string | null;
};

export type EmailFooterSocialPlatform = Exclude<
  keyof EmailFooterProfile,
  "name" | "chamberMemberProWebsiteUrl"
>;

export type EmailFooterSocialLink = {
  platform: EmailFooterSocialPlatform;
  label: string;
  url: string;
  iconDataUrl: string;
};

export type EmailBuilderStateUpdate =
  | EmailBuilderState
  | ((currentState: EmailBuilderState) => EmailBuilderState);

export type UpdateEmailBuilderState = (
  update: EmailBuilderStateUpdate
) => void;

export type RenderedEmailBuilderContent = {
  /** Full email preview HTML (body + footer). */
  html: string;
  /** Email HTML without the footer — stored as `contentHtml`. */
  bodyHtml: string;
  /** Footer HTML — stored as `footerHtml` so the backend wrapper appends it. */
  footerHtml: string;
  text: string;
};

const defaultTheme = {
  accentColor: "#9DCBF7",
  textColor: emailBuilderColors.darkNavy,
  highlightColor: emailBuilderColors.blue,
  backgroundColor: emailBuilderColors.white,
  footerBackgroundColor: emailBuilderColors.black,
};

// Quick "Color Palatte" strip offered next to the free color picker (blue
// theme: blue / light blue / black / white per the Four-Grid composite).
export const getEmailThemeQuickPalette = (theme: {
  highlightColor: string;
}): string[] => [
  theme.highlightColor,
  emailBuilderColors.lightBlue,
  emailBuilderColors.black,
  emailBuilderColors.white,
];

export const getReadableEmailTextColor = (backgroundColor: string): string => {
  const darkTextColor = "#0A0A0A";
  const value = backgroundColor.replace(/^#/, "");
  const expanded = value.length === 3 ? value.split("").map((c) => c + c).join("") : value;
  if (!/^[0-9a-f]{6}$/i.test(expanded)) return darkTextColor;
  const rgb = [0, 2, 4].map((offset) => parseInt(expanded.slice(offset, offset + 2), 16));
  return (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255 > 0.55
    ? darkTextColor : emailBuilderColors.white;
};

// C3: the backend refreshes the New Members rows at scheduled send time. It
// finds them by this attribute on their container. Keep this markup in step
// with cap-backend src/utilities/cmpNewsletterNewMembersRefresh.ts.
export const NEW_MEMBERS_ROWS_MARKER_ATTRIBUTE = "data-cmp-new-members";

const makeId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

// ---------------------------------------------------------------------------
// Item factories (used by "+ Add Grid Item" / "+ Add Sponsor" / event cards)
// ---------------------------------------------------------------------------

export const createIconGridItem = (
  overrides: Partial<Omit<EmailBuilderIconGridItem, "id">> = {}
): EmailBuilderIconGridItem => ({
  id: makeId("item"),
  icon: "calendar",
  title: "",
  linkTo: "",
  ...overrides,
});

export const createMemberSpotlightItem = (
  overrides: Partial<Omit<MemberSpotlightItem, "id">> = {}
): MemberSpotlightItem => ({
  id: makeId("item"),
  imageUrl: "",
  title: "",
  description: "",
  buttonName: "Read Their Story",
  addLink: "",
  ...overrides,
});

export const createArticleListItem = (
  overrides: Partial<Omit<ArticleListItem, "id">> = {}
): ArticleListItem => ({
  id: makeId("item"),
  title: "",
  description: "",
  imageUrl: "",
  linkText: "",
  linkUrl: "",
  ...overrides,
});

export const createSplitArticleItem = (
  overrides: Partial<Omit<SplitArticleItem, "id">> = {}
): SplitArticleItem => ({
  id: makeId("item"),
  icon: "pencil",
  title: "",
  desc: "",
  buttonName: "",
  addLink: "",
  ...overrides,
});

export const createKeyLinkItem = (
  overrides: Partial<Omit<KeyLinkItem, "id">> = {}
): KeyLinkItem => ({
  id: makeId("item"),
  buttonText: "",
  addLink: "",
  ...overrides,
});

export const createSponsorItem = (
  overrides: Partial<Omit<EmailBuilderSponsorItem, "id">> = {}
): EmailBuilderSponsorItem => ({
  id: makeId("sponsor"),
  logoUrl: "",
  linkTo: "",
  ...overrides,
});

export const createEventItem = (
  overrides: Partial<Omit<EmailBuilderEventItem, "id">> = {}
): EmailBuilderEventItem => ({
  id: makeId("event"),
  eventId: "",
  eventTitle: "",
  date: "",
  time: "",
  location: "",
  linkUrl: "",
  ...overrides,
});

// ---------------------------------------------------------------------------
// Block factories (SAMPLE defaults come from the block screenshots)
// ---------------------------------------------------------------------------

export const createTextAndButtonHeroBannerBlock = (
  overrides: Partial<TextAndButtonHeroBannerSettings> = {}
): EmailBuilderBlockOf<"textAndButtonHeroBanner"> => ({
  id: makeId("textAndButtonHeroBanner"),
  type: "textAndButtonHeroBanner",
  settings: {
    backgroundImageUrl: "",
    title: "The Chamber Voice",
    helperText: "Your Weekly Business Update",
    textColor: emailBuilderColors.white,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.black,
    // Design (16) hero (EC-25): no button on new blocks. Saved blocks keep
    // their stored button.
    buttonName: "",
    buttonTextColor: emailBuilderColors.white,
    addLink: "",
    ...overrides,
  },
});

export const createTextHeroBannerBlock = (
  overrides: Partial<TextHeroBannerSettings> = {}
): EmailBuilderBlockOf<"textHeroBanner"> => ({
  id: makeId("textHeroBanner"),
  type: "textHeroBanner",
  settings: {
    textColor: emailBuilderColors.white,
    backgroundColor: emailBuilderColors.blue,
    title: "Welcome to the Chamber!",
    textContent: "We're excited to have you as part of our business community.",
    ...overrides,
  },
});

export const createEmailBannerBlock = (
  overrides: Partial<EmailBannerSettings> = {}
): EmailBuilderBlockOf<"emailBanner"> => ({
  id: makeId("emailBanner"),
  type: "emailBanner",
  settings: {
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    ceoImageUrl: "",
    title: "From the\nDesk\nof Our CEO",
    buttonName: "Chamber Updates",
    buttonTextColor: emailBuilderColors.white,
    addLink: "",
    ...overrides,
  },
});

export const createUpdateBannerBlock = (
  overrides: Partial<UpdateBannerSettings> = {}
): EmailBuilderBlockOf<"updateBanner"> => ({
  id: makeId("updateBanner"),
  type: "updateBanner",
  settings: {
    title: "New Provincial Grant Now Available!",
    description:
      "Funding to help local businesses grow and innovate. Take advantage of this exclusive opportunity before the deadline.",
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    buttonName: "Apply Now",
    buttonTextColor: emailBuilderColors.white,
    addLink: "",
    ...overrides,
  },
});

/** Design (19) DEFAULTS.alert extraText for new Urgent Alert Banner blocks. */
export const ALERT_BANNER_DEFAULT_EXTRA_TEXT =
  "Add additional text here to give members more details about this update.";

export const createAlertBannerBlock = (
  overrides: Partial<AlertBannerSettings & EmailBuilderPresentationSettings> = {}
): EmailBuilderBlockOf<"alertBanner"> => ({
  id: makeId("alertBanner"),
  type: "alertBanner",
  settings: {
    alertIcon: "warning",
    alertTitle: "IMPORTANT UPDATE",
    textColor: emailBuilderColors.white,
    backgroundColor: emailBuilderColors.blue,
    extraText: ALERT_BANNER_DEFAULT_EXTRA_TEXT,
    ...overrides,
  },
});

export const createAdvocacyUpdateBannerBlock = (
  overrides: Partial<AdvocacyUpdateBannerSettings> = {}
): EmailBuilderBlockOf<"advocacyUpdateBanner"> => ({
  id: makeId("advocacyUpdateBanner"),
  type: "advocacyUpdateBanner",
  settings: {
    icon: "megaphone",
    title: "ADVOCACY UPDATE",
    textColor: emailBuilderColors.white,
    highlightColor: "#C9C9C9",
    backgroundColor: emailBuilderColors.black,
    label: "Protecting Local Interests",
    description:
      "See how your chamber is advocating for business at the local, provincial, and federal levels.",
    buttonName: "View Update",
    buttonColor: emailBuilderColors.white,
    buttonTextColor: emailBuilderColors.darkNavy,
    addLink: "",
    ...overrides,
  },
});

export const createMemberSpotlightBlock = (
  overrides: Partial<MemberSpotlightSettings> = {}
): EmailBuilderBlockOf<"memberSpotlight"> => ({
  id: makeId("memberSpotlight"),
  type: "memberSpotlight",
  settings: {
    title: "Member Spotlight",
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.lightBlue,
    items: [
      createMemberSpotlightItem({
        title: "Coastal Coffee Co.",
        description:
          "A locally owned café proudly serving our community with premium coffee and friendly service. Find them at the harbour front this summer!",
        buttonName: "Read Their Story",
      }),
    ],
    ...overrides,
  },
});

export const createArticleListBlock = (
  overrides: Partial<ArticleListSettings> = {}
): EmailBuilderBlockOf<"articleList"> => ({
  id: makeId("articleList"),
  type: "articleList",
  settings: {
    title: "Section Title",
    imageSize: 150,
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.lightBlue,
    items: [
      createArticleListItem({
        title: "Heading",
        description: "Text",
        linkText: "Read More",
      }),
    ],
    ...overrides,
  },
});

export const createSplitArticleWithBorderBlock = (
  overrides: Partial<SplitArticleWithBorderSettings> = {}
): EmailBuilderBlockOf<"splitArticleWithBorder"> => ({
  id: makeId("splitArticleWithBorder"),
  type: "splitArticleWithBorder",
  settings: {
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    items: [
      createSplitArticleItem({
        icon: "personAdd",
        title: "New Members",
        desc: "Join us in welcoming our newest business members to the chamber.",
        buttonName: "MEET THEM",
      }),
      createSplitArticleItem({
        icon: "news",
        title: "Chamber News",
        desc: "Stay up to date on the latest news and important chamber updates.",
        buttonName: "LATEST NEWS",
      }),
      createSplitArticleItem({
        icon: "megaphone",
        title: "From The Desk",
        desc: "A message from our CEO on what’s ahead for our community.",
        buttonName: "READ MESSAGE",
      }),
    ],
    ...overrides,
  },
});

export const createTextAndEmailContentBlock = (
  overrides: Partial<TextAndEmailContentSettings> = {}
): EmailBuilderBlockOf<"textAndEmailContent"> => ({
  id: makeId("textAndEmailContent"),
  type: "textAndEmailContent",
  settings: {
    textContent:
      "Join local business leaders for an afternoon of networking, lunch, and valuable insights. Our upcoming luncheon is the perfect opportunity to build lasting professional connections within the Sand Bay community.",
    textAlign: "center",
    textColor: emailBuilderColors.darkNavy,
    backgroundColor: emailBuilderColors.white,
    ...overrides,
  },
});

export const createFourGridIconAndTitleBlock = (
  overrides: Partial<FourGridIconAndTitleSettings> = {}
): EmailBuilderBlockOf<"fourGridIconAndTitle"> => ({
  id: makeId("fourGridIconAndTitle"),
  type: "fourGridIconAndTitle",
  settings: {
    title: "What is in the Newsletter?",
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    items: [
      createIconGridItem({ icon: "calendar", title: "UPCOMING EVENTS" }),
      createIconGridItem({ icon: "megaphone", title: "CHAMBER NEWS" }),
      createIconGridItem({ icon: "memberSearch", title: "MEMBER SPOTLIGHT" }),
      createIconGridItem({ icon: "personAdd", title: "NEW MEMBERS" }),
      createIconGridItem({ icon: "sparkle", title: "PERKS" }),
    ],
    ...overrides,
  },
});

export const createSixGridIconAndTitleBlock = (
  overrides: Partial<SixGridIconAndTitleSettings> = {}
): EmailBuilderBlockOf<"sixGridIconAndTitle"> => ({
  id: makeId("sixGridIconAndTitle"),
  type: "sixGridIconAndTitle",
  settings: {
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    title: "YOUR MEMBERSHIP BENEFITS",
    items: [
      createIconGridItem({ icon: "handshake", title: "Networking Opportunities" }),
      createIconGridItem({ icon: "briefcase", title: "Business Resources" }),
      createIconGridItem({ icon: "bank", title: "Advocacy & Representation" }),
      createIconGridItem({ icon: "phone", title: "Chamber Perks App™" }),
      createIconGridItem({ icon: "tag", title: "Affinity Programs" }),
      createIconGridItem({
        icon: "megaphone",
        title: "Promote Your Business",
        highlighted: true,
      }),
    ],
    ...overrides,
  },
});

export const createUpcomingEventsListBlock = (
  overrides: Partial<UpcomingEventsListSettings> = {}
): EmailBuilderBlockOf<"upcomingEventsList"> => ({
  id: makeId("upcomingEventsList"),
  type: "upcomingEventsList",
  settings: {
    title: "Upcoming Events",
    textColor: emailBuilderColors.darkNavy,
    imageUrl: "",
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    events: [],
    ...overrides,
  },
});

export const createEventAnnouncementBlock = (
  overrides: Partial<EventAnnouncementSettings> = {}
): EmailBuilderBlockOf<"eventAnnouncement"> => ({
  id: makeId("eventAnnouncement"),
  type: "eventAnnouncement",
  settings: {
    buttonName: "Register Now",
    title: "",
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    events: [],
    ...overrides,
  },
});

export const createSpecialGuestSponsorBlock = (
  overrides: Partial<SpecialGuestSponsorSettings> = {}
): EmailBuilderBlockOf<"specialGuestSponsor"> => ({
  id: makeId("specialGuestSponsor"),
  type: "specialGuestSponsor",
  settings: {
    textColor: emailBuilderColors.darkNavy,
    backgroundColor: emailBuilderColors.white,
    title: "SPECIAL GUEST SPONSOR",
    sponsorLogoUrl: "",
    textContent: "Thank you to our event sponsor for making this possible!",
    ...overrides,
  },
});

export const createSponsorsListBlock = (
  overrides: Partial<SponsorsListSettings> = {}
): EmailBuilderBlockOf<"sponsorsList"> => ({
  id: makeId("sponsorsList"),
  type: "sponsorsList",
  settings: {
    title: "OUR SPONSORS",
    textColor: emailBuilderColors.darkNavy,
    backgroundColor: emailBuilderColors.white,
    sponsors: [createSponsorItem()],
    ...overrides,
  },
});

export const createAdvertisingPdfBlock = (
  overrides: Partial<AdvertisingPdfSettings> = {}
): EmailBuilderBlockOf<"advertisingPdf"> => ({
  id: makeId("advertisingPdf"),
  type: "advertisingPdf",
  settings: {
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    title: "Best Mortgage Rates",
    imageUrl: "",
    buttonName: "View Full PDF",
    buttonTextColor: emailBuilderColors.white,
    addLink: "",
    attachPdfUrl: "",
    attachPdfName: "",
    ...overrides,
  },
});

export const createKeyLinksBlock = (
  overrides: Partial<KeyLinksSettings> = {}
): EmailBuilderBlockOf<"keyLinks"> => ({
  id: makeId("keyLinks"),
  type: "keyLinks",
  settings: {
    title: "Key Links",
    textColor: emailBuilderColors.darkNavy,
    highlightColor: emailBuilderColors.blue,
    backgroundColor: emailBuilderColors.white,
    items: [
      createKeyLinkItem({ buttonText: "Read the Full Brief" }),
      createKeyLinkItem({ buttonText: "View Proposed Changes" }),
      createKeyLinkItem({ buttonText: "Contact Your Representative" }),
    ],
    ...overrides,
  },
});

export const createInquiriesBlock = (
  overrides: Partial<InquiriesSettings> = {}
): EmailBuilderBlockOf<"inquiries"> => ({
  id: makeId("inquiries"),
  type: "inquiries",
  settings: {
    textColor: emailBuilderColors.darkNavy,
    backgroundColor: emailBuilderColors.white,
    title: "For Media Inquiries",
    textContent: "Karen Hattie, Executive Director",
    email: "info@sandbaychamber.ca",
    phoneNumber: "(705) 555-1234",
    ...overrides,
  },
});

/** Design (19) DEFAULTS.signature text. */
export const SIGNATURE_DEFAULT_TEXT = "Sincerely,<br>Contact Name<br>Title";

export const createSignatureBlock = (
  overrides: Partial<SignatureSettings> = {}
): EmailBuilderBlockOf<"signature"> => ({
  id: makeId("signature"),
  type: "signature",
  settings: {
    text: SIGNATURE_DEFAULT_TEXT,
    textColor: emailBuilderColors.darkNavy,
    backgroundColor: emailBuilderColors.white,
    ...overrides,
  },
});

const legacySignatureFieldKeys = ["opening", "addSignature", "name", "designation", "chamberName"] as const;

/**
 * Blocks saved before Oct 2026 have OPENING / NAME / TITLE LINE fields and no
 * text. The builder moves them into the one SIGNATURE field when it loads
 * the block, in the same lines the legacy renderer showed. Hidden legacy
 * fields stay out of the text. Blocks that already have text are unchanged.
 */
export const migrateLegacySignatureSettings = (
  settings: SignatureSettings & EmailBuilderPresentationSettings
): SignatureSettings & EmailBuilderPresentationSettings => {
  if (typeof settings.text === "string") return settings;
  const hidden = new Set(settings.hiddenFields || []);
  const visible = (key: (typeof legacySignatureFieldKeys)[number], fallback = ""): string =>
    hidden.has(key) ? "" : settings[key] ?? fallback;
  const opening = visible("opening", "Sincerely,");
  const addSignature = visible("addSignature");
  const name = visible("name");
  const role = [visible("designation").trim(), visible("chamberName").trim()].filter(Boolean).join(", ");
  const lines = [
    richTextToHtml(opening),
    richTextToHtml(addSignature || name),
    addSignature && name && addSignature !== name ? richTextToHtml(name) : "",
    escapeHtml(role),
  ].filter((line) => line.trim());
  const migrated = { ...settings, text: buildEmailRichTextValue(lines.join("<br>")) };
  legacySignatureFieldKeys.forEach((key) => delete migrated[key]);
  if (settings.hiddenFields)
    migrated.hiddenFields = settings.hiddenFields.filter((key) => !(legacySignatureFieldKeys as readonly string[]).includes(key));
  return migrated;
};

export const createSimpleEmailBlock = <T extends "heading" | "text" | "image" | "button" | "divider" | "spacer" | "socialLinks">(
  type: T
): EmailBuilderBlockOf<T> => ({
  id: makeId(type), type,
  settings: {
    text: type === "heading" ? "Add Your Heading" : type === "button" ? "Call To Action" : type === "text" ? "Write your message here. Click this block, then edit the text in the panel on the right." : "",
    imageUrl: "", imageWidth: 100, addLink: "", textAlign: type === "text" ? "left" : "center",
    textColor: type === "button" ? "#FFFFFF" : type === "divider" ? "#ECECEC" : "#0A0A0A", backgroundColor: "#FFFFFF", highlightColor: "#1878F2", height: 24,
  },
});

export const createNewsroomHighlightsBlock = (
  overrides: Partial<NewsroomHighlightsSettings> = {}
): EmailBuilderBlockOf<"newsroomHighlights"> => ({
  id: makeId("newsroomHighlights"), type: "newsroomHighlights",
  settings: { title: "From the Newsroom", highlightColor: emailBuilderColors.blue, items: [], ...overrides },
});

export const createNewMembersBlock = (
  overrides: Partial<NewMembersSettings> = {}
): EmailBuilderBlockOf<"newMembers"> => ({
  id: makeId("newMembers"), type: "newMembers",
  // D22 / design (16) DEFAULTS.newMembers: new blocks list members who
  // joined in the last 60 days. Saved blocks without `days` keep 30.
  settings: { title: "Welcome Our New Members", highlightColor: emailBuilderColors.blue, items: [], days: 60, ...overrides },
});

export const createAdvertisementBannerBlock = (
  overrides: Partial<AdvertisementBannerSettings> = {}
): EmailBuilderBlockOf<"advertisementBanner"> => ({
  id: makeId("advertisementBanner"), type: "advertisementBanner",
  settings: { imageUrl: "", linkUrl: "", ...overrides },
});

export const createGalleryItem = (overrides: Partial<GalleryItem> = {}): GalleryItem => ({
  id: makeId("galleryItem"), imageUrl: "", title: "Title", linkUrl: "", ...overrides,
});

export const createGalleryBlock = (
  overrides: Partial<GallerySettings> = {}
): EmailBuilderBlockOf<"gallery"> => ({
  id: makeId("gallery"), type: "gallery",
  settings: { title: "Gallery", items: [createGalleryItem(), createGalleryItem(), createGalleryItem()], ...overrides },
});

export const createFooterBlock = (
  overrides: Partial<FooterSettings> = {}
): EmailBuilderBlockOf<"footer"> => ({
  id: makeId("footer"), type: "footer",
  settings: { organization: "", communityName: "", ...overrides },
});

export const createColumnsBlock = (
  overrides: Partial<ColumnsSettings> = {}
): EmailBuilderBlockOf<"columns"> => ({
  id: makeId("columns"), type: "columns",
  settings: { left: "Left column text. Great for pairing a short update with a related note side by side.", right: "Right column text. On mobile these columns stack automatically.", ...overrides },
});

export const createImageTextBlock = (
  overrides: Partial<ImageTextSettings> = {}
): EmailBuilderBlockOf<"imageText"> => ({
  id: makeId("imageText"), type: "imageText",
  settings: { imageUrl: "", imageSize: 130, text: "Add your text here.", linkText: "", linkUrl: "", highlightColor: emailBuilderColors.blue, ...overrides },
});

export const createVideoBlock = (
  overrides: Partial<VideoSettings> = {}
): EmailBuilderBlockOf<"video"> => ({
  id: makeId("video"), type: "video",
  settings: { caption: "Watch: Chamber Year in Review", linkUrl: "", imageUrl: "", ...overrides },
});

export const createSubscribeBlock = (
  overrides: Partial<SubscribeSettings> = {}
): EmailBuilderBlockOf<"subscribe"> => ({
  id: makeId("subscribe"), type: "subscribe",
  settings: {
    title: "Did a friend or colleague forward you this newsletter?",
    text: "Subscribe to get the latest chamber news, events, and member perks delivered straight to your inbox.",
    buttonName: "Subscribe Now",
    linkUrl: "",
    highlightColor: "#10B981",
    ...overrides,
  },
});

/** "October 12, 2026" in the given IANA zone (the chamber time zone). */
export const formatEmailLogoDate = (timeZone?: string | null, now = Date.now()): string =>
  new Date(now).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", ...(timeZone ? { timeZone } : {}) });

export const createLogoDateBlock = (
  overrides: Partial<LogoDateSettings> = {}
): EmailBuilderBlockOf<"logoDate"> => ({
  id: makeId("logoDate"), type: "logoDate",
  settings: {
    logoUrl: "",
    // F1: the header logo default (+33%).
    logoWidth: 183,
    layout: "split",
    date: formatEmailLogoDate(),
    dateBackgroundColor: emailBuilderColors.black,
    ...overrides,
  },
});

export const createPerksBlock = (
  overrides: Partial<PerksSettings> = {}
): EmailBuilderBlockOf<"perks"> => ({
  id: makeId("perks"), type: "perks",
  settings: { title: "Featured Member Perk", highlightColor: emailBuilderColors.blue, items: [], seeded: false, ...overrides },
});

export const createPerkWeekBlock = (
  overrides: Partial<PerkWeekSettings> = {}
): EmailBuilderBlockOf<"perkWeek"> => ({
  id: makeId("perkWeek"), type: "perkWeek",
  settings: {
    label: "PERK OF THE WEEK",
    subtitle: "A member benefit you can share with your team",
    headline: "",
    linkUrl: "",
    highlightColor: "#E1A731",
    perk: null,
    ...overrides,
  },
});

const blockFactories: {
  [K in EmailBuilderBlockType]: () => EmailBuilderBlockOf<K>;
} = {
  textAndButtonHeroBanner: createTextAndButtonHeroBannerBlock,
  textHeroBanner: createTextHeroBannerBlock,
  emailBanner: createEmailBannerBlock,
  updateBanner: createUpdateBannerBlock,
  alertBanner: createAlertBannerBlock,
  advocacyUpdateBanner: createAdvocacyUpdateBannerBlock,
  memberSpotlight: createMemberSpotlightBlock,
  articleList: createArticleListBlock,
  splitArticleWithBorder: createSplitArticleWithBorderBlock,
  textAndEmailContent: createTextAndEmailContentBlock,
  fourGridIconAndTitle: createFourGridIconAndTitleBlock,
  sixGridIconAndTitle: createSixGridIconAndTitleBlock,
  upcomingEventsList: createUpcomingEventsListBlock,
  eventAnnouncement: createEventAnnouncementBlock,
  specialGuestSponsor: createSpecialGuestSponsorBlock,
  sponsorsList: createSponsorsListBlock,
  advertisingPdf: createAdvertisingPdfBlock,
  keyLinks: createKeyLinksBlock,
  inquiries: createInquiriesBlock,
  signature: createSignatureBlock,
  heading: () => createSimpleEmailBlock("heading"),
  text: () => createSimpleEmailBlock("text"),
  image: () => createSimpleEmailBlock("image"),
  button: () => createSimpleEmailBlock("button"),
  divider: () => createSimpleEmailBlock("divider"),
  spacer: () => createSimpleEmailBlock("spacer"),
  socialLinks: () => createSimpleEmailBlock("socialLinks"),
  newsroomHighlights: createNewsroomHighlightsBlock,
  newMembers: createNewMembersBlock,
  advertisementBanner: createAdvertisementBannerBlock,
  gallery: createGalleryBlock,
  footer: createFooterBlock,
  columns: createColumnsBlock,
  imageText: createImageTextBlock,
  video: createVideoBlock,
  subscribe: createSubscribeBlock,
  logoDate: createLogoDateBlock,
  perks: createPerksBlock,
  perkWeek: createPerkWeekBlock,
};

export const createEmailBuilderBlock = (
  type: EmailBuilderBlockType
): EmailBuilderBlock => blockFactories[type]();

/**
 * Defaults a block takes from the email when staff add it ("+ Add" or a
 * template): the theme highlight colour, the chamber date and the Step 2
 * Chamber Logo. Subscribe keeps its green button and Perk of the Week its
 * fixed gold banner (design (19) DEFAULTS.subscribe / perkWeek).
 */
export const applyNewEmailBuilderBlockDefaults = (
  block: EmailBuilderBlock,
  { highlightColor, logoUrl, date }: { highlightColor: string; logoUrl: string; date: string }
): EmailBuilderBlock => {
  switch (block.type) {
    case "logoDate":
      return { ...block, settings: { ...block.settings, date, logoUrl: block.settings.logoUrl || logoUrl } };
    case "alertBanner":
      return { ...block, settings: { ...block.settings, backgroundColor: highlightColor } };
    case "subscribe":
    case "perkWeek":
      return block;
    default:
      return "highlightColor" in block.settings
        ? ({ ...block, settings: { ...block.settings, highlightColor } } as EmailBuilderBlock)
        : block;
  }
};

// ---------------------------------------------------------------------------
// New-block palette (previews are real renders, see EmailBlockThumbnail)
// ---------------------------------------------------------------------------

export type EmailBuilderPaletteItem = {
  key: EmailBuilderBlockType;
  /** Design (16) PALETTE text shown above the thumbnail. */
  label: string;
};

// Design (16) PALETTE order and texts (D22, EC-21/EC-22). Upcoming Events
// stays offered (F9). The Advocacy banner is not offered (What's New);
// saved and template Advocacy blocks keep working. The footer is offered only
// when an email has no footer block (getEmailBuilderPaletteSections).
// Saved legacy types keep their factories, parsers, settings panels and
// output renderers.
export const emailBuilderPaletteItems: EmailBuilderPaletteItem[] = [
  { key: "textAndButtonHeroBanner", label: "Hero Banner with Title, Subtitle and Optional Image" },
  { key: "articleList", label: "Add Image and Article Details with Link" },
  { key: "text", label: "Add Text Block" },
  { key: "imageText", label: "Add Image and Text" },
  { key: "image", label: "Add Image Only" },
  { key: "columns", label: "Add Side By Side Text Column" },
  { key: "splitArticleWithBorder", label: "Add Side by Side Articles with Icons and Link" },
  { key: "fourGridIconAndTitle", label: "Newsletter Highlights" },
  { key: "gallery", label: "Image Gallery" },
  { key: "perks", label: "Auto Add 5 Most Recent Perks" },
  { key: "perkWeek", label: "Auto Select a Perk of the Week" },
  { key: "newsroomHighlights", label: "Auto Add Newsroom Articles" },
  { key: "newMembers", label: "Auto Feature New Members" },
  { key: "upcomingEventsList", label: "Upcoming Event Cards" },
  { key: "updateBanner", label: "Announcement with Button" },
  { key: "subscribe", label: "Subscribe to Newsletter" },
  { key: "alertBanner", label: "Urgent Alert Banner" },
  { key: "specialGuestSponsor", label: "Add Sponsor Image with Link" },
  { key: "advertisementBanner", label: "Sponsor/Advertisement Banner" },
  { key: "keyLinks", label: "List of Helpful Links" },
  { key: "socialLinks", label: "Social Media Icons" },
  { key: "signature", label: "Email Signature" },
  { key: "footer", label: "Email Footer" },
  { key: "logoDate", label: "Logo and Date" },
  { key: "heading", label: "Add a Heading Above any Block" },
  { key: "button", label: "Add a Button Below any Block" },
  { key: "divider", label: "Add a Divider Line Between any Block" },
  { key: "spacer", label: "Add Space Between any Block" },
  { key: "video", label: "Video with Thumbnail and Link" },
  { key: "eventAnnouncement", label: "Feature Event Block" },
];

export type EmailBuilderPaletteSection = {
  heading: string | null;
  items: EmailBuilderPaletteItem[];
};

// Design (16) palette: "Blocks in This Template" (the template's block types
// in template order) first, then every other block in PALETTE order. Search
// keeps a section only when one of its labels contains the text.
export const getEmailBuilderPaletteSections = ({
  templateBlockTypes,
  search = "",
  hasFooter = false,
}: {
  templateBlockTypes: readonly EmailBuilderBlockType[];
  search?: string;
  /** The footer is added to every email; offer it only when it is missing. */
  hasFooter?: boolean;
}): EmailBuilderPaletteSection[] => {
  const offered = emailBuilderPaletteItems.filter((item) => !(hasFooter && item.key === "footer"));
  const byKey = new Map(offered.map((item) => [item.key, item]));
  const inTemplate = Array.from(new Set(templateBlockTypes))
    .map((type) => byKey.get(type))
    .filter((item): item is EmailBuilderPaletteItem => !!item);
  const templateKeys = new Set(inTemplate.map((item) => item.key));
  const query = search.trim().toLowerCase();
  const matches = (item: EmailBuilderPaletteItem) => !query || item.label.toLowerCase().includes(query);
  return [
    { heading: "Blocks in This Template", items: inTemplate.filter(matches) },
    { heading: null, items: offered.filter((item) => !templateKeys.has(item.key) && matches(item)) },
  ].filter((section) => section.items.length > 0);
};

// Short visual tags from the current design. Accessible labels use the full
// canonical block name; old saved types keep that full name as their fallback.
export const emailBuilderBlockTagLabels: Partial<Record<EmailBuilderBlockType, string>> = {
  textAndButtonHeroBanner: "Hero", fourGridIconAndTitle: "Grid",
  upcomingEventsList: "Events", newsroomHighlights: "Newsroom", newMembers: "Members",
  advertisementBanner: "Ad", gallery: "Gallery", articleList: "Article",
  advocacyUpdateBanner: "Banner", splitArticleWithBorder: "Split", alertBanner: "Alert",
  updateBanner: "Update", keyLinks: "Links", specialGuestSponsor: "Sponsor", footer: "Footer",
  eventAnnouncement: "Event", signature: "Text", heading: "Heading", text: "Text",
  image: "Image", button: "Button", divider: "Divider", spacer: "Spacer", columns: "Columns",
  imageText: "Image + Text", video: "Video", socialLinks: "Social",
  subscribe: "Subscribe", logoDate: "Logo and Date", perks: "Perks", perkWeek: "Perk of the Week",
};

// ---------------------------------------------------------------------------
// Default state
// ---------------------------------------------------------------------------

export const buildDefaultEmailBuilderState = (
  chamberName?: string
): EmailBuilderState => {
  const name = chamberName?.trim() || "{INSERT CHAMBER NAME}";

  return {
    version: 2,
    logoUrl: "",
    newsletterLabel: "",
    greetingLine: "",
    // Design (16) What's New: the default footer notice for new emails.
    // Saved emails keep their own text.
    footerText: `You are receiving this email because you have subscribed to the ${name}.`,
    fontFamily: "DM Sans",
    theme: { ...defaultTheme },
    selectedBlockId: "",
    // Custom Email = empty canvas; templates seed blocks via emailTemplatePresets.
    blocks: [],
  };
};

export const cloneEmailBuilderState = (
  builderState: EmailBuilderState
): EmailBuilderState =>
  JSON.parse(JSON.stringify(builderState)) as EmailBuilderState;

const emailBuilderLinkFieldNames = new Set([
  "addLink",
  "linkTo",
  "linkUrl",
  "attachPdfUrl",
  "pdfUrl",
]);

const normalizeUpcomingEventBlockLinks = (
  builderState: EmailBuilderState
): EmailBuilderState => ({
  ...builderState,
  blocks: builderState.blocks.map((block) =>
    block.type === "upcomingEventsList"
      ? {
          ...block,
          settings: {
            ...block.settings,
            events: block.settings.events.map((event) => ({
              ...event,
              linkUrl: normalizeUpcomingEventLinkUrl(event.linkUrl),
            })),
          },
        }
      : block
  ),
});

/**
 * Sanitizes every persisted builder URL without mutating the current editor
 * state. The renderer also calls this so legacy drafts receive the same guard
 * and older Upcoming Events links open the event details page.
 */
export const serializeEmailBuilderState = (state: EmailBuilderState): string =>
  state.preservedDocument?.builderStateJson ?? JSON.stringify(sanitizeEmailBuilderStateLinks(state));

export const sanitizeEmailBuilderStateLinks = (
  builderState: EmailBuilderState
): EmailBuilderState => {
  if (builderState.preservedDocument) return cloneEmailBuilderState(builderState);
  const sanitizedState = JSON.parse(
    JSON.stringify(builderState, (key, value: unknown) =>
      emailBuilderLinkFieldNames.has(key) && typeof value === "string"
        ? normalizeEmailLinkUrl(value)
        : value
    )
  ) as EmailBuilderState;

  return normalizeUpcomingEventBlockLinks(sanitizedState);
};

// ---------------------------------------------------------------------------
// HTML rendering (conservative table/inline-style email HTML)
// ---------------------------------------------------------------------------

export type EmailBuilderRenderTheme = {
  textColor: string;
  highlightColor: string;
  backgroundColor: string;
  footerBackgroundColor: string;
  fontFamily: string;
};

const getRenderTheme = (state: EmailBuilderState): EmailBuilderRenderTheme => ({
  ...state.theme,
  fontFamily: state.fontFamily || "DM Sans",
});

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

type EmailFooterSocialDefinition = {
  label: string;
  iconDataUrl: string;
  origin: string;
  recognizedHostnames: string[];
  buildLegacyPath: (value: string) => string;
};

const removeLeadingAt = (value: string): string => value.replace(/^@+/, "");
const ensureLeadingAt = (value: string): string =>
  `@${removeLeadingAt(value)}`;

// These small PNGs are rendered from the same Lucide and React Icons glyphs
// as the CMP website footer. The backend converts the marked data images to
// inline CID attachments before SES receives the message.
const emailFooterSocialIconDataUrls: Record<
  EmailFooterSocialPlatform,
  string
> = {
  facebook:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAMAAAC6V+0/AAAAllBMVEVHcEz///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////+ysAdDAAAAMXRSTlMAKoAid/sXGojU/MxZxLUhbAGNu/UTUcaR/UNHV4ZNDAcz9sE2Ww8NLzzg8cPmuqvrpJIgGwAAAAlwSFlzAAALEwAACxMBAJqcGAAAAJFJREFUeNqtz9cOwzAIBVAScOLUSTO7996D//+5OlIlh6ZSX8oL4kiIC4CrAyIuQNTx3mHmjbBTpR5aP6OmLc+XrW2RwDkT7K5mLZB4ta9uxsya6LPnsS+uTKmssSQaO7RhVBgqG2ngkLsYAAQ44WEbiftf1kc9h7FO6kOJjuFHpD9gUbQws99w9oF5ipjm7+EFxKwNVuFHiX4AAAAASUVORK5CYII=",
  instagram:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAMAAAC6V+0/AAAA1VBMVEVHcEz///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////882+PKAAAARnRSTlMAIoAqAYh3HwWJzQwNz/DvIZD+iwSKjws5kszxzo3moR6Cy0MgSA5/fUUQ2vr9yMpYWQJLAxLz9C3s3dBVVOHgLBel652MKLu1uAAAAAlwSFlzAAALEwAACxMBAJqcGAAAANdJREFUeNpVkdd2wjAQRMe2ErkbbNNrIKTQ0ntv8/+fhBULLO7LSldnRw0ocKWlkS5Kwk6PO+Jm8O8idrMDTebTV7bDM1QIm80ir9et1PVlKPqxixq9Ukgp8MgrNFiDQ1upm2fy/RXOLWw6Wp6nP5k35FsxrORLIoGv4Ycw5B1/VcYfLUPO05GSdUqz/eL+E7CeFnuZ7eXD9yhZzfYk2uuU0RRa7g4fnpS1wWO4sS9gXj5KcrXlqWHFEQdFCXz2vUONF3HSUotBPa4eORm0dE8+3n7HOFfzDedOHMTeJUIiAAAAAElFTkSuQmCC",
  threads:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAMAAAC6V+0/AAABcVBMVEVHcEz///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////8aHqrfAAAAenRSTlMA/vxQ7/mLIPj7NxMDnd8wUxD6b6e690Ai/ccdEaZru1YJORL1TlWroqWoVAHGqoMGBVLejCfCbtrbQdQxFT8hOgfg6hojz/a+GLwXqeVpaJ4oWUN0Vz51UfTjly16168vZOTQT1yt8Mu11jznwRsMYFg0akWytuzcsT1zydwAAAAJcEhZcwAACxMAAAsTAQCanBgAAAEMSURBVHjaY2CAAEUl5tAwZQZk4CqiUgUETB52CDEBMUb5EAVmOR/3KhMdqJiVEB8XhCUtWqUPYRlYsrDD9DiacfKDGdpVwgwMgfHprBnMDAymTBpgQS0OQQZvFs7cMhZGNQYGPU2wIJ8ukEjJZmDIy8mCGcPDyAui8sUL1EoZZaCC3FVsQLKwHOTQKnUXJMEY1oriEtXKqipfe4T22KoiIFOdI5VJFKLUWIqBwa8qkYGBizWTIUDSFiwoAnSSTFRVshSnSgKDXJUNWNAI5Hjp6LikNKBn2KogvjO0QHjT09kayhIXcoIGiIQbIzNy0IUrqPrLsjKZIwJUQjYS5HRJLweUsFdUiggKhkUHADo4LI930r4dAAAAAElFTkSuQmCC",
  linkedin:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAMAAAC6V+0/AAAA2FBMVEVHcEz///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////8HlUfZAAAAR3RSTlMA1CqAVeIcBOfMJqohQvoK+3r9cVMMd7HRosYGASSGuDdhNslde7nkFwIFLyLbWR/5aciwihUDHXwpo9maiJLxllLQocXylSl8Wi0AAAAJcEhZcwAACxMAAAsTAQCanBgAAADASURBVHjapc/XEoJADAXQAEsTQRAVVOy9995r/v+P3FXQGfTNvNzkzOxOAgBZCb7qdEA3zNmbeIynQyjhXVYyIfTOcQWt8J/S5WrJr3bWjfmoE0IiNFujDaJZLzKLIi0VoD1BZbtzDLNEkUeNiBzAFNPs7apnlhkKwHHQHI7ni3VmKfNG4Y1V7Hsu3aMGjhEL0MLOAPdyowJ5zAUoIP/p/sfnngLaNkORaBT9i1IsUqCyiAa3JxOEJJIQoYMOP+sBmeUbHcjbCyIAAAAASUVORK5CYII=",
  twitter:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAMAAAC6V+0/AAABNVBMVEVHcEz///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////9iNh6NAAAAZnRSTlMAIv4E/BUC/RMfIejbtAWJ9X0IwPgawqntFxRBS+L5N+zRbDgHTB2UaHRXP46MKe4gusZOkOXkW0SFvLiH6azIDHo75gF5TV7WESM1CWt43u+2mqb7uR7EZwNuGyxHJw5/vury2oNxOrv1AAAACXBIWXMAAAsTAAALEwEAmpwYAAAA9UlEQVR42mNgwAo4GYFAFMZTY+QEUY5pQBDiBBHTYk9TAtEuvGmhfhKyUiC2tkgaP0TW0z/AS4YpkI2BQU8/TZwNao63iKIPf5orAytPmpgQ3C7ftCBmMRY3+zQeViQXBKe5RycxpXFzITvLwUMgJpadSR7VsXHJKfGpaQloXohIE49MZLdAERMKT0uLknS2FUUSY7NLU1CUk5dmEWZGCOqkyUmHKchKCaZZwsU00pjMGRhkmKz4rJmMoWKqLGmaINomTZDVTMIALKYskKYOZjALs5gYphnxAZmM3Gm8UOMlTUHBqAtkcTAyqsAM5wAFOBf2yAAA5lYi3aailJsAAAAASUVORK5CYII=",
  tiktok:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAMAAAC6V+0/AAAA21BMVEVHcEz///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////9C+k+xAAAASHRSTlMAfPP+vzj6egLdA/wuMg0F36TTlLE8R5byqFFqC36vl/bwxKn5ZGVpARMme5HW8eeusqCI+4PsFjr067rZ0iBsNAdbZhedraZpUsoRAAAACXBIWXMAAAsTAAALEwEAmpwYAAAApklEQVR42m3Q1RLCMBAF0EChUNzd3d3duf//RWwDqdH7spuTmWR2GdPiAPw+Zgkh3HaYsEPMvTaI4/nyj4DLgouVjuFUupRT0dldKj8MxOjew5FO65NqmwqMyHNViKq1lglvZPXQ9yMNn8CuwCz4Ava8KQIZgW/g0FGbLBAReKc381STMhAV+NgC0rQ/a1AJauO1IRI3DN2UOUll09p6g+FoPNG3/gFVHSMECUhBGAAAAABJRU5ErkJggg==",
  youtube:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAMAAAC6V+0/AAAArlBMVEVHcEz///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////+weO9WAAAAOXRSTlMAqoBTkhAzxAMa/KBZG1rRHf3+kZtyJbLonXMN9rQ79QxCAXcvOofQjAkqht2oHNLcjwd/zygngYIwOT3GAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAqElEQVR42qWQRxKDMAxFbXrvhNDTgCQkpBfd/2KxsTeZYcff6PtpJEtCaK40RcykFJclTqVMVDTKPBdG1TWL3UCg6fdWo+c0n+uN0wcmcSeXvlfrDe92uSJ08M/UbkHdMYiNO/JgT60AYEgydTfQ0REeDFoqLBfEfeBFoPAPv/CeLp/8aHIkZAatU9ljpWxXTjsOP3RsvaJg8Wnzg8RhhJMER2HMDzJLP7FqFYPzmbjYAAAAAElFTkSuQmCC",
};

const emailFooterSocialDefinitions: Record<
  EmailFooterSocialPlatform,
  EmailFooterSocialDefinition
> = {
  facebook: {
    label: "Facebook",
    iconDataUrl: emailFooterSocialIconDataUrls.facebook,
    origin: "https://www.facebook.com",
    recognizedHostnames: [
      "facebook.com",
      "www.facebook.com",
      "m.facebook.com",
      "fb.com",
      "www.fb.com",
    ],
    buildLegacyPath: (value) => value,
  },
  instagram: {
    label: "Instagram",
    iconDataUrl: emailFooterSocialIconDataUrls.instagram,
    origin: "https://www.instagram.com",
    recognizedHostnames: ["instagram.com", "www.instagram.com"],
    buildLegacyPath: removeLeadingAt,
  },
  threads: {
    label: "Threads",
    iconDataUrl: emailFooterSocialIconDataUrls.threads,
    origin: "https://www.threads.net",
    recognizedHostnames: ["threads.net", "www.threads.net"],
    buildLegacyPath: ensureLeadingAt,
  },
  linkedin: {
    label: "LinkedIn",
    iconDataUrl: emailFooterSocialIconDataUrls.linkedin,
    origin: "https://www.linkedin.com",
    recognizedHostnames: ["linkedin.com", "www.linkedin.com"],
    buildLegacyPath: (value) =>
      /^(?:company|in|school|showcase|groups)\//i.test(value)
        ? value
        : `company/${value}`,
  },
  twitter: {
    label: "X",
    iconDataUrl: emailFooterSocialIconDataUrls.twitter,
    origin: "https://x.com",
    recognizedHostnames: [
      "x.com",
      "www.x.com",
      "twitter.com",
      "www.twitter.com",
    ],
    buildLegacyPath: removeLeadingAt,
  },
  tiktok: {
    label: "TikTok",
    iconDataUrl: emailFooterSocialIconDataUrls.tiktok,
    origin: "https://www.tiktok.com",
    recognizedHostnames: ["tiktok.com", "www.tiktok.com"],
    buildLegacyPath: ensureLeadingAt,
  },
  youtube: {
    label: "YouTube",
    iconDataUrl: emailFooterSocialIconDataUrls.youtube,
    origin: "https://www.youtube.com",
    recognizedHostnames: [
      "youtube.com",
      "www.youtube.com",
      "youtu.be",
      "www.youtu.be",
    ],
    buildLegacyPath: (value) =>
      /^(?:@|c\/|channel\/|user\/)/i.test(value)
        ? value
        : ensureLeadingAt(value),
  },
};

const emailFooterSocialPlatformOrder: EmailFooterSocialPlatform[] = [
  "facebook",
  "instagram",
  "threads",
  "linkedin",
  "twitter",
  "tiktok",
  "youtube",
];

const toAbsoluteHttpsUrl = (value: string): string | null => {
  const candidate = value.startsWith("//")
    ? `https:${value}`
    : /^https?:\/\//i.test(value)
      ? value
      : `https://${value}`;

  try {
    const url = new URL(candidate);
    if (!["http:", "https:"].includes(url.protocol)) return null;
    if (!url.hostname || url.username || url.password) return null;
    url.protocol = "https:";
    return url.toString();
  } catch {
    return null;
  }
};

export const normalizeEmailFooterSocialUrl = ({
  platform,
  value,
}: {
  platform: EmailFooterSocialPlatform;
  value?: string | null;
}): string | null => {
  const trimmedValue = value?.trim();
  if (!trimmedValue) return null;

  if (/^https?:\/\//i.test(trimmedValue) || trimmedValue.startsWith("//")) {
    return toAbsoluteHttpsUrl(trimmedValue);
  }

  // Reject non-web schemes before treating older username-only values as
  // platform paths. This prevents values such as javascript: from becoming
  // misleading social anchors.
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmedValue)) return null;

  const definition = emailFooterSocialDefinitions[platform];
  const valueWithoutLeadingSlash = trimmedValue.replace(/^\/+/, "");
  const possibleHostname = valueWithoutLeadingSlash
    .split(/[/?#]/, 1)[0]
    .split(":", 1)[0]
    .toLowerCase();
  const isRecognizedSchemeLessUrl =
    definition.recognizedHostnames.includes(possibleHostname) ||
    valueWithoutLeadingSlash.toLowerCase().startsWith("www.") ||
    // Manage Chamber Profile accepts full URLs without a scheme. Preserve any
    // such domain/path rather than incorrectly treating it as a legacy handle.
    /^[^/?#]+\.[a-z]{2,}(?:[/?#]|$)/i.test(valueWithoutLeadingSlash);

  if (isRecognizedSchemeLessUrl) {
    return toAbsoluteHttpsUrl(valueWithoutLeadingSlash);
  }

  const legacyPath = definition
    .buildLegacyPath(valueWithoutLeadingSlash)
    .replace(/^\/+/, "");
  if (!legacyPath) return null;

  return toAbsoluteHttpsUrl(`${definition.origin}/${legacyPath}`);
};

export const getEmailFooterSocialLinks = (
  profile?: EmailFooterProfile | null
): EmailFooterSocialLink[] =>
  emailFooterSocialPlatformOrder.flatMap((platform) => {
    const definition = emailFooterSocialDefinitions[platform];
    const url = normalizeEmailFooterSocialUrl({
      platform,
      value: profile?.[platform],
    });
    if (!url) return [];

    return [
      {
        platform,
        label: definition.label,
        url,
        iconDataUrl: definition.iconDataUrl,
      },
    ];
  });

// One whole-field alignment wrapper extends the existing B/U/I storage format.
// Only fixed alignment values are parsed. Arbitrary attributes and HTML stay escaped.
export type RichTextMarks = {
  bold: boolean; underline: boolean; italic: boolean;
  textAlign?: "left" | "center" | "right" | "justify";
};

const richTextWrappers: Array<{
  key: "bold" | "underline" | "italic";
  open: string;
  close: string;
}> = [
  { key: "bold", open: "<b>", close: "</b>" },
  { key: "underline", open: "<u>", close: "</u>" },
  { key: "italic", open: "<i>", close: "</i>" },
];

export const getRichTextParts = (
  value: string
): { text: string; marks: RichTextMarks } => {
  let text = value;
  const marks: RichTextMarks = { bold: false, underline: false, italic: false };
  const aligned = value.match(/^<span style="display:block;text-align:(left|center|right|justify)">([\s\S]*)<\/span>$/);
  if (aligned) {
    text = aligned[2];
    const align = aligned[1];
    if (align === "left" || align === "center" || align === "right" || align === "justify") marks.textAlign = align;
  }
  let unwrapped = true;
  while (unwrapped) {
    unwrapped = false;
    for (const wrapper of richTextWrappers) {
      if (
        text.startsWith(wrapper.open) &&
        text.endsWith(wrapper.close) &&
        text.length >= wrapper.open.length + wrapper.close.length
      ) {
        text = text.slice(wrapper.open.length, text.length - wrapper.close.length);
        marks[wrapper.key] = true;
        unwrapped = true;
      }
    }
  }
  return { text, marks };
};

export const buildRichTextValue = (text: string, marks: RichTextMarks): string => {
  let value = text;
  if (marks.italic) value = `<i>${value}</i>`;
  if (marks.underline) value = `<u>${value}</u>`;
  if (marks.bold) value = `<b>${value}</b>`;
  return marks.textAlign
    ? `<span style="display:block;text-align:${marks.textAlign}">${value}</span>`
    : value;
};

// Rich-text fields tolerate the simple B/U/I markup produced by the settings
// panels; everything else is escaped.
export const richTextToHtml = (value: string): string => {
  if (isEmailRichText(value)) return emailRichTextHtml(value);
  const { text, marks } = getRichTextParts(value);
  const safeValue = marks.textAlign ? buildRichTextValue(text, { ...marks, textAlign: undefined }) : value;
  const html = escapeHtml(safeValue)
    .replace(/&lt;(\/?)(b|strong|i|em|u|br)\s*\/?&gt;/gi, "<$1$2>")
    .replace(/\n/g, "<br />");
  return marks.textAlign ? `<span style="display:block;text-align:${marks.textAlign}">${html}</span>` : html;
};

const stripSimpleMarkup = (value: string): string =>
  isEmailRichText(value) ? emailRichTextToPlainText(value) : value.replace(/<span style="display:block;text-align:(?:left|center|right|justify)">([\s\S]*?)<\/span>/g, "$1")
    .replace(/<\/?(b|strong|i|em|u|br)\s*\/?>/gi, "");

const fontStack = (fontFamily: string) =>
  `'${fontFamily}', Arial, Helvetica, sans-serif`;

const sectionShell = ({
  inner,
  background,
  padding = "32px 36px",
  align = "center",
  cellAttributes = "",
}: {
  inner: string;
  background: string;
  padding?: string;
  align?: string;
  cellAttributes?: string;
}): string =>
  `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:separate;border-spacing:0;"><tr><td${cellAttributes} style="box-sizing:border-box;border-radius:7px;background:${background};padding:${padding};text-align:${align};">${inner}</td></tr></table>`;

// Media queries cover delivered email; container queries cover the builder's
// narrow preview inside a desktop page. Clients without either keep table layout.
const emailResponsiveStyles = (css: string): string =>
  `<style>@media only screen and (max-width:520px){${css}}@container (max-width:520px){${css}}</style>`;

// Outlook drops calc() widths, flexbox and object-fit, so side-by-side block
// content uses table cells. Narrow screens stack the cells.
const emailColumnsMobileCss = `
.cmp-email-columns,.cmp-email-columns>tbody,.cmp-email-columns>tbody>tr{display:block!important;width:100%!important;}
.cmp-email-columns>tbody>tr>td.cmp-email-column{display:block!important;width:100%!important;max-width:100%!important;box-sizing:border-box!important;padding-left:0!important;padding-right:0!important;border-left:0!important;}
.cmp-email-columns>tbody>tr>td.cmp-email-column+td.cmp-email-column{padding-top:12px!important;}
`;
const emailColumnsResponsiveStyles = emailResponsiveStyles(emailColumnsMobileCss);
// Logo and Date (design (20) ldDir/ldJustify): side by side in the Desktop
// view, stacked and centred only in the Mobile view. Delivered email stacks on
// phones (520px). In the builder only the Mobile preview (390px canvas)
// stacks: the Desktop canvas is about 460px wide on 1366-1440 screens
// (Monday 13300334545, EM-3B-44), so the shared 520px container rule is not
// used here.
const logoDateMobileCss =
  ".cmp-email-logo-date,.cmp-email-logo-date>tbody,.cmp-email-logo-date>tbody>tr{display:block!important;width:100%!important;}" +
  ".cmp-email-logo-date>tbody>tr>td{display:block!important;width:100%!important;box-sizing:border-box!important;padding-left:0!important;text-align:center!important;}" +
  ".cmp-email-logo-date>tbody>tr>td+td{padding-top:10px!important;}" +
  ".cmp-email-logo-date-single{text-align:center!important;}";
const logoDateResponsiveStyles = `<style>@media only screen and (max-width:520px){${logoDateMobileCss}}@container (max-width:400px){${logoDateMobileCss}}</style>`;

type EmailColumn = {
  html: string;
  /** Pixels as a number, or a percentage such as "50%". */
  width?: number | string;
  style?: string;
};

// Include emailColumnsResponsiveStyles once in each block that uses these.
const emailColumnRows = (rows: EmailColumn[][], tableStyle = ""): string =>
  `<table class="cmp-email-columns" role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;table-layout:fixed;width:100%;${tableStyle}"><tbody>${rows.map((row) => `<tr>${row.map(({ html, width, style = "" }) => {
    const cssWidth = typeof width === "number" ? `${width}px` : width;
    return `<td class="cmp-email-column"${width === undefined ? "" : ` width="${width}"`} style="${cssWidth ? `width:${cssWidth};` : ""}vertical-align:top;box-sizing:border-box;${style}">${html}</td>`;
  }).join("")}</tr>`).join("")}</tbody></table>`;

const emailColumns = (columns: EmailColumn[], tableStyle = ""): string =>
  emailColumnRows([columns], tableStyle);

// Image block: widest image (percent) that still leaves room for text beside it.
const imageBesideTextMaxWidth = 65;

/**
 * New Members rows, two members per row. The backend send refresh
 * (cap-backend cmpNewsletterNewMembersRefresh.ts) renders the same markup;
 * change both together.
 */
export const renderNewMembersRows = (items: NewMemberItem[], highlightColor: string): string => {
  const cells = [...items].sort((a, b) => a.name.localeCompare(b.name)).map((item) => {
    const initials = item.name.split(/\s+/).map((word) => word[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
    const logo = item.logoUrl
      ? `<img src="${escapeHtml(item.logoUrl)}" alt="${escapeHtml(item.name)} logo" style="display:inline-block;max-width:76px;max-height:48px;width:auto;height:auto;object-fit:contain;vertical-align:middle;" />`
      : `<span style="display:inline-block;width:40px;height:40px;line-height:40px;border-radius:50%;background:${highlightColor};color:#FFFFFF;font-size:14px;font-weight:700;text-align:center;">${escapeHtml(initials)}</span>`;
    return `<table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:collapse;"><tbody><tr><td width="80" style="width:80px;text-align:center;vertical-align:middle;font-size:0;line-height:0;">${logo}</td><td style="padding-left:14px;vertical-align:middle;font-size:15px;font-weight:700;text-align:left;">${linkedMarkup(escapeHtml(item.name), item.linkUrl, "color:#0A0A0A;")}</td></tr></tbody></table>`;
  });
  if (!cells.length) return "";
  const rows = Array.from({ length: Math.ceil(cells.length / 2) }, (_, index) => [
    { html: cells[index * 2], width: "50%", style: "padding:6px 14px;vertical-align:middle;" },
    {
      html: cells[index * 2 + 1] || "",
      width: "50%",
      style: `padding:6px 14px;vertical-align:middle;${cells[index * 2 + 1] ? "border-left:1px solid #ECECEC;" : ""}`,
    },
  ]);
  return emailColumnRows(rows);
};

/**
 * Design (19) New Members cards: three per row (two per row on narrow
 * screens), a 120 px logo area (or a 96 px initials circle) and the name. No
 * categories and no joined date. A short last row is centred (Monday
 * 13300334545, Moeen; not in design): desktop 4 = 3 + 1, 5 = 3 + 2; narrow
 * 4 = 2 + 2, 5 = 2 + 2 + 1. The centred last row is its own centred table
 * inside the cards table, so Outlook keeps it. Used by blocks with the
 * 60-day window; saved 30-day blocks keep renderNewMembersRows. The backend
 * send refresh (cap-backend cmpNewsletterNewMembersRefresh.ts) renders the
 * same markup and recognises the cards by NEW_MEMBERS_CARDS_TABLE_OPEN;
 * change both together.
 */
export const NEW_MEMBERS_CARDS_TABLE_OPEN = '<table class="cmp-email-columns cmp-email-member-cards" role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;table-layout:fixed;width:100%;"><tbody>';

// Narrow screens: every row (and the last-row table) becomes inline, so the
// cards flow two per line inside the centred cards table; an odd last card
// is centred. The style sits inside the refreshed markup so emails saved
// before Oct 2026 get it at send time too.
const newMembersCardsNarrowCss = ".cmp-email-columns.cmp-email-member-cards,.cmp-email-columns.cmp-email-member-cards>tbody{display:block!important;width:100%!important;text-align:center!important;}"
  + ".cmp-email-columns.cmp-email-member-cards>tbody>tr,.cmp-email-member-cards td.cmp-email-member-cards-last,.cmp-email-member-cards table.cmp-email-member-cards-last-row,.cmp-email-member-cards table.cmp-email-member-cards-last-row>tbody,.cmp-email-member-cards table.cmp-email-member-cards-last-row>tbody>tr{display:inline!important;vertical-align:top!important;}"
  + ".cmp-email-columns.cmp-email-member-cards td.cmp-email-column.cmp-email-member-card{display:inline-block!important;width:50%!important;max-width:50%!important;box-sizing:border-box!important;padding:10px 8px!important;vertical-align:top!important;}";
export const NEW_MEMBERS_CARDS_NARROW_STYLE = `<style>@media only screen and (max-width:520px){${newMembersCardsNarrowCss}}@container (max-width:520px){${newMembersCardsNarrowCss}}</style>`;

export const renderNewMembersCards = (items: NewMemberItem[], highlightColor: string): string => {
  const cells = [...items].sort((a, b) => a.name.localeCompare(b.name)).map((item) => {
    const initials = item.name.split(/\s+/).map((word) => word[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
    const logo = item.logoUrl
      ? `<img src="${escapeHtml(item.logoUrl)}" alt="${escapeHtml(item.name)} logo" style="display:inline-block;max-width:100%;max-height:116px;width:auto;height:auto;object-fit:contain;vertical-align:middle;" />`
      : `<span style="display:inline-block;width:96px;height:96px;line-height:96px;border-radius:50%;background:${highlightColor};color:#FFFFFF;font-size:24px;font-weight:700;text-align:center;vertical-align:middle;">${escapeHtml(initials)}</span>`;
    return `<div style="height:120px;line-height:120px;font-size:0;text-align:center;">${logo}</div><div style="margin-top:8px;font-size:14px;font-weight:700;line-height:1.3;text-align:center;overflow-wrap:anywhere;">${linkedMarkup(escapeHtml(item.name), item.linkUrl, "color:#0A0A0A;")}</div>`;
  });
  if (!cells.length) return "";
  const cell = (html: string, width: string) => `<td class="cmp-email-column cmp-email-member-card" width="${width}" style="width:${width};vertical-align:top;box-sizing:border-box;padding:10px 8px;">${html}</td>`;
  const fullRowCount = Math.floor(cells.length / 3);
  const fullRows = Array.from({ length: fullRowCount }, (_, index) =>
    `<tr>${cells.slice(index * 3, index * 3 + 3).map((html) => cell(html, "33.333%")).join("")}</tr>`).join("");
  const rest = cells.slice(fullRowCount * 3);
  const lastWidth = rest.length === 2 ? "66.667%" : "33.333%";
  const lastRow = rest.length
    ? `<tr><td class="cmp-email-member-cards-last" colspan="3" align="center" style="padding:0;text-align:center;"><table class="cmp-email-member-cards-last-row" role="presentation" align="center" width="${lastWidth}" cellpadding="0" cellspacing="0" style="border-collapse:collapse;table-layout:fixed;width:${lastWidth};margin:0 auto;"><tbody><tr>${rest.map((html) => cell(html, rest.length === 2 ? "50%" : "100%")).join("")}</tr></tbody></table></td></tr>`
    : "";
  return `${NEW_MEMBERS_CARDS_TABLE_OPEN}${fullRows}${lastRow}</tbody></table>${NEW_MEMBERS_CARDS_NARROW_STYLE}`;
};

// Two cells that never stack, such as an icon beside its label.
const inlineIconRow = (icon: string, content: string, gap: number, cellStyle = ""): string =>
  `<table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:collapse;"><tbody><tr><td style="vertical-align:middle;line-height:0;padding:0 ${gap}px 0 0;">${icon}</td><td style="vertical-align:middle;${cellStyle}">${content}</td></tr></tbody></table>`;

export const getEmailBuilderButtonUrl = (
  settings: EmailBuilderPresentationSettings & { addLink?: string; linkUrl?: string }
): string => normalizeEmailLinkUrl(settings.pdfUrl || settings.addLink || settings.linkUrl || "");

const linkedMarkup = (content: string, url: string, style = "", label = ""): string => {
  const safeUrl = normalizeEmailLinkUrl(url);
  return safeUrl
    ? `<a href="${escapeHtml(safeUrl)}"${label ? ` aria-label="${escapeHtml(label)}"` : ""} style="text-decoration:none;${style}">${content}</a>`
    : `<span style="${style}">${content}</span>`;
};

const ctaButton = ({
  label, url, background, color, align = "center", margin = "0",
  padding = "9px 18px", fontSize = 12, fontWeight = 700, radius = 6,
}: {
  label: string; url: string; background: string; color: string;
  align?: "left" | "center" | "right"; margin?: string; padding?: string;
  fontSize?: number; fontWeight?: number; radius?: number;
}): string => {
  if (!stripSimpleMarkup(label).trim()) return "";
  const button = linkedMarkup(richTextToHtml(label), url,
    `display:inline-block;background:${background};color:${color};padding:${padding};border-radius:${radius}px;font-size:${fontSize}px;font-weight:${fontWeight};line-height:normal;white-space:nowrap;`);
  return `<div style="text-align:${align};margin:${margin};">${button}</div>`;
};

const iconTile = ({
  icon, color, background = "transparent", size = 46, rounded = 12, border = true,
}: {
  icon: EmailBuilderIconKey; color: string; background?: string;
  size?: number; rounded?: number; border?: boolean;
}): string => `<span style="display:inline-block;box-sizing:border-box;width:${size}px;height:${size}px;line-height:${size - 3}px;text-align:center;background:${background};border:${border ? `1.5px solid ${color}` : "none"};border-radius:${rounded}px;">${renderEmailBuilderIcon(icon, color, 20)}</span>`;

const emptyImage = (
  width: string, height: string, background = "#FAFAFB", label = "", iconSize = 24,
  { radius = 8, labelGap = 6, padding = "" }: { radius?: number; labelGap?: number; padding?: string } = {}
): string => {
  const fluid = height === "auto" && !padding;
  return `<div style="box-sizing:border-box;width:${width};max-width:100%;height:${height};${fluid ? "aspect-ratio:4/3;" : ""}${padding ? `padding:${padding};` : ""}border:${background === "#FAFAFB" ? "1.5px dashed #D8D8DC" : "none"};border-radius:${radius}px;background:${background};text-align:center;display:table;${fluid ? "display:flex;align-items:center;justify-content:center;" : ""}"><span style="display:table-cell;vertical-align:middle;${padding || fluid ? "line-height:0;" : ""}">${renderEmailBuilderIcon("image", background === "#FAFAFB" ? "#9E9E9E" : "#7E93AC", iconSize)}${label ? `<span style="display:block;margin-top:${labelGap}px;font-size:${iconSize === 20 ? 11 : iconSize === 30 ? 13 : 12}px;line-height:normal;color:#727272;">${escapeHtml(label)}</span>` : ""}</span></div>`;
};

const sectionHeading = (title: string, color: string, font: string): string => title.trim()
  ? `<div style="margin:0 0 14px;font-family:${font};font-size:19px;font-weight:700;line-height:22px;text-align:left;"><span style="display:inline-block;width:4px;height:22px;background:${color};border-radius:2px;vertical-align:middle;margin-right:10px;"></span>${richTextToHtml(title)}</div>`
  : "";

const imageOrPlaceholder = ({
  imageUrl,
  alt,
  placeholderLabel,
  maxWidth = "100%",
  height,
  layoutWidth,
}: {
  imageUrl: string;
  alt: string;
  placeholderLabel: string;
  maxWidth?: string;
  height?: number;
  layoutWidth?: number;
}): string => {
  const maxWidthPixels = maxWidth.match(/^(\d+)px$/)?.[1];
  const emailLayoutWidth =
    layoutWidth || (maxWidthPixels ? Number(maxWidthPixels) : 600);

  return imageUrl
    ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(
        alt
      )}" width="${emailLayoutWidth}" style="display:block;${
        height
          ? `width:auto;max-width:${maxWidth};height:auto;max-height:${height}px;object-fit:contain;`
          : `width:100%;max-width:${maxWidth};height:auto;object-fit:contain;`
      }margin:0 auto;border-radius:8px;" />`
    : `<div style="display:inline-block;width:100%;max-width:${maxWidth};${
        height ? `height:${height}px;line-height:${height}px;` : "padding:26px 0;"
      }border:1px dashed #cbd5e1;border-radius:8px;color:#94a3b8;font-size:13px;text-align:center;">${escapeHtml(
        placeholderLabel
      )}</div>`;
};

const WEEKDAY_TOKENS = [
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const getEventDateParts = (
  date: string
): { month: string; day: string; weekdayPrefix: string } => {
  const trimmed = date.trim();
  const startsWithWeekday = WEEKDAY_TOKENS.includes(
    trimmed.split(/[\s,]+/)[0]?.toLowerCase() || ""
  );
  const parsed = new Date(trimmed);

  if (!Number.isNaN(parsed.getTime())) {
    return {
      month: parsed
        .toLocaleString("en-US", { month: "short" })
        .toUpperCase(),
      day: String(parsed.getDate()),
      weekdayPrefix: startsWithWeekday
        ? ""
        : `${parsed.toLocaleString("en-US", { weekday: "short" })}, `,
    };
  }

  const tokens = trimmed.split(/[\s,]+/).filter(Boolean);

  return {
    month: (tokens[0] || "").slice(0, 3).toUpperCase(),
    day: tokens[1] || "",
    weekdayPrefix: "",
  };
};

export const renderTextAndButtonHeroBanner = (
  block: EmailBuilderBlockOf<"textAndButtonHeroBanner">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const imageUrl = s.backgroundImageUrl ? escapeHtml(s.backgroundImageUrl) : "";
  const fallbackColor = imageUrl ? getHeroImageFallbackColor(s) : s.backgroundColor;
  // The dark gradient belongs to the default dark hero; a chosen colour
  // (design (16) COLOR_DEFS hero) is used flat.
  const gradient = [emailBuilderColors.black, "#14161A"].includes(s.backgroundColor.toUpperCase())
    ? ";background-image:linear-gradient(180deg,#3A3F49 0%,#14161A 100%)"
    : "";
  // Outlook drops a whole background-image declaration that contains a
  // gradient. The plain image comes first so those clients keep it; clients
  // with gradient support add the design's dark tint. The fallback colour keeps
  // the text readable while images are blocked or still loading.
  // Gmail dark mode: light text stays light on the hero (image, gradient or a
  // kept flat colour); see emailGmailDarkMode.ts.
  const keepLightText = isGmailLightColor(s.textColor);
  const background = imageUrl
    ? `${fallbackColor} url('${imageUrl}') center / cover no-repeat;background-image:linear-gradient(rgba(10,10,10,.55),rgba(10,10,10,.55)),url('${imageUrl}')`
    : `${s.backgroundColor}${gradient || (keepLightText ? `;${gmailKeepBackground(s.backgroundColor).replace(/;$/, "")}` : "")}`;
  const heroText = (html: string) => (keepLightText ? gmailKeepLightText(html) : html);
  const outlookImage = imageUrl
    ? `<!--[if mso]><img src="${imageUrl}" alt="" width="584" style="display:block;width:100%;max-width:584px;height:auto;" /><![endif]-->`
    : "";
  const sideImage = s.sideImageUrl ? escapeHtml(s.sideImageUrl) : "";
  const align = sideImage ? "left" : "center";
  const button = ctaButton({label:s.buttonName,url:getEmailBuilderButtonUrl(s),background:s.highlightColor,color:s.buttonTextColor,padding:"11px 22px",fontSize:13,fontWeight:600,margin:"18px 0 0",align});
  const copy = `<div style="font-family:${fontStack(theme.fontFamily)};color:${s.textColor};"><h1 style="margin:0;font-size:30px;font-weight:700;line-height:1.2;text-align:${align};">${heroText(richTextToHtml(s.title))}</h1><div style="margin-top:10px;font-size:17px;font-weight:600;text-align:${align};">${heroText(richTextToHtml(s.helperText))}</div>${button}</div>`;
  let inner = `${outlookImage}${copy}`;
  if (sideImage) {
    const width = Math.min(300, Math.max(90, Math.round(s.sideImageWidth || 200)));
    const fit = s.sideImageFit === "contain" ? "contain" : "cover";
    const picture = `<img src="${sideImage}" alt="" width="${width}" style="display:block;width:${width}px;max-width:100%;height:auto;${fit === "cover" ? "aspect-ratio:4/3;object-fit:cover;" : "object-fit:contain;"}border-radius:10px;" />`;
    const imageColumn: EmailColumn = { html: picture, width: `${width + 22}px`, style: "vertical-align:middle;padding-right:22px;" };
    const textColumn: EmailColumn = { html: copy, style: "vertical-align:middle;" };
    const columns = s.sideImagePosition === "right"
      ? [{ ...textColumn, style: "vertical-align:middle;padding-right:22px;" }, { ...imageColumn, style: "vertical-align:middle;" }]
      : [imageColumn, textColumn];
    inner = `${outlookImage}${emailColumnsResponsiveStyles}${emailColumns(columns)}`;
  }
  return sectionShell({
    inner,
    background: `${background};background-size:cover;background-position:center;background-repeat:no-repeat`,
    padding: sideImage || stripSimpleMarkup(s.buttonName).trim() ? "56px 28px" : "84px 28px",
    cellAttributes: imageUrl ? ` bgcolor="${escapeHtml(fallbackColor)}" background="${imageUrl}"` : "",
  });
};

// Relative luminance of a #RGB or #RRGGBB colour; other formats return null.
const getHexColorLuminance = (color: string): number | null => {
  const hex = color.trim().replace(/^#/, "");
  const fullHex = /^[0-9a-f]{3}$/i.test(hex) ? hex.replace(/./g, "$&$&") : hex;
  if (!/^[0-9a-f]{6}$/i.test(fullHex)) return null;
  const [red, green, blue] = [0, 2, 4].map((index) => {
    const channel = Number.parseInt(fullHex.slice(index, index + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

/**
 * Colour behind the hero image. The chosen background colour is used only
 * when the title stays readable on it, because the image can be missing.
 */
export const getHeroImageFallbackColor = ({
  textColor,
  backgroundColor,
}: Pick<TextAndButtonHeroBannerSettings, "textColor" | "backgroundColor">): string => {
  const text = getHexColorLuminance(textColor);
  const background = getHexColorLuminance(backgroundColor);
  if (text !== null && background !== null) {
    const contrast = (Math.max(text, background) + 0.05) / (Math.min(text, background) + 0.05);
    if (contrast >= 3) return backgroundColor;
  }
  return text !== null && text < 0.5 ? emailBuilderColors.white : "#14161A";
};

export const renderTextHeroBanner = (
  block: EmailBuilderBlockOf<"textHeroBanner">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;

  return sectionShell({
    inner: `<h2 style="margin:0;font-family:${fontStack(
      theme.fontFamily
    )};color:${s.textColor};font-size:30px;line-height:1.2;font-weight:800;">${richTextToHtml(
      s.title
    )}</h2><p style="margin:10px 0 0;font-family:${fontStack(
      theme.fontFamily
    )};color:${s.textColor};font-size:16px;line-height:1.6;">${richTextToHtml(
      s.textContent
    )}</p>`,
    background: s.backgroundColor,
    padding: "38px 36px",
  });
};

export const renderEmailBanner = (
  block: EmailBuilderBlockOf<"emailBanner">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const titleLines = s.title.split("\n").filter((line) => line.trim());
  const titleHtml = titleLines
    .map((line, index) => {
      const isAccentLine = titleLines.length > 1 && index === titleLines.length - 1;
      return `<span style="display:block;color:${
        isAccentLine ? s.highlightColor : s.textColor
      };${isAccentLine ? "font-style:italic;" : ""}">${richTextToHtml(line)}</span>`;
    })
    .join("");
  const chip = s.buttonName.trim()
    ? `<a href="${escapeHtml(
        normalizeEmailLinkUrl(s.addLink) || "#"
      )}" style="display:inline-block;background:${s.highlightColor};color:${
        s.buttonTextColor
      };font-size:13px;font-weight:700;letter-spacing:2px;text-transform:uppercase;text-decoration:none;padding:10px 18px;border-radius:4px;">${richTextToHtml(
        s.buttonName
      )}</a>`
    : "";

  return sectionShell({
    inner: `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;"><tr><td style="vertical-align:middle;width:50%;padding-right:18px;text-align:left;">${chip}<h2 style="margin:${
      chip ? "18px" : "0"
    } 0 0;font-family:${fontStack(
      theme.fontFamily
    )};font-size:40px;line-height:1.1;font-weight:800;">${titleHtml}</h2></td><td style="vertical-align:middle;width:50%;text-align:right;">${imageOrPlaceholder(
      {
        imageUrl: s.ceoImageUrl,
        alt: "",
        placeholderLabel: "CEO Image",
        maxWidth: "100%",
        height: 220,
        layoutWidth: 260,
      }
    )}</td></tr></table>`,
    background: s.backgroundColor,
    padding: "34px 36px",
    align: "left",
  });
};

export const renderUpdateBanner = (
  block: EmailBuilderBlockOf<"updateBanner">, theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  return sectionShell({
    inner: `<div style="border:1px solid #ECECEC;border-radius:7px;padding:24px;font-family:${fontStack(theme.fontFamily)};text-align:center;">${s.title ? `<div style="font-size:16px;font-weight:700;color:${s.textColor};">${richTextToHtml(s.title)}</div>` : ""}${s.description ? `<div style="margin:8px auto 0;max-width:420px;font-size:13px;color:#727272;line-height:1.5;">${richTextToHtml(s.description)}</div>` : ""}${ctaButton({label:s.buttonName,url:getEmailBuilderButtonUrl(s),background:s.highlightColor,color:s.buttonTextColor,margin:"12px 0 0"})}</div>`,
    background:s.backgroundColor,padding:"0",
  });
};

export const renderAlertBanner = (
  block: EmailBuilderBlockOf<"alertBanner">, theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  return sectionShell({
    inner:`<table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin:0 auto;"><tr><td style="vertical-align:middle;line-height:0;">${renderEmailBuilderIcon(s.alertIcon,s.textColor,22)}</td><td style="vertical-align:middle;padding-left:12px;font-family:${fontStack(theme.fontFamily)};color:${s.textColor};font-size:17px;font-weight:700;letter-spacing:.04em;line-height:1.5;">${richTextToHtml(s.alertTitle)}</td></tr></table>`,
    background:s.backgroundColor,padding:"30px 24px",
  });
};

export const renderAdvocacyUpdateBanner = (
  block: EmailBuilderBlockOf<"advocacyUpdateBanner">, theme: EmailBuilderRenderTheme
): string => {
  const s=block.settings;
  const button = s.buttonName.trim() ? ctaButton({label:s.buttonName,url:getEmailBuilderButtonUrl(s),background:s.buttonColor,color:s.buttonTextColor,padding:"12px 20px",fontSize:13,radius:8}) : "";
  const copy=`${inlineIconRow(renderEmailBuilderIcon(s.icon,s.textColor,14),richTextToHtml(s.title),8,`font-size:11px;font-weight:700;letter-spacing:.08em;color:${s.textColor};`)}<div style="margin-top:6px;font-size:20px;font-weight:700;color:${s.textColor};">${richTextToHtml(s.label)}</div><div style="margin-top:6px;font-size:13px;color:${getReadableEmailTextColor(s.backgroundColor) === emailBuilderColors.white ? "#C9C9C9" : "#444444"};line-height:1.5;">${richTextToHtml(s.description)}</div>`;
  // The button cell shrinks to its label; the columns stack on narrow screens.
  const content=button
    ? `${emailColumnsResponsiveStyles}${emailColumns([{html:copy,style:"vertical-align:middle;padding-right:18px;"},{html:button,width:"1%",style:"vertical-align:middle;white-space:nowrap;"}],"table-layout:auto;")}`
    : copy;
  return sectionShell({
    inner:`<div style="font-family:${fontStack(theme.fontFamily)};text-align:left;">${content}</div>`,
    background:s.backgroundColor,padding:"24px",align:"left",
  });
};

export const renderMemberSpotlight = (
  block: EmailBuilderBlockOf<"memberSpotlight">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);
  const itemsHtml = s.items
    .map(
      (item) =>
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin-top:22px;"><tr><td style="width:190px;vertical-align:top;">${imageOrPlaceholder(
          {
            imageUrl: item.imageUrl,
            alt: stripSimpleMarkup(item.title),
            placeholderLabel: "Add an image",
            maxWidth: "180px",
            height: 150,
          }
        )}</td><td style="vertical-align:top;padding-left:22px;text-align:left;"><h3 style="margin:0;font-family:${font};color:${
          s.textColor
        };font-size:20px;font-weight:800;">${richTextToHtml(
          item.title
        )}</h3><p style="margin:10px 0 0;font-family:${font};color:#4b5563;font-size:15px;line-height:1.6;">${richTextToHtml(
          item.description
        )}</p>${
          item.buttonName.trim()
            ? `<p style="margin:14px 0 0;"><a href="${escapeHtml(
                normalizeEmailLinkUrl(item.addLink) || "#"
              )}" style="font-family:${font};color:${
                s.highlightColor
              };font-size:15px;font-weight:700;text-decoration:underline;">${richTextToHtml(
                item.buttonName
              )} &rsaquo;</a></p>`
            : ""
        }</td></tr></table>`
    )
    .join("");

  return sectionShell({
    inner: `<h2 style="margin:0;font-family:${font};color:${
      s.textColor
    };font-size:28px;font-weight:800;text-align:center;">${richTextToHtml(
      s.title
    )}</h2>${itemsHtml}`,
    background: s.backgroundColor,
    padding: "34px 36px",
    align: "left",
  });
};

export const renderArticleList = (
  block: EmailBuilderBlockOf<"articleList">, theme: EmailBuilderRenderTheme
): string => {
  const s=block.settings;
  const size=Math.min(260,Math.max(90,s.imageSize || 150));
  const imageHeight=Math.round(size*2/3*1000)/1000;
  const heading=s.title ? `<div style="font-size:19px;font-weight:700;text-align:center;margin-bottom:16px;">${richTextToHtml(s.title)}</div>` : "";
  const items=s.items.map((item,index)=>{
    // Natural height: Outlook ignores object-fit and would stretch a fixed-height crop.
    const picture=item.imageUrl ? `<img src="${escapeHtml(item.imageUrl)}" alt="" width="${size}" style="display:block;width:100%;max-width:${size}px;height:auto;border-radius:8px;" />` : emptyImage(`${size}px`,`${imageHeight}px`,"#C9D7E8","",26);
    const copy=`<div style="font-size:15px;font-weight:700;">${richTextToHtml(item.title)}</div><div style="margin-top:6px;font-size:13px;line-height:1.55;">${richTextToHtml(item.description)}</div>${item.linkText ? `<div style="margin-top:6px;font-size:13px;font-weight:700;">${linkedMarkup(`${richTextToHtml(item.linkText)} &rsaquo;`,item.linkUrl || "",`color:${s.highlightColor};`)}</div>` : ""}`;
    return emailColumns([{html:picture,width:size+18,style:"padding-right:18px;"},{html:copy}],`text-align:left;${index ? "margin-top:16px;" : ""}`);
  }).join("");
  return sectionShell({inner:`${emailColumnsResponsiveStyles}<div style="font-family:${fontStack(theme.fontFamily)};color:${s.textColor};">${heading}${items}</div>`,background:s.backgroundColor,padding:"26px 20px",align:"left"});
};

export const renderSplitArticleWithBorder = (
  block: EmailBuilderBlockOf<"splitArticleWithBorder">, theme: EmailBuilderRenderTheme
): string => {
  const s=block.settings;
  const width=s.items.length ? 100/s.items.length : 100;
  const items=s.items.map((item,index)=>`<td style="width:${width}%;vertical-align:top;padding:6px 14px;${index ? "border-left:1px solid #ECECEC;" : ""}"><div style="line-height:0;">${renderEmailBuilderIcon(item.icon,s.highlightColor,18)}</div><div style="margin-top:8px;font-size:14px;font-weight:700;color:${s.textColor};">${richTextToHtml(item.title)}</div><div style="margin-top:8px;font-size:12px;color:#727272;line-height:1.5;">${richTextToHtml(item.desc)}</div>${item.buttonName ? `<div style="margin-top:8px;font-size:11px;font-weight:700;letter-spacing:.04em;">${linkedMarkup(`${richTextToHtml(item.buttonName)} &rsaquo;`,item.addLink,`color:${s.highlightColor};`)}</div>` : ""}</td>`).join("");
  return sectionShell({inner:`<table role="presentation" width="100%" style="table-layout:fixed;border-collapse:collapse;font-family:${fontStack(theme.fontFamily)};"><tr>${items}</tr></table>`,background:s.backgroundColor,padding:"20px 12px",align:"left"});
};

export const renderTextAndEmailContent = (
  block: EmailBuilderBlockOf<"textAndEmailContent">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const paragraphs = s.textContent
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map(
      (paragraph, index) =>
        `<p style="margin:${index === 0 ? "0" : "16px"} 0 0;font-family:${fontStack(
          theme.fontFamily
        )};color:${s.textColor};font-size:16px;line-height:1.7;text-align:${
          s.textAlign
        };">${richTextToHtml(paragraph)}</p>`
    )
    .join("");

  return sectionShell({
    inner: paragraphs,
    background: s.backgroundColor,
    padding: "30px 36px",
    align: s.textAlign === "justify" ? "left" : s.textAlign,
  });
};

const renderIconGrid = ({ settings, theme, columns }: {
  settings: FourGridIconAndTitleSettings | SixGridIconAndTitleSettings;
  theme: EmailBuilderRenderTheme; columns: number;
}): string => {
  const items=settings.items.map((item)=>{
    const url=item.linkMode === "pdf" ? item.pdfUrl || "" : item.linkTo;
    const content=`${iconTile({icon:item.icon,color:settings.highlightColor,background:item.highlighted ? emailBuilderColors.lightBlue : "transparent",border:!item.highlighted})}<div style="margin-top:8px;font-size:10px;font-weight:700;letter-spacing:.05em;color:${settings.textColor};text-align:center;">${richTextToHtml(item.title)}</div>`;
    return `<div style="display:inline-block;width:88px;vertical-align:top;margin:0 9px 12px;">${linkedMarkup(content,url)}</div>`;
  }).join("");
  const heading=settings.title ? `<div style="font-size:20px;font-weight:700;color:${settings.textColor};text-align:center;margin-bottom:20px;">${richTextToHtml(settings.title)}</div>` : "";
  return sectionShell({inner:`<div style="font-family:${fontStack(theme.fontFamily)};">${heading}<div style="font-size:0;text-align:center;margin:0 auto -12px;${columns === 3 ? "max-width:360px;" : ""}">${items}</div></div>`,background:settings.backgroundColor,padding:"26px 20px"});
};

export const renderFourGridIconAndTitle = (block: EmailBuilderBlockOf<"fourGridIconAndTitle">,theme:EmailBuilderRenderTheme):string =>
  renderIconGrid({settings:block.settings,theme,columns:5});

export const renderSixGridIconAndTitle = (block: EmailBuilderBlockOf<"sixGridIconAndTitle">,theme:EmailBuilderRenderTheme):string =>
  renderIconGrid({settings:block.settings,theme,columns:3});


// Upcoming Event Cards in one row share the row height: the card border,
// radius and background sit on the table cell, and cellspacing makes the
// 14 px gutters (Outlook keeps cellspacing). Narrow screens stack the cards.
const eventCardsNarrowCss = ".cmp-email-event-cards,.cmp-email-event-cards>tbody,.cmp-email-event-cards>tbody>tr{display:block!important;width:auto!important;}.cmp-email-event-cards{margin:14px 14px 0!important;}.cmp-email-event-cards>tbody>tr>td[data-email-event-card]{display:block!important;width:auto!important;margin:0 0 14px!important;}.cmp-email-event-cards>tbody>tr>td.cmp-email-event-filler{display:none!important;}";
const renderEventCardRows = (cards: string[], perRow: number): string => {
  const width=`${Math.round(100/perRow*1000)/1000}%`;
  const card=(html:string)=>`<td class="cmp-email-column" data-email-event-card width="${width}" style="width:${width};vertical-align:top;box-sizing:border-box;padding:0;border:1px solid #ECECEC;border-radius:12px;background:#FFFFFF;text-align:left;overflow:hidden;">${html}</td>`;
  const filler=`<td class="cmp-email-event-filler" width="${width}" style="width:${width};padding:0;"></td>`;
  const rows=Array.from({length:Math.ceil(cards.length/perRow)},(_,index)=>{
    const row=cards.slice(index*perRow,(index+1)*perRow);
    return `<tr>${row.map(card).join("")}${filler.repeat(perRow-row.length)}</tr>`;
  }).join("");
  return `${emailResponsiveStyles(eventCardsNarrowCss)}<table class="cmp-email-event-cards" role="presentation" width="100%" cellpadding="0" cellspacing="14" style="border-collapse:separate;border-spacing:14px;table-layout:fixed;width:100%;"><tbody>${rows}</tbody></table>`;
};

export const renderUpcomingEventsList = (
  block: EmailBuilderBlockOf<"upcomingEventsList">, theme: EmailBuilderRenderTheme
): string => {
  const s=block.settings;
  const image=s.imageUrl ? `<div style="margin-bottom:18px;">${imageOrPlaceholder({imageUrl:s.imageUrl,alt:"",placeholderLabel:"",maxWidth:"100%",layoutWidth:600})}</div>` : "";
  // D22 EC-26 (F9): event cards, up to 3 per row, each with the event's
  // featured image or a black panel with the date, then the details and a
  // Learn More button (design DEFAULTS.events link).
  const buttonColor=s.buttonColor || s.highlightColor;
  const perRow=Math.max(1,Math.min(s.events.length,3));
  const cells=s.events.map((event):string=>{
    const date=getEventDateParts(event.date);
    const linkUrl=normalizeUpcomingEventLinkUrl(event.linkUrl);
    const title=linkedMarkup(richTextToHtml(event.eventTitle),linkUrl,`color:${s.textColor};`);
    const tag=event.tag ? `<span style="display:inline-block;margin-bottom:6px;padding:4px 10px;background:${event.tagColor || s.highlightColor};border-radius:6px;color:#FFFFFF;font-size:11px;font-weight:700;">${escapeHtml(event.tag)}</span>` : "";
    const visual=event.imageUrl
      ? `<img src="${escapeHtml(event.imageUrl)}" alt="" style="display:block;width:100%;height:auto;aspect-ratio:16/9;object-fit:cover;border-radius:10px 10px 0 0;" />`
      : `<div style="background:#0A0A0A;border-radius:10px 10px 0 0;padding:26px 12px;text-align:center;color:#FFFFFF;"><div style="font-size:12px;font-weight:800;letter-spacing:.06em;">${escapeHtml(date.month)}</div><div style="font-size:30px;font-weight:800;line-height:1.1;">${escapeHtml(date.day)}</div></div>`;
    const details=[event.date ? `<div style="font-weight:800;">${richTextToHtml(event.date)}</div>` : "",event.time ? `<div style="margin-top:2px;font-weight:700;">${richTextToHtml(event.time)}</div>` : "",event.location ? `<div style="margin-top:2px;color:#444444;">${richTextToHtml(event.location)}</div>` : ""].join("");
    return `<div style="overflow-wrap:anywhere;word-wrap:break-word;word-break:break-word;">${linkedMarkup(visual,linkUrl)}<div style="padding:14px 16px 16px;">${tag}<div style="font-size:17px;font-weight:800;line-height:1.3;">${title}</div>${event.description ? `<div style="margin-top:6px;font-size:13px;color:#444444;line-height:1.55;">${richTextToHtml(event.description)}</div>` : ""}<div data-email-event-details style="margin-top:10px;font-size:13px;color:#0A0A0A;">${details}</div>${ctaButton({label:"Learn More",url:linkUrl,background:buttonColor,color:getReadableEmailTextColor(buttonColor),align:"left",margin:"12px 0 0",radius:8,fontSize:13})}</div></div>`;
  });
  const cards=cells.length ? `<div style="margin:0 -14px;">${renderEventCardRows(cells,perRow)}</div>` : "";
  return sectionShell({inner:`<div style="font-family:${fontStack(theme.fontFamily)};">${image}${sectionHeading(s.title,s.highlightColor,fontStack(theme.fontFamily))}${cards}</div>`,background:s.backgroundColor,padding:"20px",align:"left"});
};

export const renderEventAnnouncement = (
  block: EmailBuilderBlockOf<"eventAnnouncement">, theme: EmailBuilderRenderTheme
): string => {
  const s=block.settings;
  const detailRow=(icon:EmailBuilderIconKey,content:string):string => `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin-top:9px;text-align:left;"><tbody><tr><td width="24" style="width:24px;vertical-align:top;line-height:0;">${renderEmailBuilderIcon(icon,s.highlightColor,15)}</td><td style="vertical-align:top;font-size:13px;">${content}</td></tr></tbody></table>`;
  const cards=s.events.map((event,index)=>{
    const [venue="",...addressLines]=event.location.split("\n").map((line)=>line.trim());
    const hidden=(key:string)=>isEmailBuilderFieldHidden(block,`events.${event.id}.${key}`);
    const venueHtml=!hidden("venue") && venue ? `<span style="font-weight:400;">${richTextToHtml(venue)}</span>` : "";
    const addressHtml=hidden("address") ? "" : addressLines.filter(Boolean).map((line)=>`<div style="color:#727272;">${richTextToHtml(line)}</div>`).join("");
    return `<div style="${index ? "margin-top:18px;padding-top:9px;border-top:1px solid #ECECEC;" : ""}">${!hidden("date") && event.date ? detailRow("calendar",`<span style="font-size:14px;font-weight:700;">${richTextToHtml(event.date)}</span>`) : ""}${!hidden("time") && event.time ? detailRow("clock",richTextToHtml(event.time)) : ""}${venueHtml || addressHtml ? detailRow("mapPin",venueHtml+addressHtml) : ""}${ctaButton({label:s.buttonName ?? "Register Now",url:getEmailBuilderButtonUrl({...s,linkUrl:event.linkUrl}),background:s.buttonColor || s.highlightColor,color:getReadableEmailTextColor(s.buttonColor || s.highlightColor),align:"left",margin:"13px 0 0"})}</div>`;
  }).join("");
  return sectionShell({inner:`<div style="padding:11px 24px 20px;border:1px solid #ECECEC;border-radius:7px;font-family:${fontStack(theme.fontFamily)};color:${s.textColor};">${s.title ? `<div style="font-size:15px;font-weight:700;margin-top:9px;">${richTextToHtml(s.title)}</div>` : ""}${cards}</div>`,background:s.backgroundColor,padding:"0",align:"left"});
};

export const renderSpecialGuestSponsor = (
  block: EmailBuilderBlockOf<"specialGuestSponsor">,theme:EmailBuilderRenderTheme
):string => {
  const s=block.settings;
  const image=s.sponsorLogoUrl ? imageOrPlaceholder({imageUrl:s.sponsorLogoUrl,alt:"Sponsor logo",placeholderLabel:"",maxWidth:"240px",height:100}) : emptyImage("220px","80px","#FAFAFB","Sponsor logo",20);
  return sectionShell({inner:`<div style="padding:26px 24px;border:1px solid #ECECEC;border-radius:7px;text-align:center;font-family:${fontStack(theme.fontFamily)};color:${s.textColor};"><div style="font-size:12px;font-weight:700;letter-spacing:.08em;">${richTextToHtml(s.title)}</div><div style="display:table;max-width:100%;margin:12px auto;">${image}</div><div style="font-size:13px;">${richTextToHtml(s.textContent)}</div></div>`,background:s.backgroundColor,padding:"0"});
};

export const renderSponsorsList = (
  block: EmailBuilderBlockOf<"sponsorsList">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);
  const sponsors = s.sponsors.slice(0, 3);
  const cells = sponsors
    .map((sponsor) => {
      const safeLink = normalizeEmailLinkUrl(sponsor.linkTo);

      return `<td style="width:${Math.floor(
          100 / Math.max(sponsors.length, 1)
        )}%;padding:8px;text-align:center;vertical-align:middle;">${
          safeLink
            ? `<a href="${escapeHtml(safeLink)}" style="text-decoration:none;">`
            : ""
        }<div style="border:1px solid #e5e7eb;border-radius:8px;padding:14px;">${imageOrPlaceholder(
          {
            imageUrl: sponsor.logoUrl,
            alt: "Sponsor logo",
            placeholderLabel: "Sponsor Logo",
            maxWidth: "100%",
            height: 70,
            layoutWidth: 150,
          }
        )}</div>${safeLink ? "</a>" : ""}</td>`;
    })
    .join("");

  return sectionShell({
    inner: `<div style="border:1px solid #e5e7eb;border-radius:10px;padding:24px;"><p style="margin:0;font-family:${font};color:${
      s.textColor
    };font-size:15px;font-weight:800;letter-spacing:3px;text-align:center;">${richTextToHtml(
      s.title
    )}</p><table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin-top:14px;"><tr>${cells}</tr></table></div>`,
    background: s.backgroundColor,
    padding: "26px 36px",
  });
};

export const renderAdvertisingPdf = (
  block: EmailBuilderBlockOf<"advertisingPdf">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);
  // The button opens the link OR the attached PDF (mutually exclusive per the
  // settings sheet's literal "OR" separator).
  const destination = s.addLink.trim() || s.attachPdfUrl.trim();

  return sectionShell({
    inner: `<h2 style="margin:0;font-family:${font};color:${
      s.textColor
    };font-size:30px;font-weight:800;">${richTextToHtml(
      s.title
    )}</h2><div style="margin:20px auto 0;max-width:300px;border:1px solid #e5e7eb;border-radius:10px;padding:18px;">${imageOrPlaceholder(
      {
        imageUrl: s.imageUrl,
        alt: stripSimpleMarkup(s.title),
        placeholderLabel: "Advertiser Image",
        maxWidth: "260px",
        height: 110,
      }
    )}</div>${ctaButton({
      label: s.buttonName,
      url: destination,
      background: s.highlightColor,
      color: s.buttonTextColor,
    })}`,
    background: s.backgroundColor,
    padding: "36px 36px",
  });
};

export const renderKeyLinks = (
  block:EmailBuilderBlockOf<"keyLinks">,theme:EmailBuilderRenderTheme
):string => {
  const s=block.settings;
  const items=s.items.filter((item)=>!isEmailBuilderFieldHidden(block,`items.${item.id}.buttonText`)).map((item)=>`<div style="margin-top:10px;font-size:13px;font-weight:600;">${linkedMarkup(`<span style="margin-right:8px;">${renderEmailBuilderIcon("arrowRight",s.highlightColor,13)}</span>${richTextToHtml(item.buttonText)}`,item.addLink,`color:${s.highlightColor};`)}</div>`).join("");
  return sectionShell({inner:`<div style="border:1px solid #ECECEC;border-radius:7px;padding:20px 24px;font-family:${fontStack(theme.fontFamily)};color:${s.textColor};"><div style="font-size:15px;font-weight:700;">${richTextToHtml(s.title)}</div>${items}</div>`,background:s.backgroundColor,padding:"0",align:"left"});
};

export const renderInquiries = (
  block: EmailBuilderBlockOf<"inquiries">,
  theme: EmailBuilderRenderTheme
): string => {
  const s = block.settings;
  const font = fontStack(theme.fontFamily);

  return sectionShell({
    inner: `<div style="border:1px solid #e5e7eb;border-radius:10px;padding:24px;text-align:left;"><p style="margin:0;font-family:${font};color:${
      s.textColor
    };font-size:14px;font-weight:800;">${richTextToHtml(
      s.title
    )}</p><p style="margin:10px 0 0;font-family:${font};color:${
      s.textColor
    };font-size:19px;">${richTextToHtml(s.textContent)}</p>${
      s.email.trim()
        ? `<p style="margin:10px 0 0;"><a href="mailto:${escapeHtml(
            s.email
          )}" style="font-family:${font};color:${
            s.textColor
          };font-size:15px;text-decoration:underline;">${escapeHtml(
            s.email
          )}</a></p>`
        : ""
    }${
      s.phoneNumber.trim()
        ? `<p style="margin:6px 0 0;font-family:${font};color:${
            s.textColor
          };font-size:15px;">${escapeHtml(s.phoneNumber)}</p>`
        : ""
    }</div>`,
    background: s.backgroundColor,
    padding: "26px 36px",
    align: "left",
  });
};

export type EmailSignatureChamber = {
  /** Chamber name under the signature (footer ORGANIZATION or profile name). */
  name: string;
  /** Step 2 Chamber Logo. */
  logoUrl: string;
  /** Palette thumbnail: design sample "Chamber Name" and a dashed "Chamber Logo" box. */
  sample?: boolean;
};

// Blocks saved before Oct 2026 (no text) render exactly as they did then.
const renderLegacySignature = (
  s:SignatureSettings,theme:EmailBuilderRenderTheme
):string => {
  const role=[(s.designation ?? "").trim(),(s.chamberName ?? "").trim()].filter(Boolean).join(", ");
  const name=s.addSignature || s.name || "";
  const additionalName=s.addSignature && s.name && s.addSignature !== s.name ? `<div style="margin-top:4px;font-size:13px;font-weight:700;">${richTextToHtml(s.name)}</div>` : "";
  return sectionShell({inner:`<div style="border:1px solid #ECECEC;border-radius:7px;padding:20px 24px;font-family:${fontStack(theme.fontFamily)};color:${s.textColor};font-size:13px;"><div>${richTextToHtml(s.opening ?? "Sincerely,")}</div><div style="margin-top:4px;font-family:Georgia,serif;font-size:19px;font-style:italic;">${richTextToHtml(name)}</div>${additionalName}<div style="margin-top:4px;font-size:12px;font-weight:600;letter-spacing:.05em;color:#727272;">${escapeHtml(role)}</div></div>`,background:s.backgroundColor,padding:"0",align:"left"});
};

/**
 * Design (19) SIGNATURE: the SIGNATURE text (13px, line height 1.5), the
 * chamber name (12px/600, .05em, #727272) and the chamber logo in a
 * 128 x 64 box on the left.
 */
export const renderSignature = (
  block:EmailBuilderBlockOf<"signature">,theme:EmailBuilderRenderTheme,chamber?:EmailSignatureChamber
):string => {
  const s=block.settings;
  if(typeof s.text !== "string") return renderLegacySignature(s,theme);
  const chamberName=chamber?.sample ? "Chamber Name" : chamber?.name.trim() || "";
  const logoUrl=chamber?.sample ? "" : chamber?.logoUrl || "";
  const name=chamberName ? `<div style="margin-top:4px;font-size:12px;font-weight:600;letter-spacing:.05em;color:#727272;">${escapeHtml(chamberName)}</div>` : "";
  const logo=logoUrl
    ? `<div style="margin-top:14px;line-height:0;"><img src="${escapeHtml(logoUrl)}" alt="${escapeHtml(chamberName ? `${chamberName} logo` : "Chamber logo")}" width="128" style="display:block;width:128px;max-width:100%;height:auto;max-height:64px;object-fit:contain;object-position:left center;" /></div>`
    : chamber?.sample
      ? `<div style="box-sizing:border-box;width:128px;max-width:100%;height:64px;line-height:61px;margin-top:14px;border:1.5px dashed #9E9E9E;border-radius:8px;background:#F1F1F3;text-align:center;font-size:12px;font-weight:600;color:#727272;">Chamber Logo</div>`
      : "";
  return sectionShell({inner:`<div style="border:1px solid #ECECEC;border-radius:7px;padding:20px 24px;font-family:${fontStack(theme.fontFamily)};color:${s.textColor};text-align:left;"><div style="font-size:13px;line-height:1.5;">${richTextToHtml(s.text)}</div>${name}${logo}</div>`,background:s.backgroundColor,padding:"0",align:"left"});
};

export const isEmailBuilderFieldHidden = (
  block: EmailBuilderBlock,
  key: string
): boolean => !!block.settings.hiddenFields?.includes(key);

export const getVisibleEmailBuilderBlock = (block: EmailBuilderBlock): EmailBuilderBlock => {
  const hidden = new Set(block.settings.hiddenFields || []);
  if (hidden.size === 0) return block;
  const visibleValue = (value: unknown, path: string): unknown => {
    if (typeof value === "string") return hidden.has(path) ? "" : value;
    if (Array.isArray(value)) return value.map((item, index) => {
      const id = item && typeof item === "object" && "id" in item ? String(item.id) : String(index);
      return visibleValue(item, `${path}.${id}`);
    });
    if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, visibleValue(child, path ? `${path}.${key}` : key)]));
    return value;
  };
  return { ...block, settings: visibleValue(block.settings, "") } as EmailBuilderBlock;
};

const perkLogo = (item: EmailBuilderPerkItem, color: string): string => {
  const initials = item.company.split(/\s+/).map((word) => word[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  return item.logoUrl
    ? `<div style="width:72px;height:72px;border:1px solid #ECECEC;border-radius:12px;background:#FFFFFF;text-align:center;line-height:72px;font-size:0;"><img src="${escapeHtml(item.logoUrl)}" alt="${escapeHtml(item.company)} logo" style="display:inline-block;vertical-align:middle;max-width:62px;max-height:62px;width:auto;height:auto;object-fit:contain;" /></div>`
    : `<div style="width:72px;height:72px;border-radius:12px;background:${color};color:${getReadableEmailTextColor(color)};text-align:center;line-height:72px;font-size:20px;font-weight:700;">${escapeHtml(initials)}</div>`;
};

const perkMeta = (item: EmailBuilderPerkItem): string => {
  const parts = [item.redeem ? `Redeem: ${escapeHtml(item.redeem)}` : "", escapeHtml(item.validUntil)].filter(Boolean);
  return parts.length ? `<div style="border-top:1px solid #F1F1F3;background:#FAFAFB;padding:10px 18px;font-size:11px;color:#444444;">${parts.join(`<span style="margin:0 8px;">&middot;</span>`)}</div>` : "";
};

export type EmailBuilderBlockRenderOptions = {
  builderState?: EmailBuilderState;
  /** Palette thumbnails: design sample text where chamber data would show. */
  samplePlaceholders?: boolean;
  /** The Step 3 canvas: design placeholders for blocks not filled in yet. */
  editorPreview?: boolean;
};

// The signature shows the name the footer shows; no placeholder when unknown.
const getSignatureChamberName = (
  builderState: EmailBuilderState | undefined,
  footerProfile: EmailFooterProfile | null | undefined
): string =>
  (builderState && getEmailBuilderFooterBlock(builderState.blocks)?.settings.organization.trim()) ||
  footerProfile?.name?.trim() || "";

export const renderEmailBuilderBlock = (
  sourceBlock:EmailBuilderBlock,theme:EmailBuilderRenderTheme,
  footerProfile?:EmailFooterProfile | null,options:EmailBuilderBlockRenderOptions = {}
):string => {
  const block=getVisibleEmailBuilderBlock(sourceBlock);
  const s=block.settings;
  const imageWidth=Math.min(100,Math.max(25,s.imageWidth || 100));
  const top=s.topImageUrl ? `<div style="padding:12px 12px 0;"><img src="${escapeHtml(s.topImageUrl)}" alt="" style="display:block;width:${imageWidth}%;max-width:100%;height:auto;object-fit:contain;border-radius:8px;margin:0 auto;" /></div>` : "";
  const bottom=s.extraText ? `<div style="padding:6px 24px 16px;font-size:13px;color:#0A0A0A;line-height:1.6;">${richTextToHtml(s.extraText)}</div>` : "";
  const font=fontStack(theme.fontFamily);
  let content:string;
  switch(block.type) {
    case "heading":
    case "text":
    case "image":
    case "button":
    case "divider":
    case "spacer":
    case "socialLinks": {
      const simple=block.settings;
      const textStyle=`font-family:${font};color:${simple.textColor};text-align:${simple.textAlign};`;
      let inner="";
      let padding="12px 24px";
      if(block.type === "heading") {inner=`<div style="margin:0;font-size:24px;font-weight:700;line-height:1.3;${textStyle}">${richTextToHtml(simple.text)}</div>`;padding="18px 24px";}
      if(block.type === "text") inner=`<div style="margin:0;font-size:14px;line-height:1.6;${textStyle}">${richTextToHtml(simple.text)}</div>`;
      if(block.type === "image") {
        const picture=simple.imageUrl ? `<img src="${escapeHtml(simple.imageUrl)}" alt="" style="display:block;width:100%;max-width:100%;height:auto;${simple.imageFit === "cover" ? "aspect-ratio:3/2;object-fit:cover;" : "object-fit:contain;"}border-radius:10px;" />` : emptyImage("100%","auto","#FAFAFB","Upload an Image",30,{radius:12,labelGap:8,padding:"40px 20px"});
        const imageHtml=`<div style="text-align:${simple.textAlign};"><div data-email-builder-image-container style="display:inline-block;vertical-align:top;width:${imageWidth}%;max-width:100%;">${linkedMarkup(picture,simple.addLink)}</div></div>`;
        const textHtml=simple.imageUrl && simple.text ? `<div style="font-size:14px;line-height:1.6;${textStyle}">${richTextToHtml(simple.text)}</div>` : "";
        // Text sits beside the image only when the image leaves a readable
        // column; a wider image keeps the text below it, as in Outlook.
        inner=textHtml && imageWidth <= imageBesideTextMaxWidth
          ? `${emailColumnsResponsiveStyles}${emailColumns([{html:`<div data-email-builder-image-container>${linkedMarkup(picture,simple.addLink)}</div>`,width:`${imageWidth}%`,style:"padding-right:16px;"},{html:textHtml}])}`
          : `${imageHtml}${textHtml ? `<div style="margin-top:12px;">${textHtml}</div>` : ""}`;
      }
      if(block.type === "button") {inner=ctaButton({label:simple.text,url:getEmailBuilderButtonUrl(simple),background:simple.highlightColor,color:simple.textColor,align:simple.textAlign,padding:"12px 30px",fontSize:14,fontWeight:600,radius:8});padding="14px 24px";}
      if(block.type === "divider") inner=`<div style="height:${Math.min(12,Math.max(1,Math.round(simple.lineHeight || 1)))}px;background:${simple.textColor};"></div>`;
      if(block.type === "spacer") {content=`<div style="height:${Math.min(80,Math.max(8,simple.height))}px;line-height:1px;">&#8203;</div>`;break;}
      if(block.type === "socialLinks") {
        inner=getEmailFooterSocialLinks(footerProfile).map((link)=>linkedMarkup(`<span style="display:inline-block;vertical-align:middle;box-sizing:border-box;width:34px;height:34px;line-height:31px;margin:0 6px;border:1.5px solid ${simple.highlightColor};border-radius:50%;"><img data-cmp-newsletter-builder-icon="social-${link.platform}" src="${recolorEmailIconDataUrl(link.iconDataUrl.replace("data:image/png;base64,", ""), simple.highlightColor)}" alt="${escapeHtml(link.label)}" width="15" height="15" style="display:inline-block;width:15px;height:15px;vertical-align:middle;" /></span>`,link.url,`color:${simple.highlightColor};`)).join("");
        padding="16px 24px";
      }
      content=sectionShell({inner,background:simple.backgroundColor,padding,align:simple.textAlign});break;
    }
    case "newsroomHighlights": {
      // D22 EC-27 / design (16) newsroom: cards in a grid, up to 3 per row
      // (6 articles at most). An article without an image shows the
      // chamber logo on white. sourceAttribution is the website's origin
      // code ("cmp-newsletter", "website-builder") and is not shown.
      const news=block.settings;
      const buttonColor=news.buttonColor || news.highlightColor;
      const logoUrl=options.builderState?.logoUrl || "";
      const perRow=Math.max(1,Math.min(news.items.length,3));
      const width=`${Math.round(100/perRow*1000)/1000}%`;
      const cells=news.items.map((item):EmailColumn=>{
        const date=item.publishedAtUtcMilli ? new Date(item.publishedAtUtcMilli).toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric",timeZone:"UTC"}) : "";
        const picture=item.imageUrl
          ? `<img src="${escapeHtml(item.imageUrl)}" alt="" style="display:block;width:100%;height:auto;aspect-ratio:16/9;object-fit:cover;border-radius:8px;" />`
          : logoUrl
            ? `<div style="border:1px solid #F1F1F3;border-radius:8px;background:#FFFFFF;text-align:center;padding:14px 10px;"><img src="${escapeHtml(logoUrl)}" alt="" style="display:inline-block;max-width:70%;max-height:80px;width:auto;height:auto;object-fit:contain;" /></div>`
            : emptyImage("100%","auto","#F0F2F5","",26);
        const meta=[item.category ? `<span style="display:inline-block;background:${news.highlightColor};color:${getReadableEmailTextColor(news.highlightColor)};border-radius:6px;padding:3px 8px;font-weight:700;">${escapeHtml(item.category)}</span>` : "",escapeHtml(date),escapeHtml(item.readTime)].filter(Boolean).join(`<span style="margin:0 6px;">&middot;</span>`);
        const html=`<div style="background:#FFFFFF;border:1px solid #ECECEC;border-radius:14px;padding:14px;text-align:left;overflow-wrap:anywhere;word-wrap:break-word;word-break:break-word;">${linkedMarkup(picture,item.linkUrl)}${meta ? `<div style="margin-top:10px;font-size:11px;color:#727272;line-height:1.8;">${meta}</div>` : ""}<div style="margin-top:8px;font-size:15px;font-weight:700;line-height:1.3;">${linkedMarkup(escapeHtml(item.title),item.linkUrl,"color:#0A0A0A;")}</div><div style="margin-top:10px;padding-top:10px;border-top:1px solid #F1F1F3;">${item.author ? `<div style="font-size:12px;font-weight:600;color:#0A0A0A;margin-bottom:8px;">${escapeHtml(item.author)}</div>` : ""}${ctaButton({label:"Read More",url:item.linkUrl,background:buttonColor,color:getReadableEmailTextColor(buttonColor),padding:"8px 14px",fontSize:12,radius:8,align:"left"})}</div></div>`;
        return {html,width,style:"padding:0 7px 14px;"};
      });
      const rows=Array.from({length:Math.ceil(cells.length/perRow)},(_,index)=>{
        const row=cells.slice(index*perRow,(index+1)*perRow);
        return [...row,...Array.from({length:perRow-row.length},():EmailColumn=>({html:"",width}))];
      });
      content=sectionShell({inner:emailColumnsResponsiveStyles+sectionHeading(news.title,news.highlightColor,font)+`<div style="margin:14px -7px 0;">${emailColumnRows(rows,"text-align:left;")}</div>`,background:theme.backgroundColor,padding:"20px",align:"left"});break;
    }
    case "newMembers": {
      const members=block.settings;
      content=sectionShell({inner:`${emailColumnsResponsiveStyles}<div style="font-size:19px;font-weight:700;text-align:center;margin-bottom:14px;">${richTextToHtml(members.title)}</div><div ${NEW_MEMBERS_ROWS_MARKER_ATTRIBUTE}="${escapeHtml(block.id)}" style="font-size:0;text-align:left;margin:0 -14px;position:relative;">${(members.days ?? 30) >= 60 ? renderNewMembersCards(members.items,members.highlightColor) : renderNewMembersRows(members.items,members.highlightColor)}</div>`,background:theme.backgroundColor,padding:"20px"});break;
    }
    case "advertisementBanner": {
      const ad=block.settings;
      const size=emailAdBannerSizes[ad.size || "large"];
      const slots=getEmailAdBannerSlots(ad);
      const label=`<div style="font-size:10px;font-weight:700;letter-spacing:.12em;color:#9E9E9E;text-align:center;margin-bottom:8px;">ADVERTISEMENT</div>`;
      if(size.count===1) {
        const fit=ad.imageFit==="cover" ? `aspect-ratio:${size.width}/${size.height};object-fit:cover;` : "object-fit:contain;";
        const picture=ad.imageUrl ? `<img src="${escapeHtml(ad.imageUrl)}" alt="Advertisement" style="display:block;width:100%;height:auto;${fit}border-radius:10px;" />` : emptyImage("100%","auto","#FAFAFB","Upload a banner image in Update Block Contents",24,{radius:10,padding:"28px"});
        content=sectionShell({inner:`${label}${linkedMarkup(picture,ad.linkUrl)}`,background:theme.backgroundColor,padding:"14px 20px"});break;
      }
      // Medium and small: images side by side, each in the size's frame.
      const fit=ad.imageFit==="contain" ? "contain" : "cover";
      const width=`${Math.round(100/size.count*1000)/1000}%`;
      const cells=slots.map((slot):EmailColumn=>({
        html:slot.imageUrl
          ? linkedMarkup(`<img src="${escapeHtml(slot.imageUrl)}" alt="Sponsor" style="display:block;width:100%;height:auto;aspect-ratio:${size.width}/${size.height};object-fit:${fit};background:#FFFFFF;border-radius:8px;" />`,slot.linkUrl)
          : emptyImage("100%","auto","#FAFAFB",`${size.width} × ${size.height}`,20,{radius:6,padding:"18px 6px"}),
        width,style:"padding:0 5px;",
      }));
      content=sectionShell({inner:`${emailColumnsResponsiveStyles}${label}<div style="margin:0 -5px;">${emailColumns(cells)}</div>`,background:theme.backgroundColor,padding:"14px 20px"});break;
    }
    case "gallery": {
      const gallery=block.settings;
      const perRow=Math.max(1,Math.min(gallery.items.length,3));
      const width=`${Math.round(100/perRow*1000)/1000}%`;
      const cells=gallery.items.map((item):EmailColumn=>{
        const picture=item.imageUrl ? `<img src="${escapeHtml(item.imageUrl)}" alt="${escapeHtml(item.title)}" style="display:block;width:100%;height:auto;aspect-ratio:4/3;object-fit:cover;border-radius:8px;" />` : emptyImage("100%","auto");
        const button=ctaButton({label:item.buttonLabel || "",url:item.buttonUrl || "",background:gallery.buttonColor || "#1878F2",color:getReadableEmailTextColor(gallery.buttonColor || "#1878F2"),margin:"10px 0 0",fontSize:13,radius:8});
        return {html:`${linkedMarkup(picture,item.linkUrl)}<div style="margin-top:8px;font-size:14px;font-weight:700;text-align:center;">${linkedMarkup(richTextToHtml(item.title),item.linkUrl,"color:#0A0A0A;")}</div>${button}`,width,style:"padding:0 8px 16px;"};
      });
      const rows=Array.from({length:Math.ceil(cells.length/perRow)},(_,index)=>{
        const row=cells.slice(index*perRow,(index+1)*perRow);
        return [...row,...Array.from({length:perRow-row.length},():EmailColumn=>({html:"",width}))];
      });
      content=sectionShell({inner:`${emailColumnsResponsiveStyles}<div style="font-size:22px;font-weight:800;text-align:center;margin-bottom:16px;">${richTextToHtml(gallery.title)}</div>${emailColumnRows(rows,"text-align:left;")}`,background:theme.backgroundColor,padding:"20px 12px 4px"});break;
    }
    case "columns":
      content=sectionShell({inner:`${emailColumnsResponsiveStyles}${emailColumns([block.settings.left,block.settings.right].map((text)=>({html:richTextToHtml(text),width:"50%",style:"padding:0 8px;font-size:13px;line-height:1.6;"})),"text-align:left;")}`,background:theme.backgroundColor,padding:"12px 16px",align:"left"});break;
    case "imageText": {
      const image=block.settings;
      const width=Math.min(260,Math.max(90,image.imageSize || 130));
      // Natural height: Outlook ignores object-fit and would stretch a fixed-height crop.
      const picture=image.imageUrl ? `<img src="${escapeHtml(image.imageUrl)}" alt="" width="${width}" style="display:block;width:100%;height:auto;border-radius:10px;" />` : emptyImage(`${width}px`,"100px");
      const copy=`<div style="font-size:14px;line-height:1.6;">${richTextToHtml(image.text)}</div>${image.linkText ? `<div style="margin-top:8px;font-size:13px;font-weight:700;">${linkedMarkup(`${richTextToHtml(image.linkText)} &rarr;`,image.linkUrl,`color:${image.highlightColor};`)}</div>` : ""}`;
      content=sectionShell({inner:`${emailColumnsResponsiveStyles}${emailColumns([{html:picture,width:width+16,style:"padding-right:16px;"},{html:copy}],"text-align:left;")}`,background:theme.backgroundColor,padding:"14px 24px",align:"left"});break;
    }
    case "video": {
      const video = block.settings;
      const url = normalizeEmailLinkUrl(video.linkUrl);
      const previewUrl = normalizeEmailLinkUrl(video.imageUrl);
      const previewBackground = previewUrl
        ? `background-image:url('${escapeHtml(previewUrl.replace(/\\/g, "%5C").replace(/'/g, "%27"))}');background-size:cover;background-position:center;`
        : "";
      const tile = `<table data-email-builder-video-tile role="presentation" width="100%" height="180" cellpadding="0" cellspacing="0"${previewUrl ? ` background="${escapeHtml(previewUrl)}"` : ""} style="height:180px;border-collapse:separate;border-spacing:0;background:#0A0A0A;border-radius:10px;${previewBackground}"><tr><td style="height:180px;padding:0;text-align:center;vertical-align:middle;line-height:0;"><span style="display:inline-block;width:52px;height:52px;line-height:52px;border-radius:50%;background:rgba(255,255,255,.92);font-size:0;text-align:center;"><span style="display:inline-block;vertical-align:middle;margin-left:3px;line-height:0;">${renderEmailBuilderIcon("play", "#0A0A0A", 20)}</span></span></td></tr></table>`;
      const linkedTile = url
        ? linkedMarkup(tile, url, "display:block;", stripSimpleMarkup(video.caption).trim() || "Play video")
        : tile;
      content = sectionShell({inner:`${linkedTile}<div style="margin-top:8px;font-size:13px;font-weight:600;text-align:center;">${richTextToHtml(video.caption)}</div>`,background:theme.backgroundColor,padding:"14px 24px"});
      break;
    }
    case "perks": {
      const perks=block.settings;
      const cards=perks.items.map((item)=>`<div style="margin-top:14px;border:1px solid #E3E3E6;border-radius:14px;background:#FFFFFF;overflow:hidden;text-align:left;overflow-wrap:anywhere;word-break:break-word;">${emailColumns([{html:perkLogo(item,perks.highlightColor),width:"88px",style:"vertical-align:top;padding:16px 0 16px 18px;"},{html:`<div style="font-size:12px;font-weight:700;color:#0A0A0A;">${escapeHtml(item.company)}</div><div style="margin-top:4px;font-size:17px;font-weight:800;color:#0A0A0A;text-transform:uppercase;line-height:1.25;">${escapeHtml(item.title)}</div>${item.description ? `<div style="margin-top:6px;font-size:12px;color:#727272;line-height:1.5;">${escapeHtml(item.description)}</div>` : ""}`,style:"vertical-align:top;padding:16px 18px 16px 16px;"}])}${perkMeta(item)}</div>`).join("");
      content=sectionShell({inner:emailColumnsResponsiveStyles+sectionHeading(perks.title,perks.highlightColor,font)+cards,background:theme.backgroundColor,padding:"20px",align:"left"});break;
    }
    case "perkWeek": {
      // Design (19): fixed gold banner, app column and the perk in a phone.
      // Saved highlight colours (theme colour before Oct 2026) are ignored.
      const pw=block.settings;
      content=sectionShell({inner:renderPerkOfTheWeekContent({perk:pw.perk,linkUrl:pw.linkUrl,labelHtml:richTextToHtml(pw.label),subtitleHtml:stripSimpleMarkup(pw.subtitle).trim() ? richTextToHtml(pw.subtitle) : "",headlineHtml:stripSimpleMarkup(pw.headline).trim() ? richTextToHtml(pw.headline) : "",headlineHidden:isEmailBuilderFieldHidden(sourceBlock,"headline"),placeholder:!!(options.samplePlaceholders || options.editorPreview),stacked:!!options.samplePlaceholders}),background:theme.backgroundColor,padding:"20px 0 24px"});break;
    }
    case "subscribe": {
      const sub=block.settings;
      const button=ctaButton({label:sub.buttonName,url:sub.linkUrl,background:sub.highlightColor,color:getReadableEmailTextColor(sub.highlightColor),margin:"6px 0 0",radius:6,fontSize:12});
      content=sectionShell({inner:`<div style="background:#F7F7F8;border-radius:7px;padding:24px;text-align:center;font-family:${font};">${renderEmailBuilderIcon("email",sub.highlightColor,26)}<div style="margin:8px auto 0;max-width:460px;font-size:16px;font-weight:700;color:#0A0A0A;line-height:1.3;">${richTextToHtml(sub.title)}</div><div style="margin:8px auto 0;max-width:420px;font-size:13px;color:#444444;line-height:1.5;">${richTextToHtml(sub.text)}</div>${button}</div>`,background:theme.backgroundColor,padding:"12px 24px"});break;
    }
    case "logoDate": {
      // EC-20: the email's logo lives here when the block is present (the
      // generated header then shows no logo, see renderEmailBuilderHeader).
      const ld=block.settings;
      // "Delete Logo" stays deleted. An empty logo without the flag (saved
      // before Oct 2026) still shows the Step 2 Chamber Logo.
      const logoUrl=ld.logoUrl || (ld.logoRemoved ? "" : options.builderState?.logoUrl || "");
      const width=Math.min(300,Math.max(90,Math.round(ld.logoWidth || 183)));
      const logo=logoUrl ? `<img src="${escapeHtml(logoUrl)}" alt="Chamber logo" width="${width}" style="display:inline-block;width:${width}px;max-width:100%;height:auto;object-fit:contain;" />` : "";
      const dateColor=getReadableEmailTextColor(ld.dateBackgroundColor);
      // Gmail dark mode keeps the pill colour and its text (the icon is an image).
      const dateLight=isGmailLightColor(dateColor);
      const dateKeepsWhite=/^#fff(fff)?$/i.test(ld.dateBackgroundColor.trim());
      const dateText=dateLight ? gmailKeepLightText(escapeHtml(ld.date)) : dateKeepsWhite ? `<span style="display:inline-block;vertical-align:middle;">${gmailKeepDarkText(escapeHtml(ld.date))}</span>` : escapeHtml(ld.date);
      const date=ld.date.trim() ? `<span style="display:inline-block;background:${ld.dateBackgroundColor};${dateLight || dateKeepsWhite ? gmailKeepBackground(ld.dateBackgroundColor) : ""}color:${dateColor};border-radius:8px;padding:7px 14px;font-size:13px;font-weight:600;white-space:nowrap;">${renderEmailBuilderIcon("calendar",dateColor,13)}&nbsp; ${dateText}</span>` : "";
      const inner=ld.layout==="centered"
        ? `<div style="text-align:center;">${logo ? `<div>${logo}</div>` : ""}${date ? `<div style="margin-top:${logo ? 10 : 0}px;">${date}</div>` : ""}</div>`
        // Design row with space-between: one item alone sits on the left.
        : !logo || !date
          ? `${logoDateResponsiveStyles}<div class="cmp-email-logo-date-single" style="text-align:left;">${logo || date}</div>`
          : `${logoDateResponsiveStyles}<table class="cmp-email-logo-date" role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%;"><tbody><tr><td style="vertical-align:middle;text-align:left;">${logo}</td><td width="1%" style="width:1%;vertical-align:middle;text-align:right;white-space:nowrap;padding-left:12px;">${date}</td></tr></tbody></table>`;
      content=sectionShell({inner,background:theme.backgroundColor,padding:"14px 24px"});break;
    }
    case "signature":
      content=renderSignature(block,theme,{name:getSignatureChamberName(options.builderState,footerProfile),logoUrl:options.builderState?.logoUrl || "",sample:options.samplePlaceholders});break;
    case "footer": {
      const state=options.builderState || {...buildDefaultEmailBuilderState(footerProfile?.name || undefined),theme,blocks:[block]};
      content=renderFooterBlock({builderState:state,theme,footerProfile});break;
    }
    default: content=renderBaseEmailBuilderBlock(block,theme);
  }
  return `<div style="font-family:${font};color:#0A0A0A;line-height:normal;overflow-wrap:break-word;">${top}${content}${bottom}</div>`;
};

const renderBaseEmailBuilderBlock = (
  block: EmailBuilderBlock,
  theme: EmailBuilderRenderTheme
): string => {
  switch (block.type) {
    case "textAndButtonHeroBanner":
      return renderTextAndButtonHeroBanner(block, theme);
    case "textHeroBanner":
      return renderTextHeroBanner(block, theme);
    case "emailBanner":
      return renderEmailBanner(block, theme);
    case "updateBanner":
      return renderUpdateBanner(block, theme);
    case "alertBanner":
      return renderAlertBanner(block, theme);
    case "advocacyUpdateBanner":
      return renderAdvocacyUpdateBanner(block, theme);
    case "memberSpotlight":
      return renderMemberSpotlight(block, theme);
    case "articleList":
      return renderArticleList(block, theme);
    case "splitArticleWithBorder":
      return renderSplitArticleWithBorder(block, theme);
    case "textAndEmailContent":
      return renderTextAndEmailContent(block, theme);
    case "fourGridIconAndTitle":
      return renderFourGridIconAndTitle(block, theme);
    case "sixGridIconAndTitle":
      return renderSixGridIconAndTitle(block, theme);
    case "upcomingEventsList":
      return renderUpcomingEventsList(block, theme);
    case "eventAnnouncement":
      return renderEventAnnouncement(block, theme);
    case "specialGuestSponsor":
      return renderSpecialGuestSponsor(block, theme);
    case "sponsorsList":
      return renderSponsorsList(block, theme);
    case "advertisingPdf":
      return renderAdvertisingPdf(block, theme);
    case "keyLinks":
      return renderKeyLinks(block, theme);
    case "inquiries":
      return renderInquiries(block, theme);
    case "signature":
    default:
      return renderSignature(block as EmailBuilderBlockOf<"signature">, theme);
  }
};

// Moeen Monday ask (EC-47): the chamber logo is 33% larger (137.5 -> 183px
// wide, max height 125 -> 166px). The backend upgrades saved drafts and
// scheduled emails to the same size at send time.
export const emailHeaderLogoStyle = {
  width: "183px",
  maxWidth: "100%",
  height: "auto",
  maxHeight: "166px",
  objectFit: "contain" as const,
  display: "block",
  margin: "0 auto",
};

export const renderEmailBuilderHeader = (
  state: EmailBuilderState,
  theme: EmailBuilderRenderTheme = getRenderTheme(state)
): string => {
  const logo = state.logoUrl && !state.blocks.some((block) => block.type === "logoDate")
    ? `<img src="${escapeHtml(state.logoUrl)}" alt="Chamber logo" width="183" style="display:block;width:${emailHeaderLogoStyle.width};max-width:100%;height:auto;max-height:${emailHeaderLogoStyle.maxHeight};object-fit:contain;margin:0 auto;" />`
    : "";
  const label = state.newsletterLabel?.trim() ? escapeHtml(state.newsletterLabel) : "";
  return logo || label ? `<table data-cmp-newsletter-header role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;"><tbody>${logo ? `<tr><td align="center" style="padding:24px 24px 10px;text-align:center;">${logo}</td></tr>` : ""}${label ? `<tr><td align="center" style="padding:0 24px 10px;text-align:center;font-family:${fontStack(theme.fontFamily)};font-size:13px;font-weight:600;">${label}</td></tr>` : ""}</tbody></table>` : "";
};

export type EmailFooterModel = {
  chamberName: string;
  copyrightText: string;
  customMessageLines: string[];
  socialLinks: EmailFooterSocialLink[];
};

export const getEmailBuilderFooterBlock = (
  blocks: EmailBuilderBlock[],
): EmailBuilderBlockOf<"footer"> | undefined =>
  blocks.find(
    (block): block is EmailBuilderBlockOf<"footer"> => block.type === "footer",
  );

// Reordering visible content must retain every stored footer and its settings.
export const replaceEmailBuilderContentBlocks = (
  storedBlocks: EmailBuilderBlock[],
  contentBlocks: EmailBuilderBlock[],
): EmailBuilderBlock[] => {
  let contentIndex = 0;
  const blocks = storedBlocks.flatMap((block) => {
    if (block.type === "footer") return [block];
    const content = contentBlocks[contentIndex++];
    return content ? [content] : [];
  });
  blocks.push(...contentBlocks.slice(contentIndex));
  return blocks.length === storedBlocks.length &&
    blocks.every((block, index) => block === storedBlocks[index])
    ? storedBlocks
    : blocks;
};

export const getEmailFooterModel = ({
  builderState,
  footerProfile,
}: {
  builderState: EmailBuilderState;
  footerProfile?: EmailFooterProfile | null;
}): EmailFooterModel => {
  const footer = getEmailBuilderFooterBlock(builderState.blocks);
  const chamberName = footer?.settings.organization.trim() || footerProfile?.name?.trim() || "{INSERT CHAMBER NAME}";
  const communityName = footer?.settings.communityName.trim();
  const customMessageLines = builderState.footerText
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => communityName && /^You are receiving this email because you are a valued member of the .+ business community\.$/.test(line)
      ? `You are receiving this email because you are a valued member of the ${communityName} business community.`
      : line);
  return {
    chamberName,
    copyrightText: `© ${new Date().getFullYear()} ${chamberName}`,
    customMessageLines,
    socialLinks: getEmailFooterSocialLinks(footerProfile),
  };
};

const emailFooterSocialLinksPerRow = 4;

const chunkEmailFooterSocialLinks = (
  socialLinks: EmailFooterSocialLink[]
): EmailFooterSocialLink[][] => {
  const rows: EmailFooterSocialLink[][] = [];
  for (
    let index = 0;
    index < socialLinks.length;
    index += emailFooterSocialLinksPerRow
  ) {
    rows.push(socialLinks.slice(index, index + emailFooterSocialLinksPerRow));
  }
  return rows;
};

const socialIconSpacerCell = `<td width="10" style="width:10px;font-size:0;line-height:0;">&nbsp;</td>`;

// Gmail dark mode lightens the footer; each white icon keeps the footer colour
// in its own circle so it stays visible (emailGmailDarkMode.ts).
const renderEmailFooterSocialLinks = (
  socialLinks: EmailFooterSocialLink[],
  footerBackgroundColor = ""
): string =>
  chunkEmailFooterSocialLinks(socialLinks).map((row, rowIndex) =>
    // Spacer cells keep the gap: Outlook drops border-spacing.
    `<table role="presentation" data-cmp-newsletter-social-row="true" data-cmp-newsletter-social-row-size="${row.length}" cellpadding="0" cellspacing="0" style="border-collapse:separate;border-spacing:0;margin:${rowIndex === 0 ? "0" : "8px"} auto 0;"><tr>${row.map(({platform,label,url,iconDataUrl},index) =>
      `${index ? socialIconSpacerCell : ""}<td width="30" height="30" align="center" valign="middle" style="box-sizing:border-box;width:30px;height:30px;padding:0;${footerBackgroundColor ? `background:${footerBackgroundColor};${gmailKeepBackground(footerBackgroundColor)}` : ""}border:1px solid #4A4A4A;border-radius:50%;text-align:center;vertical-align:middle;font-size:0;line-height:0;"><a data-cmp-newsletter-social-platform="${escapeHtml(platform)}" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" aria-label="${escapeHtml(label)}" title="${escapeHtml(label)}" style="display:inline-block;font-size:0;line-height:0;color:#ffffff;text-decoration:none;"><img data-cmp-newsletter-social-icon="${escapeHtml(platform)}" src="${iconDataUrl}" alt="${escapeHtml(label)}" width="14" height="14" style="display:block;width:14px;height:14px;border:0;outline:none;" /></a></td>`
    ).join("")}</tr></table>`
  ).join("");

const renderFooterBlock = ({
  builderState,
  theme,
  footerProfile,
}: {
  builderState: EmailBuilderState;
  theme: EmailBuilderRenderTheme;
  footerProfile?: EmailFooterProfile | null;
}): string => {
  const font = fontStack(theme.fontFamily);
  const footerModel = getEmailFooterModel({ builderState, footerProfile });
  const socialLinksHtml = renderEmailFooterSocialLinks(
    footerModel.socialLinks,
    theme.footerBackgroundColor
  );
  const customMessageHtml = footerModel.customMessageLines
    .map(
      (line) =>
        `<p style="margin:12px 0 0;font-family:${font};color:#C9C9C9;font-size:12px;line-height:1.5;">${escapeHtml(
          line
        )}</p>`
    )
    .join("");

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;"><tr><td style="background:${
    theme.footerBackgroundColor
  };padding:26px 24px;border-radius:7px;text-align:center;">${socialLinksHtml}<p style="margin:${
    footerModel.socialLinks.length > 0 ? "12px" : "0"
  } 0 0;font-family:${font};color:#C9C9C9;font-size:12px;line-height:1.5;">${escapeHtml(
    footerModel.copyrightText
  )}</p>${customMessageHtml}<p style="margin:12px 0 0;"><span data-cmp-newsletter-unsubscribe="true" style="font-family:${font};color:#FFFFFF;font-size:12px;font-weight:600;text-decoration:underline;">Unsubscribe</span><span data-cmp-newsletter-preferences="true" style="font-family:${font};color:#FFFFFF;font-size:12px;font-weight:600;text-decoration:underline;margin-left:18px;">Update Preferences</span></p></td></tr></table>`;
};

export const renderEmailFooterHtml = ({
  builderState,
  footerProfile,
}: {
  builderState: EmailBuilderState;
  footerProfile?: EmailFooterProfile | null;
}): string => {
  const theme = getRenderTheme(builderState);
  return `<div style="background:#f8fafc;padding:0 0 24px;"><div style="max-width:640px;margin:0 auto;border:1px solid #e5e7eb;border-top:none;">${renderFooterBlock(
    {
      builderState,
      theme,
      footerProfile,
    }
  )}</div></div>`;
};

const blockToTextLines = (sourceBlock: EmailBuilderBlock, footerProfile?: EmailFooterProfile | null, builderState?: EmailBuilderState): string[] => {
  const block = getVisibleEmailBuilderBlock(sourceBlock);
  switch (block.type) {
    case "heading":
    case "text":
    case "image":
    case "button":
      return [block.settings.text, getEmailBuilderButtonUrl(block.settings)];
    case "divider":
    case "spacer":
      return [];
    case "socialLinks":
      return getEmailFooterSocialLinks(footerProfile).map((link) => `${link.label}: ${link.url}`);
    case "textAndButtonHeroBanner":
      return [
        block.settings.title,
        block.settings.helperText,
        block.settings.buttonName,
        block.settings.buttonName ? getEmailBuilderButtonUrl(block.settings) : "",
      ];
    case "textHeroBanner":
      return [block.settings.title, block.settings.textContent];
    case "emailBanner":
      return [
        block.settings.buttonName,
        getEmailBuilderButtonUrl(block.settings),
        block.settings.title.replace(/\n/g, " "),
      ];
    case "updateBanner":
      return [
        block.settings.title,
        block.settings.description,
        block.settings.buttonName,
        block.settings.buttonName ? getEmailBuilderButtonUrl(block.settings) : "",
      ];
    case "alertBanner":
      return [block.settings.alertTitle];
    case "advocacyUpdateBanner":
      return [
        block.settings.title,
        block.settings.label,
        block.settings.description,
        block.settings.buttonName,
        block.settings.buttonName ? getEmailBuilderButtonUrl(block.settings) : "",
      ];
    case "memberSpotlight":
      return [
        block.settings.title,
        ...block.settings.items.flatMap((item) => [
          item.title,
          item.description,
        ]),
      ];
    case "articleList":
      return [block.settings.title || "", ...block.settings.items.flatMap((item) => [item.title,item.description,item.linkText || "",item.linkUrl || ""])];
    case "splitArticleWithBorder":
      return block.settings.items.flatMap((item) => [item.title, item.desc, item.buttonName, item.addLink]);
    case "textAndEmailContent":
      return [block.settings.textContent];
    case "fourGridIconAndTitle":
    case "sixGridIconAndTitle":
      return [
        block.settings.title,
        ...block.settings.items.flatMap((item) => [item.title,item.linkMode === "pdf" ? item.pdfUrl || "" : item.linkTo]),
      ];
    case "upcomingEventsList":
      return [
        block.settings.title,
        ...block.settings.events.flatMap((event) => [
          event.eventTitle,
          event.tag || "",
          event.description || "",
          event.linkUrl,
          [event.date, event.time].filter(Boolean).join(" | "),
          event.location.replace(/\n/g, ", "),
        ]),
      ];
    case "eventAnnouncement":
      return [
        block.settings.title,
        ...block.settings.events.flatMap((event) => [
          event.eventTitle,
          isEmailBuilderFieldHidden(
            block,
            `events.${event.id}.date`
          ) ? "" : event.date,
          isEmailBuilderFieldHidden(
            block,
            `events.${event.id}.time`
          ) ? "" : event.time,
          (() => {
            const [venue = "", ...address] = event.location.split("\n");
            return [
              isEmailBuilderFieldHidden(
                block,
                `events.${event.id}.venue`
              ) ? "" : venue,
              ...(
                isEmailBuilderFieldHidden(
                  block,
                  `events.${event.id}.address`
                ) ? [] : address
              ),
            ].filter(Boolean).join(", ");
          })(),
          block.settings.buttonName ?? "Register Now",
          getEmailBuilderButtonUrl({...block.settings,linkUrl:event.linkUrl}),
        ]),
      ];
    case "specialGuestSponsor":
      return [block.settings.title, block.settings.textContent];
    case "sponsorsList":
      return [block.settings.title];
    case "advertisingPdf":
      return [block.settings.title, block.settings.buttonName, block.settings.attachPdfUrl || block.settings.addLink];
    case "keyLinks":
      return [
        block.settings.title,
        ...block.settings.items
          .filter(
            (item) =>
              !isEmailBuilderFieldHidden(
                block,
                `items.${item.id}.buttonText`
              )
          )
          .flatMap((item) => [item.buttonText,item.addLink]),
      ];
    case "inquiries":
      return [
        block.settings.title,
        block.settings.textContent,
        block.settings.email,
        block.settings.phoneNumber,
      ];
    case "newsroomHighlights":
      // sourceAttribution is the website's origin code (for example
      // "cmp-newsletter"), never reader text.
      return [block.settings.title,...block.settings.items.flatMap((item)=>[item.title,item.category,item.author,item.linkUrl])];
    case "newMembers":
      return [block.settings.title,...[...block.settings.items].sort((a,b)=>a.name.localeCompare(b.name)).flatMap((item)=>[item.name,item.linkUrl])];
    case "advertisementBanner": return ["ADVERTISEMENT",...getEmailAdBannerSlots(block.settings).map((slot)=>slot.linkUrl)];
    case "gallery": return [block.settings.title,...block.settings.items.flatMap((item)=>[item.title,item.linkUrl,...(stripSimpleMarkup(item.buttonLabel || "").trim() ? [item.buttonLabel || "",item.buttonUrl || ""] : [])])];
    case "columns": return [block.settings.left,block.settings.right];
    case "imageText": return [block.settings.text,block.settings.linkText,block.settings.linkUrl];
    case "video": return [block.settings.caption,block.settings.linkUrl];
    case "footer": return [];
    case "perks": return [block.settings.title,...block.settings.items.flatMap((item)=>[item.title,item.company,item.description])];
    case "perkWeek": return [block.settings.label,block.settings.subtitle,block.settings.headline || (isEmailBuilderFieldHidden(sourceBlock,"headline") ? "" : block.settings.perk?.title || ""),...getPerkOfTheWeekTextLines(block.settings.perk),block.settings.linkUrl];
    case "subscribe": return [block.settings.title,block.settings.text,block.settings.buttonName,block.settings.linkUrl];
    case "logoDate": return [block.settings.date];
    case "signature": {
      const settings = block.settings;
      if (typeof settings.text === "string") {
        // Plain values keep the design's <br> line breaks as text lines.
        const text = isEmailRichText(settings.text) ? settings.text : settings.text.replace(/<br\s*\/?>/gi, "\n");
        return [text, getSignatureChamberName(builderState, footerProfile)];
      }
      return [
        settings.opening ?? "Sincerely,",
        settings.addSignature || settings.name || "",
        settings.addSignature && settings.name && settings.addSignature !== settings.name ? settings.name : "",
        [settings.designation, settings.chamberName].filter(Boolean).join(", "),
      ];
    }
  }
};

export const getEmailGreetingText = (greeting: EmailBuilderState["greetingLine"]): string => {
  switch (greeting) {
    case "firstName": return "Hello {First Name},";
    case "fullName": return "Hello {First and Last Name},";
    case "companyName": return "Hello {Company Name},";
    default: return "";
  }
};

// Website file links open through /api/inline-media/<file>, which redirects to
// an S3 link that expires one hour later. A copied S3 link is dead before most
// members open the email, so it is sent as the website link instead.
const expiringWebsiteMediaLinkPattern =
  /https:\/\/chamber-member-pro-website-media\.s3[a-z0-9.-]*\.amazonaws\.com\/media\/([^?#"'\s<>]+)\?[^#"'\s<>]*X-Amz-Signature=[^#"'\s<>]*/gi;

export const replaceExpiringWebsiteMediaLinks = (
  builderState: EmailBuilderState,
  chamberWebsiteUrl: string | null | undefined
): EmailBuilderState => {
  const websiteRoot = normalizeEmailLinkUrl(chamberWebsiteUrl || "").replace(/\/+$/, "");
  if (!/^https?:\/\//i.test(websiteRoot)) return builderState;
  return JSON.parse(
    JSON.stringify(builderState, (_key, value: unknown) =>
      typeof value === "string"
        ? value.replace(expiringWebsiteMediaLinkPattern, (_link, fileName: string) => `${websiteRoot}/api/inline-media/${fileName}`)
        : value
    )
  ) as EmailBuilderState;
};

export const renderEmailBuilderState = (
  builderState: EmailBuilderState,
  footerProfile?: EmailFooterProfile | null
): RenderedEmailBuilderContent => {
  if (builderState.preservedDocument) {
    const saved = builderState.preservedDocument;
    return { html: saved.contentHtml + saved.footerHtml, bodyHtml: saved.contentHtml, footerHtml: saved.footerHtml, text: saved.contentText };
  }
  const safeBuilderState = replaceExpiringWebsiteMediaLinks(
    sanitizeEmailBuilderStateLinks(builderState),
    footerProfile?.chamberMemberProWebsiteUrl
  );
  const theme = getRenderTheme(safeBuilderState);
  const footerModel = getEmailFooterModel({
    builderState: safeBuilderState,
    footerProfile,
  });
  const greeting = getEmailGreetingText(safeBuilderState.greetingLine);
  const blocksHtml = safeBuilderState.blocks
    .filter((block) => block.type !== "footer")
    .map((block) => renderEmailBuilderBlock(block, theme, footerProfile, {builderState:safeBuilderState}))
    .join("");
  const bodyHtml = `<div style="background:#f8fafc;padding:24px 0 0;"><div style="max-width:640px;margin:0 auto;background:${theme.backgroundColor};border:1px solid #e5e7eb;border-bottom:none;">${renderEmailBuilderHeader(
    safeBuilderState,
    theme
  )}${greeting ? `<p style="padding:0 24px;font-family:${fontStack(theme.fontFamily)};color:${theme.textColor};">${escapeHtml(greeting)}</p>` : ""}${blocksHtml}</div></div>`;
  const footerHtml = renderEmailFooterHtml({
    builderState: safeBuilderState,
    footerProfile,
  });
  const text = [
    greeting,
    ...safeBuilderState.blocks
      .filter((block) => block.type !== "footer")
      .flatMap((block) => [...blockToTextLines(block, footerProfile, safeBuilderState), getVisibleEmailBuilderBlock(block).settings.extraText || ""])
      .map(stripSimpleMarkup),
    footerModel.copyrightText,
    ...footerModel.customMessageLines,
  ]
    .map((line) => line.trim())
    .filter(Boolean)
    .join("\n\n");

  return { html: bodyHtml + footerHtml, bodyHtml, footerHtml, text };
};

// ---------------------------------------------------------------------------
// Stored-state parsing (version 2 + version-1 migration)
// ---------------------------------------------------------------------------

type LegacyEmailBuilderGridItem = {
  id?: string;
  title?: string;
  body?: string;
  iconLabel?: string;
  imageUrl?: string;
};

type LegacyEmailBuilderBlock = {
  id?: string;
  type?: string;
  title?: string;
  body?: string;
  buttonLabel?: string;
  buttonUrl?: string;
  imageUrl?: string;
  iconLabel?: string;
  textColor?: string;
  highlightColor?: string;
  backgroundColor?: string;
  items?: LegacyEmailBuilderGridItem[];
};

const legacyIconToKey = (iconLabel?: string): EmailBuilderIconKey => {
  switch ((iconLabel || "").toLowerCase()) {
    case "calendar":
      return "calendar";
    case "megaphone":
      return "megaphone";
    case "star":
      return "sparkle";
    case "email":
      return "email";
    default:
      return "calendar";
  }
};

const legacyColorOverrides = (legacy: LegacyEmailBuilderBlock) => ({
  ...(legacy.textColor ? { textColor: legacy.textColor } : {}),
  ...(legacy.highlightColor ? { highlightColor: legacy.highlightColor } : {}),
  ...(legacy.backgroundColor
    ? { backgroundColor: legacy.backgroundColor }
    : {}),
});

// Version-1 blocks map to the nearest canonical version-2 block, best-effort.
const migrateLegacyBlock = (
  legacy: LegacyEmailBuilderBlock
): EmailBuilderBlock | null => {
  const title = legacy.title || "";
  const body = legacy.body || "";
  const buttonLabel = legacy.buttonLabel || "";
  const buttonUrl = legacy.buttonUrl || "";
  const imageUrl = legacy.imageUrl || "";

  switch (legacy.type) {
    case "article":
      return createMemberSpotlightBlock({
        title: title || "Member Spotlight",
        items: [
          createMemberSpotlightItem({
            imageUrl,
            title,
            description: body,
            buttonName: buttonLabel || "Read Their Story",
            addLink: buttonUrl,
          }),
        ],
        ...legacyColorOverrides(legacy),
      });
    case "eventDisplay":
      return createUpcomingEventsListBlock({
        events: [
          createEventItem({ eventTitle: title, location: body, linkUrl: buttonUrl }),
        ],
        ...legacyColorOverrides(legacy),
      });
    case "eventDetails":
      return createEventAnnouncementBlock({
        events: [
          createEventItem({ eventTitle: title, location: body, linkUrl: buttonUrl }),
        ],
        ...legacyColorOverrides(legacy),
      });
    case "fourGridIconTitle":
      return createFourGridIconAndTitleBlock({
        title,
        items: (legacy.items || []).map((item) =>
          createIconGridItem({
            icon: legacyIconToKey(item.iconLabel),
            title: item.title || "",
          })
        ),
        ...legacyColorOverrides(legacy),
      });
    case "sixImageGrid":
      return createSixGridIconAndTitleBlock({
        title,
        items: (legacy.items || []).map((item) =>
          createIconGridItem({
            icon: legacyIconToKey(item.iconLabel),
            title: item.title || "",
          })
        ),
        ...legacyColorOverrides(legacy),
      });
    case "textButtonBanner":
    case "emailBanner":
      return createUpdateBannerBlock({
        title,
        description: body,
        buttonName: buttonLabel,
        addLink: buttonUrl,
        ...legacyColorOverrides(legacy),
      });
    case "splitArticleBorder":
      return createSplitArticleWithBorderBlock({
        items: [
          createSplitArticleItem({
            title,
            desc: body,
            buttonName: buttonLabel,
            addLink: buttonUrl,
          }),
        ],
        ...legacyColorOverrides(legacy),
      });
    case "imageTextBanner":
      return createEmailBannerBlock({
        title,
        ceoImageUrl: imageUrl,
        buttonName: buttonLabel,
        addLink: buttonUrl,
        ...legacyColorOverrides(legacy),
      });
    case "textBorder":
    case "textOnlyBanner":
      return createTextAndEmailContentBlock({
        textContent: [title, body].filter(Boolean).join("\n\n"),
        textAlign: "left",
        ...(legacy.textColor ? { textColor: legacy.textColor } : {}),
        ...(legacy.backgroundColor
          ? { backgroundColor: legacy.backgroundColor }
          : {}),
      });
    case "sponsorLogo":
      return createSpecialGuestSponsorBlock({
        title: title || "SPECIAL GUEST SPONSOR",
        sponsorLogoUrl: imageUrl,
        textContent: body,
        ...(legacy.textColor ? { textColor: legacy.textColor } : {}),
        ...(legacy.backgroundColor
          ? { backgroundColor: legacy.backgroundColor }
          : {}),
      });
    case "advertisingLogo":
      return createAdvertisingPdfBlock({
        title,
        imageUrl,
        buttonName: buttonLabel || "View Full PDF",
        addLink: buttonUrl,
        ...legacyColorOverrides(legacy),
      });
    case "signature":
      return createSignatureBlock({
        text: body || title,
        ...(legacy.textColor ? { textColor: legacy.textColor } : {}),
        ...(legacy.backgroundColor
          ? { backgroundColor: legacy.backgroundColor }
          : {}),
      });
    default:
      return null;
  }
};

const isKnownBlockType = (value: unknown): value is EmailBuilderBlockType =>
  typeof value === "string" && Object.prototype.hasOwnProperty.call(blockFactories, value);

// Fills any missing settings fields with factory defaults so older/partial
// version-2 payloads stay renderable.
const sanitizeStoredBlock = (raw: unknown): EmailBuilderBlock | null => {
  if (!raw || typeof raw !== "object") return null;
  const candidate = raw as { id?: unknown; type?: unknown; settings?: unknown };
  if (!isKnownBlockType(candidate.type)) return null;
  const fallback = blockFactories[candidate.type]();
  const storedSettings =
    candidate.settings && typeof candidate.settings === "object"
      ? (candidate.settings as Record<string, unknown>)
      : {};

  // Blocks saved before the 60-day window keep their 30-day window (and the
  // title that names it). The backend send refresh reads the same field.
  if (candidate.type === "newMembers" && storedSettings.days === undefined) {
    storedSettings.days = 30;
  }
  // New Urgent Alert Banner blocks get the design sample bottom text; saved
  // blocks without bottom text keep none.
  if (candidate.type === "alertBanner" && storedSettings.extraText === undefined) {
    storedSettings.extraText = "";
  }
  // Signatures saved before the SIGNATURE field: keep the stored lines, not
  // the new sample text.
  if (candidate.type === "signature" && typeof storedSettings.text !== "string") {
    const { textColor, backgroundColor } = fallback.settings as SignatureSettings;
    const legacy: Record<string, unknown> = { textColor, backgroundColor, ...storedSettings };
    delete legacy.text;
    return {
      ...fallback,
      id: typeof candidate.id === "string" && candidate.id ? candidate.id : fallback.id,
      settings: migrateLegacySignatureSettings(legacy as SignatureSettings & EmailBuilderPresentationSettings),
    } as EmailBuilderBlock;
  }
  if (candidate.type === "articleList" && Array.isArray(storedSettings.items)) {
    storedSettings.items = storedSettings.items.map((item) => ({ imageUrl: "", linkText: "", linkUrl: "", ...item }));
  }
  if (Array.isArray(storedSettings.items)) {
    const itemDefaults = candidate.type === "newsroomHighlights"
      ? {title:"",linkUrl:"",imageUrl:"",category:"",publishedAtUtcMilli:null,author:"",readTime:"",sourceAttribution:""}
      : candidate.type === "newMembers"
      ? {name:"",logoUrl:"",category:"",joinedAtUtcMilli:null,linkUrl:""}
      : candidate.type === "gallery" ? {imageUrl:"",title:"",linkUrl:""} : null;
    if (itemDefaults) storedSettings.items = storedSettings.items
      .filter((item) => item && typeof item === "object")
      .map((item) => ({id:makeId("item"),...itemDefaults,...item}));
  }
  return {
    ...fallback,
    id: typeof candidate.id === "string" && candidate.id ? candidate.id : fallback.id,
    settings: { ...fallback.settings, ...storedSettings },
  } as EmailBuilderBlock;
};

const legacyGeneratedCopyrightLinePattern = /^\s*©\s*\d{4}\b.*$/;

export const migrateStoredEmailFooterText = ({
  fallbackFooterText,
  storedFooterText,
}: {
  fallbackFooterText: string;
  storedFooterText: unknown;
}): string => {
  if (typeof storedFooterText !== "string") return fallbackFooterText;

  const storedLines = storedFooterText.split(/\r?\n/);
  const firstContentLineIndex = storedLines.findIndex(
    (line) => line.trim().length > 0
  );

  // Older version-2 drafts stored the generated copyright and the chamber's
  // editable message in one field. Remove only that known generated first
  // line; preserve every other line so customized legacy copy is never lost.
  if (
    firstContentLineIndex >= 0 &&
    legacyGeneratedCopyrightLinePattern.test(
      storedLines[firstContentLineIndex].trim()
    )
  ) {
    storedLines.splice(firstContentLineIndex, 1);
  }

  return storedLines.join("\n").trim();
};

export const parseEmailBuilderStateJson = ({
  builderStateJson,
  chamberName,
  fallbackContent,
  fallbackHtml,
  fallbackFooterHtml,
}: {
  builderStateJson?: string | null;
  chamberName?: string;
  fallbackContent?: string;
  fallbackHtml?: string | null;
  fallbackFooterHtml?: string | null;
}): EmailBuilderState => {
  const defaultState = buildDefaultEmailBuilderState(chamberName);

  const preserveDocument = (): EmailBuilderState => ({
    ...defaultState,
    blocks: [],
    preservedDocument: {
      builderStateJson: builderStateJson || "",
      contentHtml: fallbackHtml || "",
      contentText: fallbackContent || "",
      footerHtml: fallbackFooterHtml || "",
    },
  });
  if (builderStateJson) {
    try {
      const parsed = JSON.parse(builderStateJson) as {
        version?: number;
        blocks?: unknown[];
      } & Omit<Partial<EmailBuilderState>, "version" | "blocks">;

      if (parsed?.version === 2 && Array.isArray(parsed.blocks)) {
        const storedBlocks = parsed.blocks.map(sanitizeStoredBlock);
        if (storedBlocks.some((block) => !block)) return preserveDocument();
        const blocks = storedBlocks.filter((block): block is EmailBuilderBlock => !!block);

        return {
          ...defaultState,
          ...parsed,
          version: 2,
          fontFamily: parsed.fontFamily || defaultState.fontFamily,
          footerText: migrateStoredEmailFooterText({
            fallbackFooterText: defaultState.footerText,
            storedFooterText: parsed.footerText,
          }),
          theme: { ...defaultState.theme, ...(parsed.theme || {}) },
          blocks,
          selectedBlockId: blocks.some(
            (block) => block.id === parsed.selectedBlockId
          )
            ? (parsed.selectedBlockId as string)
            : "",
        };
      }

      if (parsed?.version === 1 && Array.isArray(parsed.blocks)) {
        const storedBlocks = (parsed.blocks as LegacyEmailBuilderBlock[]).map(migrateLegacyBlock);
        if (storedBlocks.some((block) => !block)) return preserveDocument();
        const blocks = storedBlocks.filter((block): block is EmailBuilderBlock => !!block);

        return {
          ...defaultState,
          logoUrl: typeof parsed.logoUrl === "string" ? parsed.logoUrl : "",
          greetingLine: parsed.greetingLine || "none",
          footerText: migrateStoredEmailFooterText({
            fallbackFooterText: defaultState.footerText,
            storedFooterText: parsed.footerText,
          }),
          theme: { ...defaultState.theme, ...(parsed.theme || {}) },
          blocks,
          selectedBlockId: "",
        };
      }
    } catch {
      return preserveDocument();
    }
    return preserveDocument();
  }

  const state = defaultState;
  if (fallbackContent?.trim()) {
    state.blocks = [
      createTextAndEmailContentBlock({
        textContent: fallbackContent.trim(),
        textAlign: "left",
      }),
    ];
  }

  return state;
};

// ---------------------------------------------------------------------------
// Template presets (EMAIL-007 — verbatim seeded content from the template
// screenshots; gallery copy from the Step 3 gallery screenshots)
// ---------------------------------------------------------------------------

export type EmailTemplateKey =
  | "newsletter"
  | "eblast"
  | "advocacyUpdate"
  | "eventAnnouncement"
  | "mediaRelease"
  | "memberWelcome"
  | "fromTheDesk"
  | "customEmail";

export type EmailTemplatePreset = {
  hiddenFromGallery?: boolean;
  key: EmailTemplateKey;
  /** Gallery card heading text (uppercase; card number comes from list order). */
  title: string;
  /** Title-case name for "Email Template: <name>" in the builder header. */
  builderTitle: string;
  description: string;
  /** Copy after the "Best For: " label. */
  preview: { title: string; heading: string; button: string };
  bestFor: string;
  /** Copy after the "Design Tip: " label. */
  designTip: string;
  /** Fresh factory-built blocks on every access (safe to mutate). */
  readonly orderedBlocks: EmailBuilderBlock[];
};

// Design (16) EB_TEMPLATES: every gallery template starts with Logo and
// Date (EC-20) and ends with the footer.
// What's New: "The Newsletter template starts with Logo and Date, Hero
// Banner, Newsletter Highlights and the footer."
const buildNewsletterTemplateBlocks = (): EmailBuilderBlock[] => [
  createLogoDateBlock(),
  createTextAndButtonHeroBannerBlock(),
  createFourGridIconAndTitleBlock(),
  createFooterBlock(),
];

const buildEBlastTemplateBlocks = (): EmailBuilderBlock[] => [
  createLogoDateBlock(),
  createAlertBannerBlock(),
  createUpdateBannerBlock(),
  createSignatureBlock(),
  createFooterBlock(),
];

const buildAdvocacyUpdateTemplateBlocks = (): EmailBuilderBlock[] => [
  createLogoDateBlock(),
  createAdvocacyUpdateBannerBlock(),
  createArticleListBlock(),
  createKeyLinksBlock(),
  createSignatureBlock(),
  createFooterBlock(),
];

const buildEventAnnouncementTemplateBlocks = (): EmailBuilderBlock[] => [
  createLogoDateBlock(),
  createTextAndButtonHeroBannerBlock(),
  createEventAnnouncementBlock(),
  createSpecialGuestSponsorBlock(),
  createFooterBlock(),
];

const buildMediaReleaseTemplateBlocks = (): EmailBuilderBlock[] => [
  createLogoDateBlock(),
  createTextAndButtonHeroBannerBlock(),
  createArticleListBlock(),
  createKeyLinksBlock(),
  createSignatureBlock(),
  createFooterBlock(),
];

const buildMemberWelcomeTemplateBlocks = (): EmailBuilderBlock[] => [
  createTextAndButtonHeroBannerBlock(),
  createUpdateBannerBlock(),
  createSignatureBlock(),
  createFooterBlock(),
];

const buildFromTheDeskTemplateBlocks = (): EmailBuilderBlock[] => [
  createLogoDateBlock(),
  createArticleListBlock(),
  createSignatureBlock(),
  createFooterBlock(),
];

export const emailTemplatePresets: EmailTemplatePreset[] = [
  {
    key: "newsletter",
    preview: {"title": "Newsletter", "heading": "Chamber Insider", "button": "Read Story"},
    title: "NEWSLETTER",
    builderTitle: "Newsletter",
    description: "Share updates, events, and member news.",
    bestFor:
      "Regular communication that provides value and keeps members informed.",
    designTip: "Use clear sections and mixed content for engagement.",
    get orderedBlocks() {
      return buildNewsletterTemplateBlocks();
    },
  },
  {
    key: "eblast",
    preview: {"title": "Important Update", "heading": "New Provincial Grant Now Available!", "button": "Apply Now"},
    title: "E-BLAST/CUSTOM EMAIL",
    builderTitle: "E-Blast/Custom Email",
    description: "Short, urgent messages to drive action.",
    bestFor: "Time-sensitive updates that require quick action or awareness.",
    designTip: "Use a strong headline, contrasting colours, and a clear CTA.",
    get orderedBlocks() {
      return buildEBlastTemplateBlocks();
    },
  },
  {
    key: "advocacyUpdate",
    preview: {"title": "Advocacy Update", "heading": "Support Smart Growth In Our Community", "button": "Take the Survey"},
    title: "ADVOCACY UPDATE",
    builderTitle: "Advocacy Update",
    description: "Inform members about key issues and actions.",
    bestFor: "Engaging members on important issues and encouraging advocacy.",
    designTip: "Clearly explain the issue and make the call-to-action easy.",
    get orderedBlocks() {
      return buildAdvocacyUpdateTemplateBlocks();
    },
  },
  {
    key: "eventAnnouncement",
    preview: {"title": "Save the Date", "heading": "Business Networking Luncheon", "button": "Register Now"},
    title: "EVENT ANNOUNCEMENT",
    builderTitle: "Event Announcement",
    description: "Promote upcoming events and drive registrations.",
    bestFor: "Driving registrations and attendance for events.",
    designTip: "Highlight key details and use a prominent CTA button.",
    get orderedBlocks() {
      return buildEventAnnouncementTemplateBlocks();
    },
  },
  {
    key: "mediaRelease",
    preview: {"title": "Media Release", "heading": "New Board of Directors Announcement", "button": "Read Full Release"},
    title: "MEDIA RELEASE",
    builderTitle: "Media Release",
    description: "Share official news and announcements.",
    bestFor: "Sharing official announcements with members and media.",
    designTip: "Keep it professional, clear, and easy to read.",
    get orderedBlocks() {
      return buildMediaReleaseTemplateBlocks();
    },
  },
  {
    key: "memberWelcome",
    // Monday D11 / D@1200358 (EC-15): not offered for new emails; saved
    // drafts and sent emails with this template still open.
    hiddenFromGallery: true,
    preview: {"title": "Welcome", "heading": "Welcome to the Chamber!", "button": "Get Started"},
    title: "MEMBER WELCOME AND LOG IN",
    builderTitle: "Member Welcome and Log In",
    description: "Welcome new members and send them their Member Dashboard Log-in Credentials",
    bestFor: "Onboarding new members and showcasing value.",
    designTip: "Personalize the greeting and highlight key benefits.",
    get orderedBlocks() {
      return buildMemberWelcomeTemplateBlocks();
    },
  },
  {
    key: "fromTheDesk",
    preview: {"title": "From the Desk", "heading": "of Our CEO", "button": "Read Message"},
    title: "FROM THE DESK",
    builderTitle: "From the Desk",
    description: "Personal messages from leadership or staff.",
    bestFor: "Building trust and connection through leadership.",
    designTip: "Use a personal tone, image, and authentic message.",
    get orderedBlocks() {
      return buildFromTheDeskTemplateBlocks();
    },
  },
  {
    key: "customEmail",
    // Monday D11 / D@1200358 (EC-15): not offered for new emails; saved
    // drafts and sent emails with this template still open.
    hiddenFromGallery: true,
    preview: {"title": "Your Message, Your Way", "heading": "Drag-and-Drop Builder", "button": "Start Building"},
    title: "CUSTOM EMAIL",
    builderTitle: "Custom Email",
    description: "Create a message that fits your unique needs.",
    bestFor: "Unique messages or campaigns that don\u2019t fit into standard templates.",
    designTip: "Keep your message focused and align with your brand.",
    get orderedBlocks() {
      return [createTextAndButtonHeroBannerBlock(), createFooterBlock()];
    },
  },
];

/** The six templates the design offers for a new email. */
export const emailGalleryTemplatePresets = (): EmailTemplatePreset[] =>
  emailTemplatePresets.filter((preset) => !preset.hiddenFromGallery);

export const getEmailTemplatePreset = (
  key: string
): EmailTemplatePreset | undefined =>
  emailTemplatePresets.find((preset) => preset.key === key);
